import React, { useEffect, useRef } from 'react';
import styled, { keyframes, css, Keyframes } from 'styled-components';
import { motion, useAnimation, useInView } from 'framer-motion';

const generateKeyframes = (scaleFactor: number) => keyframes`
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(${scaleFactor});
  }

  100% {
    transform: scale(1);
  }
`;

const usCircleKeyframes = generateKeyframes(1.0546);

const ukCircleKeyframes = generateKeyframes(1.096);

const beCircleKeyframes = generateKeyframes(0.94);

const deCircleKeyframes = generateKeyframes(1.091);

const jpCircleKeyframes = generateKeyframes(0.954);

const generateAnimation = (kfs: Keyframes, duration: number, delay: number) => css`
  transform-box: fill-box;
  transform-origin: center;
  animation: ${kfs} ${`${duration}s`} ease-in-out ${`${delay}s`} infinite alternate-reverse;
`;

interface ICircleProps {
  $isInView: boolean;
}

const UsCircle = styled(motion.circle)<ICircleProps>`
  ${generateAnimation(usCircleKeyframes, 3.87, 0.3)}
`;

const UkCircle = styled(motion.circle)<ICircleProps>`
  ${generateAnimation(ukCircleKeyframes, 2.974, 0.5)}
`;

const BeCircle = styled(motion.circle)<ICircleProps>`
  ${generateAnimation(beCircleKeyframes, 3.235, 0.7)}
`;

const DeCircle = styled(motion.circle)<ICircleProps>`
  ${generateAnimation(deCircleKeyframes, 4.46, 0.9)}
`;

const JpCircle = styled(motion.circle)<ICircleProps>`
  ${generateAnimation(jpCircleKeyframes, 3.275, 1.1)}
`;

const MapPath = styled(motion.path)`
  fill: none;
  stroke: #7872ef;
  stroke-width: 0.7902;
`;

const MapSvg: React.FC = () => {
  const mapPathAnimation = useAnimation();
  const circleAnimation = useAnimation();
  const labelAnimation = useAnimation();
  const labelBorderAnimation = useAnimation();
  const labelTextAnimation = useAnimation();

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.25 });

  const mapPathDrawingAnim = { pathLength: 1, transition: { duration: 3, ease: 'easeIn' } };
  const circleIntroAnimation = { opacity: 1, transition: { duration: 2, ease: 'easeOut' } };
  const labelIntroAnimation = { opacity: 1, transition: { duration: 1.5, ease: 'easeInOut' } };
  const labelBorderIntroAnimation = {
    pathLength: 1,
    transition: { duration: 1.5, ease: 'easeInOut' }
  };
  const labelTextIntoAnimation = { opacity: 1, transition: { duration: 2, ease: 'easeOut' } };

  useEffect(() => {
    if (isInView) {
      mapPathAnimation.start(mapPathDrawingAnim);
      circleAnimation.start(circleIntroAnimation);
      labelAnimation.start(labelIntroAnimation);
      labelBorderAnimation.start(labelBorderIntroAnimation);
      labelTextAnimation.start(labelTextIntoAnimation);
    }
  }, [isInView]);

  return (
    <svg
      version="1.1"
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      viewBox="0 0 1400 723"
      ref={ref}
    >
      <g>
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M707.5,537.9l-1.2,0.3c1.6,4.8,5,11.3,7.7,17l2.5,8.7c0.5,7.8,0.9,16,6.3,22.3c1.8,5.5,8.3,13.8,6.6,18.2
	c-2.4,0.1,1.7,8.1,2.9,7.8c4.3,4.3,3.7,0.2,9.1,0.3c4.4-2.9,9.2-0.7,13.2-2c7.9-0.4,18.4-10.5,22.8-18.7c3.5-5.7,7.3-6.1,7.4-13.1
	c0.3-1.8,0.3-2.3-0.2-2.8c0.5,0.5,0.5,1,0.2,2.8l1.1-6.3c1.2-0.7,2.6-1.3,4-2c2-0.9,3.8-1.9,4.4-2.7c0-0.1,0.1-0.1,0.1-0.2
	c0,0,0,0,0-0.1c0-0.1,0-0.1,0-0.2c0.4-1.1,0.5-2.3,0.5-3.5c0-3.4-1.1-7.1-1.9-10.1c-0.6-2.1-1-3.8-0.8-4.8c0.1-0.4,0.3-0.6,0.6-0.7
	c0.8,0,0.9-0.3,1.3-1.2c0.3-0.4,0.7-0.9,1.1-1.4c0.7-0.5,1.3-1,1.9-1.5c0.2-0.1,0.4-0.2,0.7-0.2c0.2-0.3,0.3-0.6,0.5-0.8
	c0.8-0.8,1.6-1.6,2.7-2.8c0.7-0.6,1.4-1.1,2.2-1.6c1.3-0.6,2.8-1.3,4.2-2.1c0.7-0.3,1.4-0.5,2-0.7c1.6-1.1,2.8-2.3,3.7-3.6
	c1.4-1.5,2.3-3.1,2.4-4.8c-0.1-0.5-0.3-1-0.4-1.6c0.2-2.8-0.3-5.9-0.7-9c0-2.5-0.1-4.9-0.3-7.1"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M694.3,447.6c1.3,2,2.5,2.6,3.4,2.9c1.1,0.4,1.8,0.3,2.1,1.3c0.2,0.9,0.1,2.6-0.5,6.2c0,0,0,0,0,0
	c2,1.9-3.5,3.4-1,4.4c0.6,0,0.5,0.5,0.1,1.1c0.5-0.1,1.1-0.2,1.7-0.2c-0.7,0.1-1.3,0.1-1.7,0.2c-0.5,0.4-0.6,0.7-0.6,1
	c0,0.2,0.1,0.3,0.1,0.4c0.3,0.4,0.8,0.8,1.3,1.5c-0.3,0.2-0.6,0.3-0.8,0.5c-0.9,0.6-1.1,1.2-1.4,1.9c-0.3,0.5-0.6,1.1-1.2,1.7
	c-0.1,0-0.1,0.1-0.2,0.1c1.3,4,5.1,8,8.5,12.3c0,0,0,0,0,0c1.7,1.3,3,3,3.7,4.6c0,0,0,0,0,0c0,0,0,0,0,0c0.8,1.7,1.1,3.3,1.5,4.9
	c0,0,0,0,0,0c0.7,2.8,2.9,7.4,3.4,8.4c-1.2,3.7,0.5,8.5,1.8,12.7c-3,7.9-8,16-8.1,24.9l1.2-0.3"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M818.6,473.3c10.4-12.7,26.6-21.9,32.9-38.4c2.3-2.6,8.3-19.4,2.4-16.4c-4.1,3.3-15.2,2.7-19.9,4.9
	c-2.7,3.3-6.5-0.3-8.4-2.8c0,0,0,0-0.1,0.1c0,0,0,0,0-0.1c-1.5-0.4-2-0.8-1.9-1.2c0-0.3,0.3-0.5,0.7-0.8c0.8-0.6,2-1.2,1.8-2.1
	c-0.1-0.4-0.4-1-1.3-1.6c-0.5,0.3-0.8,0.5-1,0.7c-0.2,0.1-0.3,0.2-0.5,0.3c-0.3,0.1-0.7,0-1.4-0.1c0,0,0,0,0,0c0,0,0,0,0,0l0,0
	c1.8,0.4,1.6-0.1,2.9-0.9c-9.6-12.5-11.9-6-17.7-22.2c0,0,0,0,0,0.1c0,0,0,0,0-0.1c-1.4-1.2-2.3-2.3-3-3.4c-1.4-2.2-1.7-4.4-1.9-6.6
	c-0.3-2.6-0.5-5.3-2.6-8.5c-0.8-1.3-2-2.7-3.6-4.1c0,0,0,0,0,0c0-1.9-0.6-3.9-1.5-5.9c-0.7-1.5-1.5-3-2.4-4.5
	c-1.6-2.8-3.2-5.5-3.8-8c-0.3-0.9-2.1-3.9-3.4-6.7c-0.1-0.1-0.1-0.2-0.2-0.3c0-0.1-0.1-0.2-0.1-0.3c-0.1-0.2-0.2-0.4-0.3-0.6
	c-0.7-1.8-1.1-3.3-0.5-3.9c0,0,0-0.1,0.1-0.1c0.2,0.2,0.4,0.4,0.6,0.7c1.3,1.8,2.3,5.3,3.4,7.6c1.4,3,2.8,4.1,4.8-3.1
	c-0.2,6.1,8.4,12.1,9.5,17.4c0.8,4.9,8.6,7.5,7.4,13.5c-0.5,6,5.8,7.1,7.6,12.1c2,4.9,4.4,7.2,6.1,11.6c-0.1,4.2,2,10.9,2.8,14.9
	c2.4,2,9.2-2.7,12-2.5c3.5-0.3,4.5-3.7,8.1-2.8c3.4-1.9,5.2-3.9,11.2-5c5.3-2.2,1.7-4.8,7-5.4c0,0,0,0,0,0c3.9-2.6,8,0.5,8.7-4.8
	c5.3-1.4,3.5,0.1,6.7-4.4c4.4,0.2,1.2-5.3,3.8-6.7c3.6-1.1,3.8-4.1,6.3-7c1.7-2.6-3.2-3.6-3.5-6.5c-1.6-0.6-8.7-1.9-8.5-6.8
	l-1.3,0.8h-1.5v0h1.5l1.3-0.8c-1-13.5-5.7,6.9-13,4c-3.6-0.9-3.7,1.9-5.4,1c0,0.1,0,0.1,0.1,0.2c0-0.1,0-0.1-0.1-0.2
	c-0.6-0.5-1.4-1.6-1.6-2.6c0,0,0,0,0,0c2.1-6.1-1.2-9.3-3.2-2.3c0.4,1.1,1,1.7,1.6,2c-0.6-0.3-1.2-0.9-1.6-2c-0.7-0.5-1.1-1-1.4-1.5
	c-0.6-1.2-0.5-2.5-0.7-3.8c0-0.4-0.1-0.9-0.2-1.3c-0.2-0.7-0.7-1.5-1.5-2.2c-0.2-0.1-0.3-0.3-0.5-0.4c-0.4-0.3-0.8-0.7-1.1-1
	c-0.7-0.7-1.4-1.5-1.9-2.3c-0.5-0.8-0.8-1.7-1-2.7l0,0c-0.3-1-0.8-2-1.2-2.9c-0.2-0.5-0.4-1-0.5-1.5c0-0.1,0-0.1,0-0.2
	c0,0,0-0.1,0-0.1c0-0.4,0-0.8,0.3-1.1l1.7-0.9l8.3,3.5c0.9,1.8,1.8,4,3.3,5.8c7.3,3.7,11.1,7.9,19.7,4.2c2.6,0.5,2,4.1,3,5.3
	c3.5,1.7,13.1,2.5,17.5,3c5.7,0.8,6.7-1.5,10.2-1c0.9,0.6,4.7,0.8,6.2-0.1c1.9-1.1,3,0,3.1,1c3.1,3.8,1.2,5.2,6.6,6.9c0,0,0,0,0,0
	c3.4,5.7,5.7,2.1,7.8,3.7c0,1.7-5.3,1.4-5.4,1.9c0.9,3.8,11.5,11.2,12.3,2.8c-0.8-0.5,0.5-2.4,1.2-2.1c1.9,6.9,0.6,14.2,3.5,22
	c2,10.2,8.1,20.2,11,29.7c0.6,4.5,4.6,10.9,7.4,4.7c0.5-1.7,3.4-2.3,3.6-3.8c0.2-1.4,0.5-2.6,1.5-2.7c2.2-1.1,0.1-6,2.2-8
	c2.4-4.2-1.6-11.5,2.3-15c7.9-4.3,13.9-13.6,21.8-18.4c1.8-5.6,2.4-6.6,9.1-6.7c0-1.6-0.3-3.3-0.6-4.8c0,0,0,0,0,0
	c0.3,1.5,0.6,3.2,0.6,4.8c2.2-0.2,3.8-1,4.9-1.9l5.8,5.1l8.4,15.6c-0.1,1-0.4,2-1,3.1c-0.8,1.4,0.3,1.5,1.6,2.2
	c0.9,0.7,1.8,0.8,2.5,0.5l11.9,22c-0.7,2.3-1.1,4-1.2,5.3c0,0.2,0,0.3,0,0.5c0,0,0,0,0,0.1c-0.1,2.4,0.7,3.5,2.2,5.2
	c1.1,1.3,2.7,2.9,4.4,5.7c0.8-0.2,1.4-0.2,1.9,0c0.4,0.1,0.7,0.4,1,0.6c0,0,0.1,0.1,0.1,0.1c0,0,0,0,0,0c0,0-0.1-0.1-0.1-0.1
	c0,0,0,0,0,0c-0.2-0.2-0.4-0.3-0.6-0.4c-0.6-0.3-1.3-0.5-2.3-0.2c0.4,5.3,2.6,10.8,6,15c2.7,3.3,6.1,5.7,10.1,6.6l0,0l0,0
	c0-0.7-0.2-1.4-0.5-2.1c-0.1-0.2-0.2-0.5-0.3-0.7c-0.4-1-1-2.1-1.4-3.2c-0.1-0.3-0.2-0.6-0.3-0.9c-0.3-0.9-0.5-1.8-0.6-2.7
	c-0.1-1.2,0-2.6,0.6-3.9c0.1-2.7-2.4-2.4-3-4.2c-0.1-0.1-0.1-0.2-0.2-0.3c-0.1-0.2-0.2-0.4-0.3-0.5s-0.2-0.2-0.3-0.3
	c-0.2-0.1-0.3-0.2-0.5-0.3c-0.1,0-0.2-0.1-0.3-0.1c-0.3-0.2-0.5-0.4-0.7-1c-1.3,0.4-0.8,1.6-1.2,1.6c-0.2,0.1-0.4,0.1-0.6,0.2
	c-0.2,0-0.4,0-0.6,0c-0.1,0-0.3,0-0.4-0.1c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0.1,0,0.1,0c0.1,0,0.2,0,0.3,0
	c0.3,0,0.6-0.1,1-0.2c0.1,0,0.1,0,0.2-0.1c0,0,0,0,0,0s0,0,0,0c0.2,0,0.2-0.3,0.2-0.7s0.3-0.7,0.9-0.9c-2.1-1.5-3.5-2.3-4.4-2.9
	c-1.5-0.9-2-1.3-2.6-3.3c-0.4-1.1-0.8-2.6-1.5-4.9c-3-1.5-3-4.1-2.3-7c0.7-2.6,2-5.5,2.3-8c0.1-0.6,0.1-1.1,0.1-1.6
	c-0.3-0.6,0-1.1,0.5-1.4c0.1-0.1,0.2-0.1,0.3-0.2c0,0,0,0,0,0c0.5-0.2,1.1-0.3,1.6-0.2c0.4,0.1,0.7,0.4,0.8,0.8
	c0.2,0.9,0.4,1.5,0.8,1.9s1,0.6,2.1,0.5c0.1,0,0.1,0,0.2,0c2.1,0,2.3,1.9,4.2,2.4c0,0,0,0,0,0c0,0,0,0,0,0c0.2,0.1,0.3,0.2,0.4,0.4
	c0.9,1,0.5,2.6,0.8,3.5c0,0.1,0.1,0.1,0.1,0.2c0.2,0.3,0.7,0.4,1.6,0.2c0.2,0,0.3-0.1,0.5-0.2c-0.3,2.9,0.6,2.4,1.7,2.2
	c0.6-0.1,1.2-0.1,1.7,0.5c0.1,0.1,0.2,0.3,0.3,0.4c0,0,0,0,0,0c0.6,0.4,1.1,0.8,1.3,1c0.5,0.5,0.4,0.9,0.1,1.6
	c-0.2,0.5-0.5,1.2-0.8,2.3c0.2,5.1,5.6-2,6.3-2.7c0.9,0.1,0.6-1.4,1-2.1c2.7-0.8,6.4-1.3,8.7-3.7c1-1.1,1.8-2.6,2-4.7
	c0.3-2.7,0.2-5.1-0.2-7.1c-0.6-3-2-5.4-3.9-7.6c-0.6-0.7-1.3-1.4-2-2.2c-0.4-0.4-0.7-0.7-1.1-1.1c-1.1-1.1-2.4-2.1-3.6-3.3
	c-0.3-0.5-0.7-1-1-1.5c-1.5-2.6-2.3-5.3-1.2-7.8c0.4-1,1.2-1.9,2.2-2.8c-0.3-2.4,2-2.4,3.6-3.4c0.1-0.1,0.3-0.2,0.4-0.3
	c0.2-0.2,0.5-0.4,0.6-0.7c0.1-0.1,0.2-0.3,0.2-0.4c0,0,0,0,0,0c0.7-0.4,2.1,0.3,2.4-0.7c0.4-0.6,0.7-0.8,1.1-0.7
	c0.2,0,0.3,0.2,0.5,0.3s0.3,0.3,0.5,0.5c0.2,0.2,0.3,0.3,0.5,0.5c0.1,0.1,0.2,0.1,0.3,0.2c0.2,0.1,0.4,0.2,0.6,0.1
	c0.3,0,0.6-0.2,1-0.7c1.3-0.3,1.3,0.3,1.2,1.3c-0.1,0.3-0.1,0.6-0.2,0.9s-0.1,0.6-0.1,1c0,0.3,0,0.6,0.1,0.9c0,0.1,0.1,0.3,0.2,0.4
	c0.2,0.3,0.4,0.5,0.7,0.7c0.3,0.3,0.5,0.4,0.6,0.3c0.5-0.1,0.5-1.3,0.4-2.1c-0.1-0.5,0.7-1.1,1.9-1.8c0.3-0.2,0.6-0.4,1-0.5
	c0.2-0.1,0.4-0.2,0.6-0.3c0.4-0.2,0.8-0.4,1.2-0.6c0.6-0.3,1.2-0.6,1.9-0.9c1.7-0.8,3.4-1.5,4.6-2.2c0.3-0.2,0.6-0.3,0.8-0.5
	c0.2-0.2,0.5-0.3,0.6-0.5c0.3-0.3,0.5-0.6,0.4-0.8c2.1,0.8,7.9-1.2,10.5-1c0.6-0.1,1.2-0.6,1.7-1.3c0.2-0.2,0.3-0.4,0.5-0.7
	c0.1-0.1,0.2-0.2,0.3-0.3c0.2-0.2,0.4-0.4,0.5-0.6c0.3-0.3,0.6-0.6,0.9-0.8c0.2-0.1,0.4-0.2,0.7-0.3c0.7-0.2,1.2-0.6,1.6-1.1
	c0.4-0.5,0.6-1,0.6-1.3c0.3-0.2,0.6-0.3,0.9-0.5c1.1-0.7,2.1-1.7,3.1-2.6c0.7-0.7,1.5-1.4,2.3-2.1c0.5-0.7-0.4-2.8,0.5-3.1
	c0.9-0.3-0.3-1.4-0.5-2c3.8-0.9,4-5,7-6.6c0.8-0.3,1-1.9,0.8-3.3c-0.2-1.4,1.3-1,0.6-1.6c-0.7-0.5,0.3-2,1.7-2.4
	c1.4-0.4-0.9-1.1-1.6-0.5c-0.7,0.6-1.6-1.7-2.2-0.8c-0.7,0.9-2.6-0.5-1.4-0.8c0.5-0.2,0.9-0.4,1.3-0.5s0.7-0.3,1-0.5
	c0.4-0.2,0.8-0.4,1-0.7c0.8-0.7,0.6-1.2-0.1-1.8c-0.1-0.1-0.3-0.2-0.5-0.4c-0.1-0.1-0.2-0.1-0.3-0.2c-0.2-0.1-0.4-0.3-0.6-0.4
	c-0.1-0.1-0.2-0.1-0.3-0.2c-1-0.6-2.3-1.3-3.3-2.3c-0.1,0-0.2,0-0.3,0c-0.4-0.1,1.3-0.7,2.5-0.1c5.1,2.7,3.4-1,0.2-3.1
	c-0.6-2-1.4-3.4-2.3-5.3c-0.3-0.6-0.6-1.3-0.9-2.1c-1.6-1.6-4.2-2.7-3.5-4.3c0.1-0.1,0.1-0.3,0.2-0.4c0.2-0.3,0.5-0.6,1-1
	c0.2-0.2,0.5-0.3,0.8-0.5c1.8-0.8,1-2.3,1.8-2.1c0.1,0,0.2,0,0.3,0c0.8-0.1,1.6-1.1,2.4-1.9c0.3-0.3,0.6-0.5,0.9-0.7
	c0.3-0.2,0.6-0.3,0.9-0.3c0.2-0.1,0.4-0.1,0.7-0.2c0.2,0,0.4-0.1,0.5-0.2c0.2-0.1,0.4-0.1,0.6-0.2c0.5-0.2,1-0.3,1.4-0.5
	c0.2-0.1,0.3-0.2,0.5-0.2c0.2-0.1,0.4-0.2,0.6-0.4s0.3-0.3,0.4-0.4c0-0.1,0.1-0.2,0.1-0.3c0-0.5-0.7-1-2.5-1.6l0,0l0,0
	c-0.2,0-0.3,0.1-0.5,0.1c-1.2,0.1-2.5-0.4-3.3-1.7c-0.9-1.4-3.3,2.6-5.2,3.5c-1.8,0.9-3-1.6-2.4-3.2c0.3-1.1-0.8-1.3-2.1-1.5
	c-2.5-0.3-5.8-0.3-1.9-4.9c0.1,0,0.2,0,0.3,0c0.4,0,0.9,0.1,1.3,0.2c1.2,0.2,2.5,0.4,3.2-1.6c1-3,2.3-0.7,4.5-3.3
	c2.2-2.6,4.7-4.2,6.6-2.7c1.9,1.6-2,4-2.6,5.5c-2.9,9.8,6.6-2.1,11.1-1.4c0,0,0,0,0,0c3.9,0.9,4.8,1.9,4.6,2.9
	c0,0.2-0.1,0.3-0.2,0.5c-0.2,0.4-0.4,0.7-0.7,1.1c-0.4,0.5-0.7,1-1,1.5c-0.1,0.1-0.1,0.2-0.2,0.3c-0.4,0.8-0.5,1.7,0.3,2.7
	c0.1,1.1,1.4,2,2,1c0.1-0.2,0.2-0.3,0.4-0.4c0.3-0.1,0.6-0.2,1-0.3c0.3,0,0.6,0,0.9,0c0.2,0,0.4,0.1,0.6,0.1
	c0.4,0.1,0.7,0.3,0.9,0.5c0.4-0.5,0.9-0.9,1.4-1.2c0.3-0.2,0.6-0.3,0.8-0.5c-0.4,0.4-1.4,1-2.2,1.7c0,0.1,0,0.2,0,0.3
	c0,0.2,0.1,0.4,0.3,0.6c0.3,0.4,0.7,0.8,1,1.2c0.2,0.2,0.3,0.4,0.5,0.5c0.4,0.6,0.5,1-0.8,1.2c-1.3-0.3-1.3,1.9-0.2,3.7
	c0,0.2,0,0.4,0.1,0.6c0,0.4,0,0.7-0.1,1c-0.2,0.9-0.6,1.4-0.9,2c-0.1,0.2-0.2,0.5-0.2,0.7c-0.1,0.7,0,1.6,0.5,3.2
	c0.2,0.3,0.5,0.4,0.9,0.4c0.7,0,1.8-0.4,2.9-1c0.8-0.5,1.7-1,2.6-1.5c0.3-0.2,0.6-0.4,0.9-0.5c0.6-0.3,1.1-0.7,1.6-0.9
	c0.3-0.1,0.5-0.2,0.7-0.3c0.5-0.2,0.8-0.3,1.2-0.3c0.2,0,0.3,0,0.4,0.1c0.1,0,0.2,0,0.3,0c0.9-0.1,1.2-1.2,1.1-2.7
	c0-0.3-0.1-0.7-0.1-1.1c-0.2-1.3-0.6-2.8-1.1-4.3c-0.2-0.6-0.5-1.3-0.7-1.9c-0.2-0.4-0.4-0.8-0.5-1.2c-0.8-1.5-1.6-2.7-2.4-3.3
	c-0.6,0.6-1.5,0.8-2.5,0.8c0.3-0.1,0.6-0.1,0.9-0.2c0.5-0.1,1.1-0.3,1.6-0.6c-1.1-0.8-1.9-1.5-2.5-2.1c-0.3-0.3-0.5-0.6-0.7-0.9
	c-0.1-0.1-0.2-0.3-0.2-0.4c-0.3-0.5-0.3-1-0.3-1.5c0-0.2,0.1-0.4,0.2-0.6c0.2-0.4,0.6-0.8,1-1.2c0.1-0.1,0.2-0.2,0.4-0.3
	c0.2-0.2,0.5-0.4,0.8-0.5c1.7-1.1,4-2.1,5.7-3.6c0.3-0.8,0.5-1.6,0.8-2.4c0.1-0.4,0.2-0.8,0.4-1.2c0.6-1.6,1.3-3.2,3-4.6
	c0.4-0.3,0.9-0.7,1.4-1c0,0,0,0,0,0c0.3-0.3,0.6-0.5,0.9-0.7c0-0.3,0-0.6,0-0.9c-0.1-1.5-0.4-3.2-0.5-4.7c-0.1-0.6-0.1-1.2-0.1-1.8
	c0,1.1,0.2,2.4,0.3,3.6c0.1,1,0.2,1.9,0.3,2.8c0,0.3,0,0.6,0,0.9c1.5-1.3,2.6-1.6,3.6-1.4c0.5,0.1,1,0.3,1.4,0.5
	c0.7,0.4,1.4,0.8,2.3,1c0.3,0.1,0.6,0.1,0.9,0.1c0.2,0,0.3,0,0.5,0c0.7,0,1.4-0.2,2.2-0.6c1.5-0.7,3.3-2.2,5.7-4.7
	c0.7-0.9,1.4-1.7,2.2-2.7c0.5-0.6,1.1-1.3,1.6-1.9c0.3-0.3,0.6-0.7,0.8-1c0.6-0.7,1.1-1.3,1.7-2c0.3-0.3,0.6-0.7,0.9-1
	c0.6-0.7,1.2-1.4,1.7-2.1c1.4-1.8,2.8-3.7,4.2-5.6c1.1-1.5,2-3.1,2.9-4.7c0.4-0.8,0.9-1.6,1.2-2.4c1.9-4,3.1-8.1,2.8-12.2
	c0-0.3,0-0.6,0.1-1c0.2-0.7,0.5-1.4,0.9-2.1c1-1.7,2.2-3.5,1.8-5c0.2-1.5-1-3.4,0.2-4.2c0.2-0.3,0.2-0.6,0.1-0.9
	c-0.1-0.2-0.3-0.3-0.5-0.5c-0.2-0.2-0.4-0.3-0.7-0.4c-0.1-0.1-0.3-0.1-0.4-0.2c-0.3-0.1-0.6-0.3-0.8-0.4c-0.8-0.4-1.5-0.9-1.4-1.6
	c-0.1-1.1-1.8-2.5-3-2.4c-0.6-0.1-1.1-0.2-1.5-0.2c-0.8-0.1-1.2,0-1.4,0.1c-0.1,0.1-0.2,0.2-0.2,0.2c0,0,0,0.1,0,0.2
	c0,0.1,0,0.2-0.1,0.4c-0.1,0.3-0.2,0.6-0.8,0.9c-0.6-0.2-0.9,0.4-1.7,1.6c-0.7,1.2-3.4,1.3-2.2,0.2c1.2-1.1-0.4-1.2-0.2-2.4
	c0-0.1,0.1-0.3,0.1-0.4c0.3-1,0.3-1.4,0.2-1.4c0,0,0,0-0.1,0c-0.3,0.1-1.1,1.2-1.8,2.1c0,0.1-0.1,0.1-0.1,0.2
	c-0.2,0.3-0.5,0.5-0.6,0.7c-0.1,0.1-0.1,0.1-0.2,0.1c-1.1,0.2-0.7-3.2-0.5-4.1c-0.8,0-1.5,0-2.1,0c-0.6,0-1.1-0.1-1.6-0.1
	c-0.7-0.1-1.2-0.2-1.5-0.4c-0.5-0.2-0.6-0.5-0.6-0.9c0-0.1,0-0.2,0.1-0.3c0.2-0.5,0.6-1,1.3-1.6c0.9-0.8,2.2-1.7,3.5-2.6
	c1.5-1,3.1-2,4.3-2.9c0.4-0.7,0.9-1.4,1.6-2.1c0.9-1,2.1-1.9,3.3-2.9c0.6-0.5,1.2-1,1.8-1.5c0.3-0.3,0.6-0.5,0.9-0.8
	c0.6-0.5,1.2-1,1.7-1.5c1.1-1,2-2,2.5-3c1.5-1.6,2.8-2.8,4.1-3.9c1.7-1.4,3.3-2.3,4.9-2.9c0.4-0.2,0.8-0.3,1.2-0.4
	c0.8-0.2,1.6-0.4,2.5-0.4c0.4,0,0.9-0.1,1.3-0.1c1.4,0,2.8,0.1,4.4,0.4c1.1,0.2,2.2,0.4,3.5,0.6c0.9-0.4,2.3-0.4,3.9-0.3
	c0.5,0,1.1,0.1,1.6,0.1c0.3,0,0.5,0,0.8,0c1.8,0,3.5-0.2,4.2-1.3c0.1-0.3,0.4-0.5,0.7-0.7c0.2-0.1,0.5-0.2,0.7-0.3
	c0.3-0.1,0.5-0.1,0.8-0.1c1.3-0.1,2.7,0.1,3.2,0.5c0.9,0.7,1.6,1.6,2.9,0.6c1.4-1,1.4,1.3,2.7,1.4c1.3,0.2,0.5,1.4-0.7,1.3
	c-0.4,0.1-0.8,0.2-1.1,0.3c-0.5,0.2-0.7,0.4-0.7,0.5c0,0.1,0,0.1,0.1,0.1c0,0,0.1,0.1,0.2,0.1c0.1,0.1,0.3,0.1,0.5,0.1
	c0.1,0,0.3,0,0.4,0.1s0.3,0,0.5,0c0.1,0,0.2,0,0.3,0s0.2,0,0.3,0c0.3,0,0.5,0,0.8-0.1c0.3,0,0.5-0.1,0.7-0.2
	c0.3-0.1,0.5-0.2,0.7-0.4c0.2,0,0.5,0.1,0.7,0.1s0.5,0,0.8-0.1c1.8-0.2,3.9-1,5.2-0.8c0.9-0.8,3-1.1,4-1c0-0.1,0.1-0.1,0.1-0.2
	c0.1-0.2,0.1-0.3,0-0.5c0,0,0-0.1-0.1-0.1c-0.1-0.1-0.1-0.1-0.2-0.1c-0.1-0.1-0.3-0.1-0.5-0.2c-0.5-0.1-1-0.3-1.4-0.5
	c-0.4-0.2-0.7-0.4-0.8-0.8c-0.1-0.5,0.1-1.2,1.1-2.3c0.3-0.6,0.8-1.2,1.3-1.7c0.2-0.2,0.3-0.4,0.5-0.5c0.4-0.3,0.8-0.7,1.2-1
	c0.6-0.5,1.2-0.9,1.9-1.3c1.5-1,2.9-1.8,3.7-2.4c0-0.5,0.2-1.4,0.6-2.2c0-0.1,0.1-0.2,0.1-0.3c0.1-0.2,0.2-0.4,0.3-0.5
	c0.3-0.4,0.6-0.7,1-0.7c1,0.5,2.6,0.1,4.2-0.2c0.5-0.1,0.9-0.2,1.3-0.3c0.4-0.1,0.9-0.1,1.2-0.1s0.8,0,1.1,0.2
	c0.5,0.2,0.9,0.5,1.1,1c0.8,0.3,1.9-1.7,2.8-1.1c0.2,0.2,0,0.7-0.3,1.3c-0.2,0.3-0.4,0.6-0.6,0.9c-0.3,0.4-0.5,0.8-0.7,1.2
	c-0.2,0.3-0.3,0.6-0.3,0.8c0,0.3,0,0.6,0.3,0.8c0.1,0.1,0.2,0.2,0.4,0.2c0.3,0.1,0.8,0.2,1.5,0.2c1,0.5-1.3,1.2-1,1.7
	c0.2,0.5,2.2,0.5,4.7-2.1c2.5-2.6,4.8-3.3,6.4-3.2c0.2-0.2,0.4-0.4,0.5-0.6c0.1-0.2,0.2-0.3,0.2-0.5c0.5-1.5,0-3.8,1.5-4.9
	c0.2-0.1,0.4-0.2,0.6-0.3c0.2-0.1,0.5-0.2,0.8-0.2c0.3-0.1,0.6-0.1,1-0.1c0.6,0,1.2,0,2,0.1c0.3,0,0.5,0.1,0.8,0.1
	c0.3,0.2,0.6,0.4,0.8,0.6c0.2,0.2,0.3,0.3,0.3,0.3c0,0-0.2,0-0.4,0c-0.1,0-0.1,0-0.2,0c-0.3,0-0.7,0-1.2,0.1
	c-0.4,0.1-0.7,0.3-1.1,0.6c-0.2,0.1-0.4,0.3-0.5,0.5c-0.7,0.9-1.3,2.5-1.3,5.4c-0.1,0.4-0.2,0.7-0.3,1c-0.2,0.6-0.5,1.1-0.8,1.5
	c-0.1,0.2-0.3,0.4-0.4,0.5c-0.5,0.4-1,0.7-1.5,0.8c-0.3,0.1-0.7,0.2-1,0.2c-0.3,0.1-0.7,0.1-1,0.2c-0.8,0.2-1.6,0.6-2.3,1.7
	c-0.5,0.8-1,1.5-1.5,2.1c-0.4,0.4-0.7,0.8-1.1,1.2c-0.2,0.2-0.4,0.4-0.6,0.5c-0.8,0.7-1.7,1.4-2.6,2.2c-0.2,0.2-0.5,0.4-0.7,0.6
	c-0.5,0.4-1,0.9-1.5,1.4c-0.7,0.7-1.5,1.6-2.3,2.6c-1,0.9-1.7,1.7-2.4,2.3c-0.5,0.4-0.9,0.7-1.2,1c-0.2,0.1-0.4,0.3-0.5,0.4
	c-0.4,0.2-0.7,0.4-1,0.6c-0.3,0.1-0.6,0.3-1,0.4c-1,0.3-2.1,0.4-3.8,0.7c-0.1,0-0.1,0-0.1,0c0,0-0.1,0.1-0.1,0.1
	c0,0.1-0.1,0.2-0.1,0.3c0,0.8,0.6,2.6-1,4.4c-1.2,1.4-2,3-2.6,4.7c-0.7,2-1,4.2-1,6.5c-0.1,6.4,2,13.4,3.5,19
	c0.2,0.7,0.3,1.3,0.5,1.9c0.1,0.6,0.3,1.2,0.4,1.8c2.4-1.1,7.5-6.6,6.9-10.1c0.1-0.2,0.3-0.4,0.4-0.5c1.2-1.2,3.1-1.3,4.3-1.9
	c0.3-0.1,0.5-0.3,0.7-0.5c0.3-0.3,0.4-0.8,0.3-1.4c-0.1-0.4-0.3-0.9-0.6-1.6c0.1-0.4,0.2-0.8,0.4-1.2c0.2-0.3,0.4-0.6,0.7-0.9
	c0.3-0.3,0.6-0.5,1-0.7c1.3-0.7,3-1,4.4-1.3c0.6-0.1,1.2-0.3,1.8-0.5c1.5-1.5-0.1-3.2-0.7-4c0.3-4.2,2.1-4.7,5.7-5.1
	c0.4-0.4,0.4-0.9,0.1-1.4c-0.4-0.8-1.5-1.4-2-1.3c-3.2-1.4,4.2-6.4,0.7-6.7c-1.5,0.1-2.9,0.2-3.5-0.6c-0.4-0.6-0.4-1.6,0.6-3.4
	c1.1-0.7,1.9-1.7,2.6-2.8c1.3-2.1,2-4.6,3.7-5.7c0.6-0.4,1.3-0.6,2.3-0.6c0.3,0,0.6,0.1,1,0.2c0.4,0.5,0.7,0.7,1,0.7
	c0.7,0,1.3-0.8,1.9-1.7c0.7-1,1.5-2.2,2.4-2.2c0.4,1.8,0.8,2.8,1.1,3.3c0.1,0.2,0.2,0.3,0.3,0.3c1,0.4,2.3-2.7,6.2-3.5
	c0.5-0.1,1-0.2,1.5-0.2c0.3,0,0.6,0,0.8,0c1.2,0,2.5,0.3,4.1,0.7c0.4,0.2,0.7,0.5,1,0.8c0.1,0.1,0.3,0.3,0.4,0.5
	c0.2,0.3,0.4,0.6,0.6,0.8c0.3,0.4,0.5,0.6,0.9,0.5c0.3-0.5,0.6-1,0.9-1.4c0.2-0.3,0.5-0.5,0.7-0.7c0.9-0.8,2-1.3,3.3-2.1
	c0.7-0.4,1.5-0.9,2.3-1.5c2.5-1.5,3.5-2.6,4.7-3.5c0.2-0.1,0.3-0.2,0.5-0.4c1.2-0.8,2.7-1.5,5.8-2.3c1-0.1,2-0.3,2.8-0.7
	c2.2-0.9,3.9-2.5,5-3.4c0.4-0.3,0.8-0.5,1.2-0.6c0.1,0,0.2,0,0.3,0c0.1,0,0.2,0.1,0.3,0.1c0.3,0.2,1,0.5,1.9,0.8
	c0.4,0.1,0.8,0.2,1.2,0.3c0.8,0.2,1.7,0.3,2.4,0.3c1,0,1.8-0.2,2.2-0.8c0.6-0.9,0.7-1.6,0.4-2.4c-0.4-1.2-1.6-2.2-2.3-3.3
	c-0.3-0.4-0.4-0.8-0.5-1.3c0.1-0.3,0-0.8-0.2-1.2c-0.1-0.2-0.2-0.5-0.3-0.7c-0.2-0.4-0.4-0.7-0.7-1c-0.6-0.7-1.2-1.2-1.5-0.9
	c-0.3,0.2-0.6,0.3-0.8,0.3c-0.8,0-1.1-1-1.3-2c-0.1-0.4-0.2-0.8-0.4-1c-0.1-0.2-0.2-0.3-0.4-0.3c-0.2,0-0.3,0-0.6,0.1
	c-0.4,0.3-0.9,0.4-1.4,0.4c-0.5,0-1-0.2-1.3-0.4c-0.2-0.2-0.4-0.4-0.5-0.7c0.7-1.6,1.8-1.7,2.9-1.3c0.2,0.1,0.4,0.2,0.6,0.3
	c0.4,0.2,0.9,0.5,1.4,0.8c0.2,0.1,0.5,0.3,0.7,0.4c1.2,0.7,2.4,1.2,3.6,0.9c0.4-0.1,0.7-0.3,1.1-0.6c2.3-0.5,6.8-3.6,6.6-5.8
	c0-0.2-0.1-0.3-0.1-0.5c-0.2-0.3-0.4-0.6-0.9-0.9c-1-0.5-0.7-2,0.3-2c1,0,0.7-1.3,1.4-1.3c0.6,0,1.1-0.1,1.4-0.1c0.2,0,0.4,0,0.6,0
	c0.2,0,0.3,0,0.4,0.1c0.1,0,0.2,0.1,0.2,0.2c0,0,0,0.1,0,0.1c0,0.1-0.1,0.3-0.3,0.4c-0.1,0.1-0.1,0.2-0.2,0.3
	c-0.1,0.2-0.2,0.3-0.3,0.5c0,0.1-0.1,0.1-0.1,0.2c-0.1,0.1-0.1,0.3-0.1,0.4c0,0.3,0,0.6,0.1,1c0.2,0.5,0.6,1,1.2,1.6
	c0.3,0.3,0.9,0.4,1.5,0.4c0.4,0,0.9,0,1.4,0c0.7,0,1.5-0.1,2.3-0.1c1.6,0,3.1,0.1,4.1,0.9c0.3,0.3,0.6,0.6,0.8,1
	c-0.1,0.9,0.4,1.6,1.1,2.3c0.3,0.2,0.7,0.5,1,0.7c0.6,0.3,1.2,0.6,1.7,0.8c0.4,0.2,0.8,0.3,1.1,0.4c0.2,0.1,0.3,0.1,0.5,0.2
	c0.3,0.1,0.6,0.2,0.8,0.3c0.1,0.1,0.2,0.1,0.3,0.2c0.1,0.1,0.2,0.2,0.3,0.3c0.7,0.8,2,1.1,3.2,1.1c0.9,0,1.9-0.2,2.7-0.4
	c0.1,0,0.3-0.1,0.4-0.1c1-0.9-2.1-1.5-1.7-2.2c0.4-0.7,1,0.5,1.9,0.5c0.7-1.1,0.9-3.7,0.2-5.3c-0.1-0.2-0.2-0.4-0.4-0.6
	c-0.7-0.2-0.1-1.5,1.2-0.5c0.9,0.8,3.4,1.1,4.3,0.9c0.5-0.1,0.5-0.5-0.9-1.1c-0.3-0.4-0.3-0.6-0.1-0.6c0.5,0,2,0.8,2.7,0.8
	c0.2,0,0.4-0.1,0.4-0.4c-0.2-0.5,0-0.9,0.4-1.2c0.8-0.7,2.6-1.1,2.6-1.5c0-0.2-0.1-0.3-0.6-0.5c-0.6-0.2-1.4-0.8-2.4-1.5
	c-0.5-0.4-1-0.8-1.5-1.2c-0.8-0.6-1.6-1.2-2.3-1.8c-0.5-0.4-1-0.7-1.5-0.9c-0.3-0.1-0.5-0.2-0.7-0.3c-0.5-0.2-0.9-0.3-1.4-0.3
	c-0.2,0-0.4,0-0.6,0.1s-0.4,0.2-0.6,0.3c0,0.7-1.4-0.2-1.8-1c-0.3-0.8-5-0.9-5-0.1c-0.1,0.8,1.1,0.9,1.8,1.9c0.7,1-0.9,1.5-0.6,2.6
	c0.3,1.1-1.7-0.5-2.4-1.1c-0.4-0.5-0.5-1-0.5-1.5c0-0.5,0.1-1.1,0.1-1.7c0-0.3,0-0.6,0-0.8c0-0.8-0.3-1.6-1.3-2.2
	c-1.6-1.4-3.5-2.6-5.5-3.7c-1.2-0.7-2.5-1.3-3.8-1.9c-0.9-0.4-1.7-0.8-2.6-1.2c-2.6-1.2-5.2-2.4-7.4-3.7c-1.4-1-2.9-1.9-4.5-2.6
	c-1.1-0.5-2.2-1-3.3-1.4c-2.3-0.8-4.6-1.4-7-1.8c-1.8-0.3-3.6-0.5-5.4-0.5c-0.6,0-1.2,0-1.8,0c-1.2,0-2.3,0.1-3.4,0.2
	c-0.6,0.1-1.1,0.1-1.7,0.2c-1.3-0.5-8.1-3.1-8.9-2.1c-1,1.1-1.3,2-1.2,2.8c0,0.3,0.1,0.7,0.3,1c0.2,0.4,0.5,0.8,0.8,1.2
	c0.4,0.5,0.8,0.9,1.1,1.3c0.4,0.6,0.7,1.2,0.5,1.8c-0.1,0.2-0.2,0.4-0.3,0.5c-0.1,0.1-0.2,0.2-0.3,0.3c-0.3,0.3-0.7,0.6-1.2,0.9
	c-0.2,0-0.4,0.1-0.6,0.1c-1.3,0.1-2.5-0.4-3.7-1.1c-1-0.6-1.9-1.3-2.8-2.1c-0.7-0.6-1.4-1.1-2-1.6c-0.1-0.5-0.1-0.9,0-1.1
	c0.1-0.3,0.3-0.4,0.5-0.4c0.2,0,0.3,0,0.5,0.1c0.1,0,0.3,0.1,0.4,0.1c0.2,0.1,0.4,0.2,0.7,0.3c0.2,0.1,0.5,0.2,0.7,0.2
	s0.4,0.1,0.6,0.1c0.2,0,0.4,0,0.6-0.1c0.1,0,0.1-0.1,0.2-0.1c0.3-0.2,0.4-0.6,0.5-1.2c-0.6-1-1.3-1.6-2.1-1.9
	c-0.2-0.1-0.4-0.1-0.6-0.2c-0.2,0-0.4,0-0.6,0c-0.2,0-0.4,0-0.6,0.1s-0.4,0.1-0.6,0.1c-0.4,0.1-0.9,0.3-1.3,0.5
	c-0.4,0.2-0.8,0.5-1.3,0.8c-0.8,0.6-1.6,1.4-2.3,2.2c-0.7,0.3-1.6,0.3-2.6,0.3c-1.2,0-2.7-0.2-4.1-0.5c-2.4-0.4-4.9-1-7.3-1.2
	c-0.9-0.1-1.7-0.2-2.5-0.1c-0.5,0-1,0-1.5,0.1c-0.5,0.1-0.9,0.2-1.3,0.3c-0.2,0.1-0.4,0.2-0.6,0.3c-0.4,0.2-0.7,0.5-1,0.8
	c-0.7,0.8-1.1,1.9-1.2,3.4c-0.4-0.3-0.6-0.6-0.8-1c-0.1-0.3-0.1-0.6-0.2-0.9s0-0.6-0.1-0.9c0-0.3,0-0.6-0.1-0.9
	c-0.1-0.3-0.2-0.5-0.3-0.7s-0.4-0.4-0.6-0.5c-0.4-0.2-1.1-0.2-2-0.1c-1.8,0-2.8-0.1-2.1-0.8c3.3-13.4-21.8-7.5-29.2-7.7
	c-2.2,0.1-0.4-0.9-1-2c-0.6-1.1-4.3-2.4-4.9-1.5c-0.5,0.9-1.5,0-1.5-0.7c0-0.7-3.5-2-4.9-1.7c-5.1-1.9,5.2-0.3,2.7-2.9
	c-1.2-1.9-11.5-2.5-12.4-2.1c0,0.7-1.1,2.5-2.5,3.8c-0.2,0.2-0.4,0.3-0.6,0.5c-0.4,0.3-0.8,0.5-1.1,0.7c-0.3,0.1-0.5,0.2-0.8,0.2
	c-0.2,0-0.5-0.1-0.7-0.2c-0.1,0-0.1-0.1-0.2-0.2c-0.2-0.3-0.4-0.7-0.6-1.3c1.1-0.7,1.9-1.1,2.4-1.5c0.4-0.2,0.5-0.4,0.5-0.5
	c0-0.1-0.1-0.2-0.2-0.2c-0.5-0.2-1.8-0.4-4-1.1c-0.6-0.9,0.8-1.5,2.2-1.1c3.9,1.2,5.9-0.7,0.2-1.2c-0.2,0-0.7,0-1.4-0.2
	c-0.3-0.1-0.7-0.1-1.2-0.2c-0.6-0.1-1.4-0.3-2.1-0.4s-1.6-0.3-2.4-0.5c-1.1-0.2-2.3-0.4-3.4-0.6c-0.6-0.1-1.1-0.2-1.6-0.3
	c-2.1-0.3-4-0.4-4.9-0.1c-0.7,0.2-0.9,0.6-0.3,1.4c0.1,0.2,0.1,0.3,0.1,0.4c0,0.1,0,0.1,0,0.2c0,0.1-0.1,0.2-0.2,0.3
	c-0.2,0.1-0.4,0.2-0.7,0.3c-0.2,0-0.4,0.1-0.6,0.1c-0.3,0-0.7,0.1-1.1,0.1c-1.3,0.1-2.8,0.2-3.7,0.5c-0.2,0.1-0.3,0.2-0.5,0.3
	c-0.2,0.1-0.3,0.3-0.4,0.6c-0.1,0.3,0,0.7,0.3,1.1c1.1,1.4,2.2-0.4,3.1,0.1c0.9,0.5-2.4,1.5-1,2.1c0.3,0.2,0.6,0.5,0.8,0.7
	c0.3,0.4,0.4,0.8,0.5,1.1c0,0.2,0,0.5-0.1,0.7c-0.1,0.3-0.3,0.6-0.6,0.8c-0.2,0.1-0.4,0.2-0.7,0.2c-0.3,0-0.5,0-0.8-0.2
	c-0.3-0.1-0.6-0.3-1-0.6c0.1-0.3-0.6-0.4-1.6-0.5c-0.8,0-1.7,0.1-2.5,0.2c-0.2,0-0.4,0.1-0.5,0.1c-0.5,0.1-0.8,0.2-0.9,0.4
	c-0.1,0.1,0.2,0.3,0.8,0.4c1.7,0.2,2.3,1.4,0,1.6c-3.2-2.3-5.7-2.7-7.9-2.6c-0.8,0.1-1.5,0.2-2.3,0.3c-0.5,0.1-1.1,0.2-1.6,0.3
	c-0.9,0.2-1.7,0.3-2.6,0.3c-1,0-2.1-0.2-3.2-0.9c-0.6-0.3-1.2-0.7-1.8-1.3c-1.5-2.3-2.4,0.4-3.8,3c-0.1,0.2-0.2,0.4-0.3,0.6
	c-0.6,1.1-1.3,2.1-2.1,2.7c-0.2,0.2-0.5,0.3-0.7,0.4s-0.5,0.2-0.8,0.2c-0.1,0-0.2,0-0.3,0c-0.1,0-0.2,0-0.3,0
	c-0.2,0-0.4-0.1-0.6-0.2c-0.7-0.2-1.4-0.7-2.2-1.4c-0.1-0.1-0.3-0.2-0.4-0.4c-1-0.9-2.1-2.3-3.3-4c0.1-0.9-2-2.7-3-3.7
	c-0.1-0.1-0.1-0.1-0.2-0.2c-0.2-0.2-0.3-0.4-0.4-0.5c-0.1-0.3,0.3-0.2,1.6,0.3c0.4,0.4,0.8,0.6,1.2,0.7c0.1,0,0.3,0,0.4,0
	c0.9-0.1,1.8-0.9,1.8-1.7c0-0.3-0.1-0.7-0.4-1c-0.2-0.2-0.5-0.4-0.9-0.5c-1.5-0.1-0.9-1.4-0.2-1.6c0.8-0.2-1.7-2.6-0.8-2.8
	c0.9-0.2,0.5-1.3-0.7-1.4c-1.2-0.2-2.6-1.3-2.6-1.9c-0.1-0.7-5.4-0.9-5.1,0.1c0.2,1-1.7,0.7-1.4-0.1c0.3-0.8-1.9-0.5-4.4-1.3
	c-2.5-0.7-2.6-2.6-3.4-2.7c-0.9-0.1-0.6,2.6-1.8,2c-1.3-0.6-2.6,0.5-1.9,2.1c0,0.1,0.1,0.3,0.1,0.4c0.1,0.3,0.1,0.5,0.1,0.8
	c0,0.1,0,0.3,0,0.4c0,0.7-0.1,1.3-0.3,1.7c-0.1,0.1-0.1,0.2-0.2,0.2c-0.2,0.2-0.5,0.3-0.8,0.2c-0.3,0-0.8-0.2-1.3-0.5l0,0l0,0
	c-0.1,0.1-0.3,0.1-0.7,0.2c-0.8,0.1-2.2,0.2-3.6,0.2c-0.6,0-1.2,0-1.7,0c-2-0.1-3.7-0.3-3.9-0.9c-0.6-1.1-1.5-1.2-1.7-0.3
	c-0.1,0.1-0.2,0.2-0.3,0.3c-0.1,0.1-0.3,0.1-0.4,0.2c-0.2,0.1-0.5,0.1-0.8,0.1c-0.5,0-1-0.1-1.5-0.2c-0.2-0.1-0.4-0.1-0.6-0.2
	c-0.4-0.1-0.8-0.3-1.1-0.5c-0.2-0.1-0.4-0.3-0.5-0.5c-0.1-0.2-0.2-0.3-0.2-0.5c0-0.1,0-0.1,0-0.2c0.1-0.4,0.6-0.9,1.7-1.2
	c-0.9-0.5-2.6-0.9-4.6-1.2c-0.7-0.1-1.4-0.2-2.2-0.2c-0.5,0-1-0.1-1.5-0.1c-0.3,0-0.5,0-0.8,0c-0.5,0-1,0-1.6,0c-0.5,0-1.1,0-1.6,0
	c-1,0-2.1,0.1-3,0.1c-0.5,0-0.9,0.1-1.4,0.1c-1.5,0.2-2.9,0.4-3.7,0.7c-0.1,0.1-0.2,0.1-0.2,0.2c0,0,0,0.1,0,0.1c0,0.1,0,0.2,0,0.3
	c0,0.1,0.1,0.2,0.1,0.3c0,0.1,0.1,0.2,0.1,0.4c0.1,0.4,0.1,0.7-0.5,0.8c-0.3,0.1-0.4-0.1-0.5-0.3c0-0.1-0.1-0.2-0.1-0.4
	c-0.1-0.5-0.1-1.2-0.2-1.9c0-0.2,0-0.4,0-0.6c0-0.1,0-0.2,0-0.3c0-0.6-0.1-1.1-0.3-1.4c-0.1-0.1-0.1-0.2-0.2-0.2
	c-0.1,0-0.2-0.1-0.3-0.1c-0.2,0-0.6,0.3-1,0.8c-0.6,0.8-1.2,1.1-1.8,1.1c-0.9,0.1-2-0.5-3.1-1.2c-0.2-0.1-0.4-0.2-0.6-0.3
	c-0.9-0.6-2-1.1-3-1.4c-0.3,0-0.5,0-0.8,0c-3.6,0.2-5,3.3-1.2,3.3c-1.2,0.4-2.4,0.8-3.7,1.3c-1.7,0.7-3.5,1.4-5.4,1.9
	c-0.5,0.1-1,0.3-1.5,0.4c-3.8,0.5-4.6,0.5-6.3,2c-1.8,1.6-0.4-1.4,1-2.8c3.5-1.7,3.7-2.9,7.8-3.3c0.3-0.1,0.6-0.3,0.9-0.4
	c0.6-0.3,1.2-0.7,1.7-1c0.8-0.5,1.6-1.1,2.4-1.7c0.8-0.6,1.6-1.2,2.4-1.7c0.3-0.2,0.5-0.4,0.8-0.5c0.5-0.3,1.1-0.7,1.7-0.9
	s1.2-0.5,1.8-0.7s1.3-0.3,2-0.4c0.4-0.8,1.5-1.5,2.8-2.2c0.7-0.4,1.5-0.8,2.2-1.1c1.3-0.7,2.5-1.3,2.9-2.1c0.1-0.2,0.2-0.3,0.2-0.5
	c0.1-0.6-0.4-1.2-1.6-1.9c-0.1-0.5,0.4-0.4,1.1-0.5c0.3,0,0.5-0.1,0.8-0.2c1.2-0.5,0-2.1-1-1.9c-1,0.3,0.5-0.9-0.3-2
	c-0.9-1.1-2.6,0.7-2.5-0.5c0-0.8,0-1.4-0.1-1.8c-0.1-0.6-0.3-0.9-0.5-1.1c-0.2-0.1-0.5-0.2-0.8-0.2c-0.3,0-0.7,0-1.2,0
	c-0.5,0-1,0-1.6-0.1c-1.3-0.7-2.4-1.1-3.4-1.3c-0.5-0.1-1-0.1-1.4-0.2c-0.8,0-1.5,0-2.2-0.1c-0.3,0-0.6,0-0.9-0.1
	c-1.2,1.1-4.9-0.5-6.5,0.4c-0.4,0.2-0.6,0.6-0.7,1.2c0,0.5-1.1,0.7-2.2,0.7s-2.3-0.1-2.7-0.2c-0.7-0.2,2.8-3.2,2.8-3.8
	c-2.4,0.1-5.3-0.6-8.1-0.9c-0.9-0.1-1.9-0.2-2.8-0.3c-0.2,0-0.3-0.1-0.1-0.2c0.2-0.1,0.6-0.3,1.2-0.4c0.3-0.1,0.6-0.2,0.9-0.3
	c0.8-0.2,1.6-0.5,2.2-0.7c0.2-0.1,0.4-0.2,0.6-0.3c1.5-0.9,0.7-1.6-0.9-2.1c-0.1,0-0.2-0.1-0.3-0.1c-0.6-0.2-1.3-0.3-1.9-0.5
	c-0.9-0.2-1.9-0.3-2.6-0.5c-0.5-0.1-0.9-0.2-1.1-0.3l0,0l0,0c-0.6-0.1-1.5,0.1-2.4,0.3c-0.3,0.1-0.6,0.2-1,0.3
	c-1.3,0.5-2.7,1.2-4.1,2c-2.6,1.6-4.8,3.7-4.6,5.3c0.4,0.4,0.6,0.7,0.8,1c0.1,0.2,0.1,0.4,0.1,0.5c0,0.2-0.1,0.4-0.3,0.5
	c-0.1,0.1-0.3,0.2-0.4,0.2c-0.2,0.1-0.4,0.1-0.6,0.1c-0.2,0-0.5,0-0.7,0.1c-0.6,0-1.4,0-2.1,0c-0.3,0-0.6,0-0.9,0
	c-1.6,0-3,0.1-3.2,0.9c1.5,1.3,2.8,2.3,2.1,2.2c-0.3,0-1.1-0.3-2.5-0.8c-0.4-0.3-0.9-0.4-1.2-0.3c-0.3,0-0.5,0.1-0.7,0.2
	s-0.5,0.3-0.7,0.4c-1,0.7-2,1.7-3.2,1c-1.6,0.1-2.3,0.3-2.5,0.2c-0.2-0.2,0.5-0.7,1.1-2.2c-0.2-0.2-0.6-0.3-1.1-0.3
	c-0.2,0-0.4,0-0.6,0.1c-0.9,0.2-1.8,0.6-2,1.2c-0.3,1.1-1.9-0.8-2.6-0.4c-1.4,2.4-10.8,0-3.9,3.3c-4,0.4-6.3,1.5-11.3,1.7
	c-2.6,0.1-1.5,1.4-3.6,1.8c-3.5-0.3-4,3.9-6.6,2c-0.7-0.8-2.6,0.4-1.5,0.5c1,0.1,0.1,1.2-0.6,1c-3.8,0.9,0.8,0.8-0.4,2.4
	c-0.5,0.7-2.7-0.8-3.3-0.2c-0.6,1.7,4.1,0.6,3.8,2.6c0.1,0.8-2.4-0.5-2.9,0.2c-0.5,0.7,0.4,1,1.5,1.1c1,0,0.5,0.9,1.4,1.6
	c0.6,0.7-0.5,1-0.4,1.7c-0.3,1.9-2.9-1.8-4.7,0.2c-2.9,1.5-13.8,0.5-17.5,1.9c-3.7,1.3,0.1,4.9-1.3,6.9c0.4,2.7,9.9,2.5,7.5,6.9
	c-1.1,1.4-4.4-0.4-6.6-2.2c-4.1-2.8-8.4-1.6-11.7-3.9c-6.8,2.2,0.3,1.7-0.9,2.6c-0.7,0.7,0.4,0.5,2.1,1.3c1.7,0.8-1.4,1.9-3.2,0.6
	c-5.9-2.1-5.1,4.2,1,5.1c2.5,0.5,5.8,3.2,2.9,3.2c-2.8-2-7.7-2.8-11-3c-2.1-3.3,3.7-9.1-1-11.6c-3,0.1,0.7,3.4-2,5.9
	c-8.7,4.6-6.5,3.8-1.8,10.6c-2.3,5.9-5.8,14.7,5.4,10.4c5,0.2,11.9,3.9,7.1,8.7c-0.1,1.8,4.3,2.6,4.5,3.1c-0.7,1-6.4-0.6-6.1-2.9
	c0.6-1.9,1.7-3.9-1-5.5c-3.1-4.1-12.1,0-7.6,5.6c0.6,1.5-3.3,3.6-3.2,4.8c0.2,2.5-6.7,3.3-6.5,5.9c0.1,1.6-2.3,0.7-3.5,0
	c-2.9-0.3-4.1,0.8-7.5-0.7c-3.4,0.9-1.6-4.5,0.2-1.2c0.1,0.8,1.1,0.4,2.2-0.2c1.9-0.3,0.8,2.2,4.2,0.2c1.3,0.1,1.3-0.3,0.9-1
	c-0.4-0.7,0.9-1.1,1.7-1.5c1.2-1.9,2-3.9,4.1-4.8c1.8-0.5-0.5-3.8,1.7-4.6c2-1.4-4.7-3.5-3-9.1c-0.6-3.8,3-10.6-2.2-12.7
	c-2.1-0.8,4.9-6.7,2.9-9.9c-1.8-2.1-14-3.5-14.3,0.2c-2.3,9.6-10.4,9.2-9.3,13.4c2.6,0.9,3.1,0.9,1.7,3.8c1,1.6-2.5,3.2-1.5,4.7
	c2.5,1.2,5.6,1.4,6.2,5.1c3.8,1.4,2.1,3.4-0.1,5.3c-4.1-3.9-10.3-6.5-16.1-8.9c-2.5-2.5-11.6-2.1-14.2-2.6
	c-6.6-6.8-13.1-2.6-3.6,0.7c1.9-0.1,2.5,0.6,2.5,1.8c-0.5,2.1,4.7,4.8,0.6,5.6c-4.6-0.7-0.6,3.6-4.6,2.2c-1.5-0.6-0.6-1.7,0-2.4
	c-2.8-5.1-5.9,2.7-14.8,1.3c-4.3,2.8-4.1,3.5-8.8,2.3c-1.1-0.7-0.2-1.1,1-1.1c1.2,0.1,1.6-0.4,0.9-1.2c-0.7-0.8,0.9-2.1,0.9-2.7
	c-0.1-0.6-4.5,1-5.3,1.6c-0.7,0.7,0.1,1.9-0.7,2.3c-3-3-11.6,1.8-14.3,4.1c-0.4,1.3-6.3-0.1-5.1,5.6c-0.5,1.2-5.1,0.8-6.6,0.9
	c-1.4,0.1-1.9-2.9-3.1-3c-1.6-1.7,0.1-3,2.6-3.6c5.8-0.4,2.8-0.1,1.1-4.1c-1.1-3.3-7.5-0.1-9.7-2c-3.3,0.1,3.1,1.5,1.8,4.4
	c1.4,1.3-4.5,6.7-0.2,5.9c2.7,0.4,1.3,8.4-1.4,6.6c-0.4,0.5-0.9-0.2-1.1-1.2c-0.2-1-2.6-0.4-3.9-0.8c-2.7-0.4-3,3.2-5.9,2.8
	c-5.6,3.2-4.7,4-1.9,9.5c-1.8,1.9-6.3-1.9-8.6-1.3c-1.6-0.8-5.7-5-6.7-0.5c0,1.7,2.7,4.3,4.9,3.7c2.4,0.8-1,4.7-2.6,2.9
	c-0.6-0.8-3,0-3-1c-0.1-1-1.8-2.4-3.5-2.1c-3.1,0.6-1.6-4.1-2.9-5.3c-0.7-1.9,0.9-3.2,0.4-5.2c-1.4-3-7.7-3.8-9.1-7.3
	c-2.6-2.1-0.3-1.8,1.7-0.1c7.9,4.6,25.4,9.8,32.3,1c1.6-9-8.5-8.2-14.6-13.4c-8.2-5.1-10.1-3.7-17.4-4.2c-1.7,0.4-2.6-0.8-2.2-1.3
	c0.5-0.5,3-0.1,2.9-1c-0.5-0.4-5-2.7-5.3-0.5c-0.4,1.1-1.8,0.4-2.6-0.3c0,0,0,0,0,0c-0.9-0.6-2.7-0.7-2.9,0.1c-0.9,1.9-3-2-4.2-2.2
	c0.8-1,3.3,1.6,4.5-0.7c3.3-1.1,3.2-1.3,0.6-3.1c-0.9-1.6-4.1-0.3-5-2c-2.3-2-3.8,5.5-3.9,2.5c0.5-0.9-0.7-2,0.5-2.3
	c2.8-2.4-5.7-3.1-4,0c-0.2,2.9-3.8,5.1-2.2-0.2c-0.3-2.6-5.5,6-6.2,5.2c-2.1-0.4,2.9-5.3,3.2-5.7c2.3-1.5-2.4-2.3-2.6,0.2
	c-1.5,0.1-3.7-1.2-3.4,1.2c0.6,0.9-0.3,1.7-0.9,0.6c-0.7-1.9-4.2,2.2-3.6,2.5c0.7,0.4,0.6,2-0.4,2.3c-4.1-6,0.4-2.4,1.4-6.3
	c-0.2-0.7-1.4-0.5-1.4,0.4c0,0.9-1.7,0.7-3.1,0.7c-3.6,0.1,3.7,5.1-0.9,3.5c-3.2-1.1-3.9,0.4-0.7,2.1c-0.8-0.2-3.4,0.3-3.9-1.6
	c-0.1-1-2.7,0.4-1.3,0.8c2.4,0.3,0.1,5.7-0.4,1.8c-0.4-3-3.3,3-2.5-2.5c-0.5-1-1.5,0-2.7,0.2c-3.3-0.1,1.1,2.8-1.4,3
	c-1-0.2-1.4,0-1.3,1c-0.4,2.5-2.9-3.7-6,3.3c0.5,1.2,3.2-1.8,3,1.3c1.5,3.1-2.3-1.2-2.3,1.3c-0.2,1.3-1-0.2-1.7-1.1
	c-0.7-0.9-1.4,1.7-2.1,1.1c-0.7-0.6,1.5-2.4,0.9-3.3c-0.6-0.9-1,0.5-2.3,1.9c-1.3,1.4-3.1,1.3-2.5,2c1.4,0.9-11.7,8-5.1,5
	c1.3-0.8,5.5-3.4,6.6-2.9c0.6,0.4,1.5,0.7,2.3-0.4c0.7-1.1,1.7-0.8,2.4-0.2c0.7,0.7-1.3,1.2-0.3,2.2c0.9,1-1.1,1.5-1.1,0.7
	c0-0.8-1.1-1.6-1.5-0.8c-1,2.2-3.4,1.4-3.4,4.6c-1.7,1.5-2.3,3.8-5,6.3c-1.6,1.6-0.1,2-0.6,3c-0.5,1-2.4,0.1-3,0.5
	c-0.1,1.7-1.1,4.6-0.5,6.4c0,0.9-2.2-0.5-2.4,0.4c-2,1.6-4.5,0.2-0.5,3.4c1,0.8-1,1.5-1.2,0.5c-0.8-1.5-5.2,3.6-5.8,4
	c-2.7,1.2-0.1,5.3-4,1.3c-1.4-0.1-1.3,1-0.5,2.3c0.8,1.1-5.1,2-5.8,2.8c-0.6,0.9,1.7,0.9,2.1,1.6c0.3,0.7-2.1,0.4-3.3,0.1
	c-1.3-0.3-0.9,1.7-2.1,1.4c-3.4-0.5-0.2,3.8-3.2,2.3c-2.2-0.5-1.9,3.3-0.3,3.8c-0.1,0.5-1.5,1.3-0.5,1.5c1.8,1.1-2.4,1.6-0.4,3.4
	c0.7,0.9-1.2,1.1-0.3,2.4c0.9,1.4,1.6-0.4,2,0.7c0.4,1,1.5,0.1,2.7-1.1c1.3-1.2,1.7,1.3,0.7,1.3c-1-0.1-2.2,0.6-2,1.7
	c0.2,1.1-1.6,0.9-1.5-0.4c0.1-1.3-2.1,0.2-1.3,1.2c0.8,1-0.3,1.5-0.3,2.4c0.8,2.3,3.3-2.9,4.2-0.1c0.4,1-1,0.2-1,1.6
	c0,1.4-0.9,0.2-1.6,1.3c-0.7,1.1,2.9,3,4.1,3.3c5.5,4.6,10.2-4.8,14.4-4.9c1.2-2.9,0.6-4.2,2.5-0.7c0.5,0.2,0.9,0.6,0.9,1.8
	c0,0,0.1,0,0.1,0c0,0-0.1,0-0.1,0c0.8,4.1,3.1,8.5,5,12.3c2.1,1.5-0.2,3.7,1.9,4.9c0.1,0.8-0.3,2.6,0.1,3.4c0.7,0.8,5.6,0,4.5-2.4
	c1.2-4,6.1,0.7,6.6-3.5c0.2-1.4,1,0.3,1.4,0.6c0.4,0.8,4.5-8,0.8-3.6c-1.8,1.4,1.2-7.4,0.6-8.5c-0.2-2.5,6.2-3.4,5.7-5.8
	c-0.3-0.9,2.3-2.4,2.3-3.3c-0.3-2.4-4.8-4.8-6.4-5c-0.5-3.2-0.1-8.9,2-11.6c2.9-4.7,10.5-7.8,14.1-11.9c0.6-1.3-2.1-2.6-0.4-3.9
	c1.9-4.8,4.6-6.6,10.1-5.7c0-0.5-0.1-0.9-0.2-1.2c0.1,0.3,0.2,0.8,0.2,1.2c1.1,0,2.3,0.7,3.2,1.6c0.9,0.9,1.6,2.1,1.5,3
	c0,0.6-0.3,1-0.9,1.3c0,0,0,0-0.1,0c-0.6-0.4-1.2-0.2-1.7,0.7c-0.2,0.3-0.3,0.7-0.4,1.1c-0.2,0.7-0.5,1.3-0.9,1.8
	c-1.4,1.9-3.9,2.5-5.4,4.1c-1,0.2-1.4,0.8-1.8,1.4c-0.5,0.9-0.7,2-1.8,2.2c-0.3,0.1-0.7,0.1-1.1,0c-0.2,0.2-0.4,0.4-0.6,0.7
	c0,0,0,0,0,0c-1,1.1-2.1,2.7-1.1,4c0,0.1,0.1,0.1,0.2,0.2c0.2,1.8,0.7,2.8,1,4.1c0.2,0.9,0.3,1.9,0,3.3c-1.1,2.2-1.4,3.3-1,3.9
	c0.4,0.8,1.7,1,3.6,1.7c1.3,1.2,2.9,1.8,4.7,1.9c0.1,0,0.1,0,0.2,0c0.1,0,0.2,0,0.2,0c5.4,0.3,12.3-2.9,16.9-3
	c0.6-0.9,1.3-1.8,2.1-2.6c0.4-0.3,0.8-0.6,1.1-0.9c-1.2,1.1-2.3,2.3-3.2,3.5c3.8-1.9,2.3,2.9,7.4,2.8c1,0.9-6.4,0.5-7.4,3.4
	c0,0,0,0,0,0c-3.9,1-11.7-1.7-15.4,1.1c-3.6,2.1-2.2,1.6-1.5,4.9c0.7,2.6,5.1,0.1,3,3c0,0,0,0,0,0c-1.1,1.2-0.3,2.1-0.3,4
	c-1.5,4.3-5.6-0.3-6.6-2.6c-3.1-0.4-7.4,5.7-5.7,9.1c2.5-0.6,5-1,7.4-1.1c0,0,0,0,0,0c-2.5,0.1-5,0.5-7.4,1.1
	c0.4,0.6,0.6,1.1,0.5,1.6c0,0.5-0.2,1-0.4,1.4c-0.3,0.6-0.6,1.2-0.6,1.7c0,0.3,0.1,0.5,0.3,0.7c0,0,0,0,0,0c2.1,1.2-1.4,1.5-0.7,2.8
	c0.1,3.4-3.7-3.9-4.7,1.1c2.6,1.2,7.4,1.7,12,1.2c0-0.1,0-0.3,0-0.4c0,0.1,0.1,0.1,0.1,0.2c0,0.1,0,0.2,0,0.3
	c-0.1,0-0.1-0.1-0.1-0.1c-1.8,0.2-3.7,0.2-5.4,0.1c-1-0.1-2-0.1-2.9-0.3c-1.5-0.2-2.8-0.6-3.7-1c-0.3,0.5-0.7,0.9-1.2,1
	c-0.1,0-0.1,0-0.2,0c0,0-0.1,0-0.1,0c-0.3,0-0.7,0-1-0.1c0,0,0,0,0,0c0,0,0,0,0,0c-0.9-0.3-1.6-1.2-1.4-2.3c0,0,0,0,0,0
	c-2.6-1.3-13.5,4.8-15.5,5.6c0,0,0,0,0,0c-1.2-3.6-4,0.4-2.3-4.1c-2.1,0.4-5.8,1.2-7.8,3.6c-1.2,1-2.9-0.2-2-1.6
	c1.3-2.8-4.8,1.8-4.5-3.1c0,0,0,0,0,0c-0.5-0.9-1.6-1.5-1.5-2.2c0.5-1.5,4,2.7,4.7,0.4c0.1-0.5-0.3-3.1-1.4-2.5
	c-2.7,1.1-1.7-1.3-0.7-2.7c1.9-1.3,4-1.5,0.6-3.3c-1.8-1.6,1.7-4.5,0.3-6.3c-0.6-0.6-2.6,0-2.8,1.6c-0.2,1.6-1.8,0.9-2.3,1.3
	c-0.5,0.4,0.9,1.1,0.2,1.8c-2.4-0.7-2.7-2.7-4.2,1.7c-1.3,3.2,1.3,7,2.1,9.9c0,0,0,0,0,0c1.4,2-0.2,3,1.7,5.4
	c0.7,1.5-1.6,0.7-1.7,1.6c-0.9,1.4-6-2.6-5.9,0.9c0.1,0.2,0.1,0.4,0.1,0.6c0-0.2-0.1-0.4-0.1-0.6c-11.3-0.1-3.7,5.9-8.3,3.5
	c-2.3-2-3.4,7.9-7.2,8.4c0,0,0,0,0,0c-0.8,0.5-1.7,1-2.6,1.2c0,0,0,0,0,0c-5-0.7-1.2,7.3-7.2,6.4c-3.2,1-2,6.1-7.4,1.1
	c-6.9-2.2,4.2,7.6-5.1,5.7c-2.8-0.2-1-2.1-6.1-0.8c-4-0.2-2,2.5-1.9,4.4c1.1,0.8,7,2.1,8.5,2.7c2.2,1.2,0.7,4.6,4.3,5.3
	c1.6,1.3,1.2,7.1,0.1,11.3l-1.7,4c-1.2-0.3-2.6-0.5-4-0.6c-1.4-0.1-3-0.1-4.5-0.1c-1.7,0-3.4,0-5.1,0c-0.1,0-0.1,0-0.2,0
	c-0.8,0-1.5,0-2.3-0.1c-3-0.1-5.8-0.5-7.9-1.4c0,0,0,0,0,0l0,0c0,0,0,0,0,0c-3.3,2-4.6,2.7-5,3.1c0,0,0,0,0,0c0,0,0,0,0,0
	c-0.3,0.3,0.2,0.5,0.8,1.2c0.5,0.7,1.1,2,1,4.5c0.3,1.2,0.4,2.3,0.4,3.4c-0.2,2.7-1.3,5.2-2.1,7.3c-0.7,1.8-1,3.2-0.4,4.2
	c0.2,0.3,0.5,0.6,1,0.9c0.1,0,0.2,0.1,0.3,0.1c1,1.9,0.8,3.8,0.8,5.2c0,1.2,0,2.1,0.8,2.4c0.8,0.4,2.2,0.2,5-0.6c0,0,0,0,0,0
	c3.5-1.3,3.8,4.9,6.7,4.8c1.2-1,0.9-2,3.1-1.4c1.4-1.3,6.5-1.8,8.5-1.6c3.4,0.3,2.5-3.3,5.8-3.6c1.5-0.2,1.3-1.9,1.4-2.9
	c0.1-1,2.7-2.4,2.9-3.1c-3.8-12.1,13.5-11.1,12.6-17.5c-0.8,0.2-1.6,0.2-2.5,0.1c0.8,0.1,1.7,0.1,2.5-0.1l1.1-4.3c0,0-0.1,0-0.1,0
	c0,0,0.1,0,0.1,0v0c0.5-0.3,1.2-0.5,1.9-0.6c0,0,0,0,0,0c0.1,0,0.1,0,0.2,0c2.4-0.2,5.4,0.5,6.8,1.5c0.4,0.5,1,0.6,1.7,0.3
	c0.5-0.2,0.9-0.4,1.4-0.8c1.4-1,2.8-2.4,3.8-2.7c0,0,0,0,0,0c2.4-0.1,2.2-1.6,3.9-2.6c0.2-0.1,0.4-0.2,0.6-0.3
	c0.9-0.1,1.7,0,2.4,0.1c1.1,0.4,2,1,2.8,1.9c0.6,0.9,1,2,1.5,3.1c0.1,0.5,0.4,1.1,0.9,1.8c0.6,0.9,1.3,1.8,2.4,2.5
	c2.2,2.1,4.8,4.1,5.6,4.9c0.8,0.3,1.6,0.7,2.3,1.1c2.2,1.4,4.4,2.7,6.7,4.6c0.9,0.9,1.6,1.9,2.1,3.1c0.1,0.1,0.2,0.2,0.3,0.3
	c0.2,0.6,0.3,1.2,0.3,1.8c-0.1,0.9-0.4,1.8-0.7,2.7c-0.4,1.4-0.4,2.1,0,2.4c0.1,0.1,0.2,0.3,0.4,0.4c0.2-0.1,0.4-0.3,0.6-0.6
	c0.5-0.4,1-1.2,1.2-2.2c0.3-0.6,0.6-1.3,1.1-1.8c0.4-0.3,0.9-0.6,1.3-1.1c0,0,0,0,0,0c0.3-0.4,0.3-0.7,0.1-1c0,0,0,0-0.1-0.1
	c0,0,0-0.1-0.1-0.1c-0.1-0.1-0.2-0.2-0.4-0.3c-0.2-0.2-0.5-0.4-0.8-0.7c-0.3-0.3-0.5-0.6-0.6-1c-0.4-1.6,1.7-3,3.7-1.7
	c2,1.3,2.8,2.2,2.9,0.2c0.1-2-6.2-4.7-7.7-5.7c-0.3-0.4-0.5-0.7-0.9-1c-0.3-0.8-0.4-1.4-2-1.4c0,0-0.1,0-0.1,0
	c-1.3-0.6-2.6-1.1-3.7-2.1c-1-1.2-1.8-2.7-2.2-4.3c0,0,0,0,0,0c-0.9-3.5-4.5-3.7-5.4-5.8c0-1.1-0.1-2.2-0.1-3.2c0-0.3,0-0.6,0.1-0.9
	c0.2-1,0.6-1.8,1.8-2.2c0.6-0.2,1.1-0.4,1.6-0.4c0.2,0,0.4,0,0.6,0c0.2,0,0.5,0.1,0.6,0.3c0.2,0.2,0.3,0.5,0.3,0.9c0,0,0,0,0,0
	c-0.4,3.1,3.3,0.6,3.7,3c1.6,5.5,6.5,8.1,11.4,10c-0.1-0.2-0.3-0.4-0.5-0.6c0.9,0.9,1.8,1.7,2.4,2.2c0,0,0,0,0,0
	c1.1,0.9,2,1.8,2.9,2.2l1.1,2.3c0.1-0.6,0.1-1.2-0.3-1.6l0.1,0.1c0.3,0.5,0.3,1,0.2,1.6l0,0c0,0,0,0.1,0,0.1c-0.3,1.3-1.1,3-0.7,4.9
	c0.3,1.4,1.1,2.9,3.1,4.5l0,0c0,0,0,0,0,0v0c0,0,0,0,0,0c0,0,0,0,0,0c3,0.3,1.3,7.1,5,4.5c0.9-1.1,3.5,0.6,3.5,1.3
	c0,0.7-2.6-0.8-4.1,0.4c-1.5,1.1,1.1,2.2,0.8,3.4c-0.7,2.7,4.2,3.8,5.6,4.6c0.8-0.4-1-3.6-0.7-4.1c1.8,0.7,5.1-1,0.9-1.7
	c-1.2,0.1-0.1-1.7,1.4-0.8c1.4,0.9,2.1-0.2,2.8-0.4c0.7-0.1,0.9-2.7-1.3-3.3c-2.1-0.6-1.8,1.6-3.2,0.1c-1.4-1.5,1.3-0.9,1.3-2.1
	c0.1-1.2-6.5-7.6-1.1-5.7c0.6,1.1,2.1,0.9,3.1,0.5c1-0.4-1-2.3,0.7-3c1.8-0.7,1.8,0.5,2.3,0.8c0.9-0.8,3.9-2.5,5.5-0.2c0,0,0,0,0,0
	c2.3,2.5,3.9,0.2,5.8-1.2c10.2-0.9,2.9-0.2,1.3-5.1c0,0,0,0,0,0c-2.5-2.5-1.2-4.4,0.6-6.4c0,0,0,0,0,0c0.1-0.1,0.2-0.2,0.3-0.3
	c0.5-0.5,1.1-1.1,1.6-1.6c-1.1-0.8-2.1-1.3-3.1-1.6c0,0,0,0,0,0c1,0.3,2.1,0.8,3.2,1.6c-1.1-6.7,3.9-3.8,3.9-7.8c0,0,0,0,0,0
	c-0.1-1.9,0.2-1.8,1.8-3c2.5-3.3,3.1-5.2,7.6-4.7c2.2,0.4-1.1,1.8,0.4,2.6c1.5,0.8,6.1-0.2,6.3,1c-1.8,3.2-9.7,1.7-1.3,4.8
	c-0.1,6.1,2.9,2,7,0.7c1.5-1.1,4.7-0.1,5.1-0.8c0.4-1.9-8.3,0-7-4.4c2.1-3.4,8.9-4.1,12.9-5.7c0,0,0-0.1,0-0.1c0,0,0,0.1,0,0.1
	c7.2-1.4,2.4,1.7-0.9,2.8c-0.8,0.3,1.5,1.6,1.5,2.3c-3,1.2-1.4,4.2-4.4,4.1c-4.3,1.3,8.6,6.4,10.2,9.8c0,0,0,0,0,0c0,0,0,0,0,0
	c0,0,0,0,0,0c0,0,0,0,0,0c0.8,0.5,1.8,0.8,2.7,1c1.5,0.4,2.9,0.9,3.2,2.4c0.4,1.5,1.2,2.3,1.5,3c0,0.1,0.1,0.1,0.1,0.2
	c0,0.1,0,0.1,0,0.2c0,0,0,0.1,0,0.1c0.1,0.5-0.2,1.1-1,2.1c0,0,0,0,0,0c0,0,0,0,0,0c-10.4,6.4-16.8,0.5-27.7-2.1
	c-9.1-0.6-12.9,6.5-20.2,4.2c0.2,4.7-7.5,3-10.1,3.8c-4.2,3.2,1.2,2.4,0.8,4.1c1.1,2.1-1.1,4.1,0.5,5.8c2.5,2.9-2.4,1.2,1.9,4.3
	c0.1,0.6,2.5,0.9,2.5,1.6c0,0.7,0.7,1,1.9,0.6c1.2-0.4,1.7,1.4,2.4,2.6c6.2,2.7,4.9-7.4,13.4,0.8c2.3,0.2,6-1.3,7.6-3.1
	c0.7-0.4,1.9,1.4,3,0.8c5.3-2.8,1.4,1,2,3.8c-0.1,0.5-0.2,0.9-0.1,1.3c0,0,0,0.1,0,0.1v0c0,0.3,0.1,0.6,0.1,0.9
	c0-0.3-0.1-0.6-0.1-0.9l-0.5,5.1c-0.6,1.4-1.7,3.1-2.6,5.1l0,0c-0.3,1-0.7,2.3-1.1,3.5c-0.3,0.8-0.6,1.7-0.9,2.5
	c-0.5,1.2-1,2.3-1.6,3.1c-0.5,0.5-1.7,0.7-3,0.7c-0.1,0-0.2,0-0.3,0c-0.1,0-0.3,0-0.4,0c0,0,0,0,0,0c-2-0.1-4.4-0.6-5.7-1.3
	c-0.8-0.4-2.3-0.4-3.8-0.2c-1.9,0.3-3.9,0.9-4.3,1.5c-1.2,0.7-3.4,0.6-5.8,0.1c-4.2-0.9-9.1-2.8-11.5-3.2c0,0,0,0,0,0
	c-0.5-1.1-1.7-1-5.3-1.6c-0.2-0.2-0.4-0.3-0.7-0.5c-1-0.8-2.2-1.4-3.3-1.8c-2.3-0.8-4.7-0.8-6.8,0.1c-1.6,0.7-2.9,1.9-3.8,3.6
	c0,0,0,0,0,0c0,0,0,0,0,0c1.2,3.7,0.9,5.4-0.2,6c-1,0.5-2.6,0.1-4.4-0.6c-2.7-1-5.8-2.6-8-2.8c-1.7,0-3-0.8-3.6-1.7
	c-0.2-0.4-0.3-0.8-0.3-1.1c-0.1-0.7-0.5-1.3-1-1.7c-1.1-1.1-3.1-1.5-5.2-1.7c-2.1-0.2-4.4-0.3-6.2-0.7c-0.6-0.2-1.2-0.4-1.6-0.6
	c-1.3-0.5-1.4-2.1-1.9-2.9c-0.2-0.3-0.6-0.5-1.1-0.2c-1.6,0.6-2.7-0.2-2.8-1.2c0,0,0,0,0,0c-0.1-1,0.7-2.3,2.8-3
	c1.9-1.5,1.4-2.9,0.6-4.3c-0.6-1.2-1.4-2.3-1-3.3c0-0.1,0.1-0.1,0.1-0.2c0,0,0,0,0-0.1c0.1-0.2,0.2-0.3,0.3-0.5c0,0,0,0,0.1-0.1
	c0,0,0,0,0.1-0.1c0.2-0.2,0.3-0.3,0.6-0.4c0.5-0.9,0.6-1.3,0.5-1.5c0,0,0,0-0.1,0c0,0,0,0-0.1,0c0,0-0.1,0-0.1,0c-0.6,0.1-2,1-2.4,0
	c-0.1-0.1-0.1-0.3-0.1-0.6c0,0,0-0.1,0-0.1c-1.1-2.6-5.2,0.1-7.3,0.4c0,0,0,0,0,0c-1.5-0.6-2.9-0.7-4.2-0.6c-3,0.2-5.8,1.5-8.7,1.4
	c-3.8-0.8-8.5-0.2-13.2,1.1c-0.5,0.1-0.9,0.3-1.4,0.4c-5.1,1.6-10.1,4.1-13.5,6.7c0,0,0,0,0,0c0,0,0,0,0,0
	c-17.1-0.6-12.2-10-18.9,4.7c-2.9,3.5-7.2,1.8-8.8,6.4c-5.8,8.9,0.4,10.9-5.8,16.2l-9.4,5.8c0.5,0.1,1,0.1,1.5,0.1l0,0
	c-0.5,0-1-0.1-1.5-0.1c-5.4,8.8-11.1,15.9-15.2,27.5c-0.2,2.6,1.3,3.5,2.2,4.6c1.2,1.4-0.9,3.9,0.9,6.5c2.3,4.7-2.3,7.2-1.9,11.5
	c0,0,0,0,0,0c-0.5,1.3-0.8,2.7-0.9,3.9c0,0,0,0,0,0c0,0.1,0,0.2,0,0.2c0,0.1,0,0.3,0,0.4c0,0,0,0,0,0c0,0.6,0,1.2,0.1,1.8
	c0,0,0,0,0,0c0,0.2,0.1,0.4,0.1,0.6c0,0.1,0.1,0.3,0.1,0.4c0,0.1,0,0.1,0.1,0.2c0.3,0.8,0.7,1.5,1.3,2.2c2.7-0.4,4-0.6,4.8-0.7
	c-0.8,0.1-2.1,0.3-4.8,0.7c0,0.7-0.4,1.5-0.5,2.4c0.8-0.2,1.7-0.3,2.7-0.5c0.3-0.1,0.7-0.1,1-0.2c0.2-0.1,0.4-0.1,0.6-0.2
	c-1.3,0.5-3,0.5-4.2,0.9c-0.2,0.9,0.2,1.7,0.8,2.4c0.5,0.7,0.9,1.2,1.3,1.5c0.7,0.6,1.2,0.6,1.7,0.9c0.2,0.1,0.5,0.3,0.7,0.6
	c0.3,0.5,0.7,1.2,1.1,2.5c0.2,0.1,0.3,0.2,0.4,0.3c0,0,0,0,0,0c0.2,0.3,0.3,0.5,0.5,0.8c0.3,0.4,0.6,0.8,0.9,1.2
	c0.2,0.2,0.4,0.4,0.6,0.7c0,0,0,0.1,0.1,0.1c0.5,0.5,0.9,0.9,1.4,1.4c0.3,0.3,0.6,0.6,1,0.9c0,0,0,0,0,0c0.2,0.2,0.5,0.5,0.7,0.7
	c0.2,0.2,0.4,0.4,0.5,0.6c0,0,0.1,0.1,0.1,0.1c0.2,0.3,0.5,0.6,0.7,1c0.2,0.3,0.4,0.6,0.6,1c0,0,0,0,0,0c1.5,5,2.8,6.3,6.7,8.7
	c0.3-0.8,1-1.4,1.7-2c0,0,0,0-0.1,0c-0.7,0.5-1.4,1.1-1.6,1.9c0.6,0.3,1.2,0.7,1.8,1.1c2.1,1.5,4.5,3.4,6.7,5
	c0.6,0.5,1.3,0.9,1.9,1.3c1.6,1,3.1,1.8,4.6,2.1c0,0,0,0,0,0c3.4-0.1,14.2-4.5,18.9-2.1c0.1-0.3,0.2-0.6,0.2-0.8c0,0,0,0,0,0
	c0,0.3-0.1,0.6-0.2,0.9c1.4,0.9,3,1,4.5,0.6c1.1-0.3,2.3-0.8,3.5-1.3c2.3-1.2,4.6-2.6,6.9-3.3c0,0,0,0,0,0c0.2-0.2,0.5-0.3,0.7-0.5
	c0.5-0.3,0.9-0.6,1.5-0.8c0.2-0.1,0.5-0.1,0.7-0.2c0.9,0.1,1.8-0.1,2.6-0.4c0.2-0.1,0.5-0.2,0.7-0.3c0,0,0.1,0,0.1-0.1l0,0
	c0.2-0.1,0.3-0.1,0.5-0.2c-0.2,0.1-0.3,0.1-0.5,0.2l1.8-0.4c6.1,1.2,8.2,3,9,4.7c1,1.8,0.5,3.4,2.4,4c0.5,0.1,1.1,0.2,1.9,0.2
	c0,0,0,0,0,0c1.6,0,4.1-0.4,7.8-1.2"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M814.2,509.8c-0.1-2.3-2.6-1.3-2.7-3.7c-0.1-0.4-0.2-0.9-0.4-1.3c0,0,0-0.1,0-0.1c-0.2-0.6-0.4-1.2-0.6-1.8
	c-0.5-1.5-0.9-2.9-0.9-4.2c0-1.1,0.2-2.1,0.7-3c-1-2.7-2.3-3-1.9-5.3c0.2-1,0.6-2.3,1.5-4.2c0,0,0,0,0,0c2.7-8,3.8-9,8.7-12.8"
        />
        <MapPath initial={{ pathLength: 0 }} animate={mapPathAnimation} d="M796.2,526.3" />
        <g>
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1076.5,462c-0.2,1-0.1,2.1,0.2,3.3c0.2,0.7,0.4,1.5,0.7,2.2c0,0.1,0.1,0.3,0.1,0.4c0.1,0.3,0.2,0.6,0.3,0.8
		c0,0.1,0.1,0.3,0.1,0.4c0.1,0.3,0.2,0.5,0.3,0.7c0.1,0.3,0.2,0.7,0.3,0.9c0.8,1.3,3.3,1.5,3.3,4.1c0,2.5,2.2,6.4,3.7,5.4
		c0.5-0.1,0.9-0.2,1.2-0.2c0.2,0,0.3,0,0.4,0.1c0.1,0,0.1,0,0.2,0.1c0.1,0.1,0.2,0.1,0.3,0.2c0,0,0.1,0.1,0.1,0.1
		c0.2,0.2,0.3,0.3,0.5,0.5c0,0,0.1,0.1,0.1,0.1c0.1,0.1,0.2,0.2,0.3,0.3c0.1,0.1,0.2,0.2,0.3,0.2s0.2,0.1,0.4,0.2
		c0.8,0.3,2,0.2,4.5-0.7c1.1,0.2,4.7,1,4.9,2.4c8,2.1,6-5.6,8.6-9.9c0.1-0.1,0.3-0.3,0.4-0.4c0.2-0.3,0.5-0.5,0.6-0.8
		c0.7-1,1-2,1.2-3c0.2-1.1,0.3-2.1,0.8-2.9c0.2-0.3,0.4-0.6,0.6-0.8c0.2-0.2,0.4-0.3,0.6-0.5c0.2-0.2,0.5-0.3,0.8-0.4
		c0.8-0.3,1.7-0.6,3.1-0.9c0.3-1-0.4-1.7-1.3-2.4c-1.1-0.9-2.6-2-2.6-4c-1-1-2.7-3.5-1.3-3.6c1.5-0.2-1.4-2.9,0.8-3.3c0,0,0,0,0,0
		c1.3-0.7,3.3-1.6,2.4-2.4c-0.3-0.2-0.2-0.4,0.1-0.4c0.1,0,0.2,0,0.3-0.1c0.9-0.2,2.9-0.1,3.2-1.1c0,0,0,0,0,0s0,0,0,0
		c0.1-0.3,0.1-0.6,0-0.9c-0.1-0.6-0.5-1-1.1-1.1c-0.5-0.3-1.1-0.4-1.5-0.3c-1.4,0.4-2.6-0.4-3.7-1.4c-1.1-2.1-2.1-2.7-3.1-2.6
		c-0.9-0.3-1.8,0.2-2.7,2.4c-0.6,0.8-1.1,1.8-1.6,2.7c-0.6,0.3-1.2,0.7-1,1.6c0,0,0,0,0,0.1c-0.3,0.4-0.6,0.8-0.8,1.1l-4.6,0.5
		c-0.4,0.2-0.8,0.5-1.1,0.8c-1.2,1.3-1.7,3.5-2.8,5.1c-0.1,0.1-0.1,0.2-0.2,0.3c-0.1,0.1-0.1,0.1-0.2,0.2c-0.1,0.1-0.2,0.3-0.4,0.4
		c-0.9,0.8-2.3,1.2-4.4,0.7c0,0-0.1,0-0.1,0c-1.9,0.7-2.2,2.1-2.3,3.2c-0.1,1.2,0,2.3-1.2,2.2c-0.8-0.1-2.2-0.6-4.7-1.9c0,0,0,0,0,0
		c-0.5,0.1-1,0.2-1.4,0.4C1077.1,460.5,1076.7,461.2,1076.5,462z M1105,450.2c-0.4,0.1-0.7,0.3-1,0.5c0.1-0.1,0.3-0.2,0.5-0.3
		C1104.7,450.3,1104.9,450.3,1105,450.2z M1103.7,451.1c0.1-0.1,0.2-0.2,0.4-0.3C1103.9,450.8,1103.8,450.9,1103.7,451.1z
		 M1099.2,461.2c0.1-0.2,0.3-0.3,0.4-0.5c0.1-0.1,0.1-0.3,0.2-0.4c0,0,0,0,0,0c-0.1,0.2-0.1,0.3-0.2,0.4
		C1099.5,460.9,1099.4,461,1099.2,461.2C1099.2,461.2,1099.2,461.2,1099.2,461.2z M1091,462.3c0.6-0.2,1.1-0.4,1.6-0.5c0,0,0,0,0,0
		c-0.5,0.1-0.9,0.3-1.4,0.5C1091.2,462.2,1091.1,462.3,1091,462.3z M1102.9,454.3c-0.1,0.2-0.2,0.3-0.4,0.4
		c-0.7,0.5-1.7,0.5-1.6,1.5c-0.1-1.6,2.5-0.5,2.3-3.5c0-0.6,0.2-1.1,0.5-1.4c-0.3,0.4-0.4,0.9-0.5,1.4c0,0.6,0,1-0.1,1.3
		C1103,454.1,1102.9,454.2,1102.9,454.3z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1093.6,502.7c0.3,0,0.6,0,0.9,0c0.3,0,0.6,0,0.9,0c0.9,0,1.7-0.1,2.3-0.1c0.2-0.2,0.3-0.3,0.5-0.3
		c0.1,0,0.3,0.1,0.4,0.1c0.9,0.4,2,1.5,3.6,0.3c2.3-1.8-3.8-1.1-3.9-2.5c-0.1-1.4-4.6-0.1-6.7-1.1c-2.2-1,2.4-1.8,3.9-2.8
		c1.5-1-0.9-1.3-4.6-0.1c-3.7,1.2-7.9-2.6-8-1c-0.1,1.7-6.6-0.7-8.2-1.9c-0.2-0.2-0.4-0.3-0.6-0.4c-1.1-0.7-2.6-1-4.1-1
		c-3-0.1-6.1,1-6.2,3.4c0.3,1.4,1.8,0.6,2.8,0.6c4.3,6.5,10.1-0.3,16.2,4.8c2.3,1.4,5.6,1.9,8.6,2
		C1092.3,502.7,1093,502.7,1093.6,502.7z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1124.6,504.3c0.8-0.1,1.6-0.1,2.5-0.2c0.6-0.1,1.1-0.1,1.7-0.2c4.4-0.7,8-1.9,5.5-2.4c-2.8,1.2-9.3,0-13,0.5
		c-0.6,0.1-1.1,0.2-1.6,0.4c-0.2,0.1-0.4,0.2-0.5,0.4c-0.2,0.1-0.3,0.3-0.4,0.5c0.1,0.5,0.7,0.7,1.6,0.9c0.2,0,0.4,0.1,0.6,0.1
		c0.4,0,0.8,0.1,1.3,0.1c0.2,0,0.5,0,0.7,0C1123.5,504.4,1124.1,504.3,1124.6,504.3z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1116.3,476c-0.2,0.4-0.3,0.7-0.5,1.1c-0.2,0.4-0.4,0.8-0.6,1.2c-0.5,0.9-0.7,1.5-0.7,2.1c0,1.6,1.8,1.9,3.1,2
		c2.9,0.3,0.3,6,0.7,8.1c0.1,0.5,0.4,0.8,0.9,0.8l0,0l0,0c1.5-0.7,2-1.7,2.1-2.9c0.1-1.4-0.5-2.9-0.9-4.5c-0.1-0.3-0.1-0.5-0.1-0.8
		c-0.1-1.1-0.1-2.1,0.4-3.1c0.5-1.3,3.5-0.4,2.5,1.7c-1,2,1.9,1.8,1.9,4.1c0,0.3,0,0.5,0.1,0.7c0.1,0.2,0.4,0.4,0.7,0.5
		c0.1,0,0.1,0,0.2,0c0.1,0,0.2,0,0.2,0c0.3,0,0.5,0,0.8-0.1c1.9-0.4,4-2.2,1.7-3.8c-1.3-1.4,0.9-3.5-0.8-4.7c-1.7-1.2-2.9-3-1.3-3.3
		s6.4-3.2,5.8-4.4c-1.2-0.9-2.6-0.6-4.1,0.1c-0.3,0.2-0.7,0.3-1,0.5c-0.2,0.1-0.3,0.2-0.5,0.3c-0.3,0.2-0.7,0.4-1,0.6
		c-0.2,0.1-0.3,0.2-0.5,0.3c-0.5,0.3-0.9,0.5-1.4,0.7c-0.1,0.1-0.3,0.1-0.4,0.2c-0.4,0.2-0.8,0.2-1.2,0.2c-1.3,0-2.2-1.1-2.5-4.4
		c0.2-1.4,1.7-2.3,3.6-2.9c3.2-1,7.5-0.9,9.2-0.1c1.7,0.8,3.6-0.3,5-1.7c0.2-0.2,0.4-0.5,0.6-0.7s0.4-0.5,0.5-0.7
		c0.4-0.5,0.6-1,0.5-1.3c0-0.1,0-0.1,0-0.2c0-0.1-0.1-0.2-0.3-0.2c-0.1,0-0.1,0-0.2,0c-0.3,0-0.8,0.3-1.6,0.7
		c-0.5,0.3-1.2,0.8-2,1.5c-0.4,0.2-0.8,0.3-1.2,0.4c-0.3,0.1-0.6,0.1-0.9,0.1s-0.6,0-0.9,0c-0.6-0.1-1.3-0.2-1.9-0.3
		c-0.3-0.1-0.6-0.1-1-0.2c-0.7-0.2-1.3-0.3-2-0.5c-0.2,0-0.3-0.1-0.5-0.1c-0.6-0.1-1.3-0.2-1.9-0.2c-0.2,0-0.3,0-0.5,0
		c-0.3,0-0.6,0-0.9,0.1c-0.6,0.1-1.1,0.3-1.7,0.7c-0.4,0.3-0.8,0.6-1.1,1c-1.4,1.7-2.1,3.1-2.5,4.6
		C1117.8,471.1,1117.5,473.2,1116.3,476z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1143.8,501.8c-0.4,0-0.8,0.1-1.3,0.1c-1.4-0.1-2.1,1.1-3.1,2c-0.2,0.1-0.4,0.3-0.5,0.4l-1.2,0.6
		c-0.1,0.2-0.1,0.4-0.2,0.6h0c-0.1,0.2-0.2,0.3-0.2,0.4c0.1-0.1,0.2-0.2,0.2-0.4l0,0c0.1-0.2,0.1-0.3,0.2-0.6
		c-0.3,0.1-0.7,0.1-1.1,0.2c-0.1,0-0.2,0-0.2,0.1c-0.2,0-0.4,0.1-0.5,0.2c-0.4,0.4-0.8,0.7-1.1,1.1c-0.8,0.8-1.2,1.5-1.4,2
		c-0.3,0.8,0.1,1.3,0.8,1.4c0.2,0,0.4,0,0.6,0s0.4-0.1,0.7-0.1c1.6-0.4,3.6-1.8,4.8-3.9c4.8-1.7,7.1-2.9,7.7-3.7
		c0.1-0.1,0.1-0.1,0.1-0.2C1148.3,501.1,1146.3,501.1,1143.8,501.8z M1137.1,506C1137.1,506,1137.2,506,1137.1,506
		C1137.2,506,1137.1,506,1137.1,506z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1227,495.6c1,0,1.5-0.5,1.3-1.2c0-0.3-0.3-0.8-0.9-1.4c0,0,0,0,0,0c-8.5-3.6-14.1-12-25.7-14
		c-5.4-2.3-8.4-3.7-10.5-4.1c-1.3-0.5-2.3-0.8-2.7-0.7c-1.2,0.9-2.2,1.8-3.2,2.6c-1.4,1.1-3.3,2.6-6.1,4.5c-1.1,0.1-2-0.6-2.8-2.7
		c-0.7-2.6-1-6-1.7-7c0,0,0,0,0,0c-1.4-1.5-3.2-2-4.9-2c-0.7,0-1.4,0.1-2,0.3c-0.4,0.1-0.8,0.2-1.2,0.4c-1.7,0.6-3.1,1.7-4,2.8
		c-0.2,1.1,0.2,1.2,1,1.1c0.4,0.1,0.9,0.1,1.4,0.2c0.8,0.6,1.3,1.3,1.7,1.8c0,0.2,0.1,0.4,0.1,0.7c0.2,0.5,0.9,0.8,1.7,0.8
		c0.8,0.2,2.1,0.1,4.5-0.5c0,0,0,0,0,0c0.8-0.1,1.2,0.6,1,1c0,0.1-0.1,0.1-0.1,0.2c0,0,0,0,0,0c0,0,0,0-0.1,0c0,0-0.1,0-0.1,0.1
		c-0.1,0-0.2,0.1-0.4,0c-0.2,0-0.4-0.1-0.6-0.1c-1.1-0.4-1.6-0.1-2,0.3c-0.9,0.1-1.6,0.2-2.1,0.2c-1.2-0.1-2.2,0.1-1.5,0.7
		c0.2,0,0.5,0.2,0.7,0.4c0.2,0.2,0.4,0.5,0.6,0.8c0.3,0.5,0.6,1.1,0.9,1.7c0.1,0.2,0.2,0.5,0.3,0.7c0,0.9,0.5,1.3,1.1,1.3
		c0.2,0.1,0.5,0.1,0.7-0.1c0.4-0.2,0.8-0.6,0.8-1.2c0,0,0,0,0,0c0-0.4,0.3-0.5,0.8-0.4c0.6,0.3,1.5,1,2.7,1.4c1.6,1,3.3,2.1,3.9,2.1
		c0.4,0,0.8,0.1,1.2,0.1c3.6,1.1,7.6,1.9,10,4.3c0.7,1.1,1.2,2.1,1.6,2.9c0.1,0.4,0.2,0.8,0.2,1.2c0.4,0.4,0.7,0.9,0.9,1.3
		c1.2,3.3-2,1.1-3.9,3.6c-1,0.6-1.1,1.1-0.7,1.5c0,0.1-0.1,0.2-0.1,0.4c1,0.3,2.1,0.3,3.3,0.2c1.4,0,3-0.1,4.3-0.4
		c0.9,0,1.8,0.2,2.7,0.6c0.7,0.7,1.5,1.7,2.7,2.5c0.8,0.7,1.7,1.1,2.8,1.3c1.4,0.4,2.7,0.1,3.8-0.4c1-0.4,1.7-0.9,2-1.6
		c1-1,1.7-2.2,2.4-3.4c3.8-3,8,2.2,11.8,6c1.8,1.9,3.5,3.5,4.8,3.6c0.8-0.2,2.6,0.3,4.4,0.7c1.3,0.4,2.5,0.9,3.7,0.9
		c0.2-0.1,0.3-0.1,0.5-0.2c0.2,0,0.3-0.1,0.4-0.2c0.7-0.6-0.2-1.3-1.4-2c0,0,0,0-0.1-0.1c-0.1-0.1-0.2-0.1-0.3-0.2
		c-0.1-0.1-0.3-0.2-0.4-0.3c-0.9-0.7-1.4-1.3-2-1.9c0-0.1,0-0.2,0-0.3c0.1-0.5-0.6-0.7-1.4-0.9c-0.6-0.4-1.3-0.7-2.4-1c0,0,0,0,0,0
		c-1.6-1.1-1-4.5-2.7-5.4c-0.6-0.4-1-0.7-1.3-1c-0.4-0.5-0.7-1-0.7-1.5C1225.7,496.1,1226.3,495.9,1227,495.6z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1249.6,582.4c-0.1-0.3-0.3-0.6-0.4-0.9c-0.3-0.6-0.5-1.3-0.6-2.3c-0.1-1,0-2.2,0.4-3.7c0.4-2.2,0.2-2-1-0.8
		c-2.8,0.9-2.2-6.6-5.6-6.2c-1.4,0.4-0.5-2.1-1.9-1.9c-0.4,0-0.6,0-0.8-0.2s-0.3-0.4-0.4-0.6c0-0.1-0.1-0.3-0.1-0.4
		c0-0.3-0.1-0.6-0.1-1c0-0.2,0-0.4,0-0.5c0-1.3,0-2.5-1.1-2.7c-0.1,0-0.3,0-0.5,0c-0.6-0.9-1.3-0.2-2.2,0.2c-0.9-0.2-1-0.9-1-1.7
		c0-0.3,0-0.6,0-0.9c0-0.3,0-0.6,0-0.9c0-0.3-0.1-0.6-0.1-0.9c-0.2-0.6-0.5-1.1-1.3-1.4c-1.3-3.5-5.6-7.2-10.2-8.4
		c-1.6-0.4-0.3-2.1-0.9-2.8c-0.7-0.6-0.9-3-0.9-4.9s-2.4-2.6-2.3-4.2c0-0.7-0.1-1.5-0.2-2.3c-0.3-2.2-0.9-4.4-2.3-5.5
		c-0.9-0.7-2-0.9-3.6-0.3c-0.5-0.1-0.8-0.5-1-1.1c-0.1-0.2-0.1-0.4-0.2-0.6c-0.2-1.1-0.2-2.6-0.2-4.1c0-0.3,0-0.6,0-0.9
		c-0.1-1.7-0.4-3.2-1.5-3.8c-0.2-0.4-0.4-0.8-0.6-1.2c-0.2-0.4-0.3-0.8-0.5-1.2c-0.1-0.2-0.2-0.4-0.2-0.6c-0.1-0.2-0.1-0.4-0.2-0.5
		c-0.1-0.3-0.3-0.6-0.4-0.8c0-0.1-0.1-0.1-0.1-0.2c-0.2-0.2-0.3-0.3-0.5-0.3c-0.5,0-1,0.8-1.8,2.8c-0.3,0.7-0.5,1.5-0.7,2.2
		c-0.1,0.3-0.1,0.5-0.2,0.8c-0.1,0.5-0.2,1.1-0.3,1.6c-0.2,1.7-0.3,3.4-0.3,5.2c-0.1,1.5-0.1,3-0.2,4.4c-0.3,4.4-1.2,8.6-4.5,11.5
		c-0.9,0.3-1.7,0.3-2.3,0.1c-0.4-0.1-0.7-0.3-1-0.5c-0.3-0.2-0.5-0.5-0.8-0.8c-0.6-0.7-1-1.6-1.7-2.1c-0.4-0.3-0.9-0.5-1.6-0.6
		c-0.2,0-0.5,0-0.7,0.1c-0.2,0-0.3,0-0.4,0c-1-0.2-1.7-1.8-3.3-3.1c-1.9-1.5-3.3,0.4-3.9-1.4c0-2.6-5.5-3.1-2.2-5
		c1.3-1,1-2.2,0.3-3.4c0-0.3,0-0.5,0-0.7s0.1-0.3,0.3-0.4c0.1-0.1,0.3-0.1,0.5-0.2c0.1,0,0.2,0,0.3-0.1c0.2,0,0.4-0.1,0.6-0.2
		c0.1,0,0.2-0.1,0.3-0.2c0.2-0.1,0.5-0.3,0.7-0.5c0.4-0.5,0.8-1.2,1.1-2.4c0.3-0.2,0.4-0.4,0.5-0.6c0.3-0.8-0.6-1.3-1.5-1.3
		c-0.7,0.1-1.3,0.5-1.3,1.5c0,0-0.1,0-0.1,0c-0.1,0-0.1-0.1-0.2-0.1c0,0-0.1-0.1-0.1-0.1c-0.1-0.1-0.1-0.2-0.2-0.4s-0.1-0.3-0.2-0.4
		c-0.1-0.1-0.1-0.3-0.2-0.4c-0.2-0.4-0.4-0.7-0.5-0.7c-0.4,0.1-2.1,2.4-2.9,1.3c-0.3-0.2-0.7-0.4-1.1-0.6c-0.4-0.2-0.9-0.4-1.4-0.6
		c-0.2-0.1-0.5-0.2-0.8-0.3c-0.5-0.2-1.1-0.3-1.6-0.5c-1.1-0.4-2.3-0.7-3.3-1.1c-1.3-0.5-2.4-1-3-1.7v0v0c-0.1,0.5,0,0.9,0.2,1.3
		c0.6,1.1,1.8,2,1.1,2.6c-0.1,0.1-0.2,0.2-0.4,0.2c-0.4,0.2-1.1,0.3-2.1,0.4c-0.8,0.1-1.9,0.1-3.2,0.1c-0.2-0.1-0.4-0.1-0.5-0.1
		c-0.7,0,0.5,1.3-0.7,1.3c-1.4-0.1-1.6,2.4-2.7,3.7c-0.6,2.9-4,3.3-1.4,6.4c1.2,2.5-0.6,1.2-2.2,0.2c-0.2-0.1-0.4-0.2-0.5-0.3
		c-0.2-0.1-0.3-0.2-0.5-0.2c-0.2-0.1-0.5-0.2-0.6-0.2c-0.4,0-0.7,0.3-0.6,1.1c0,0.4-0.1,0.6-0.2,0.7c-0.1,0.1-0.3,0.1-0.5,0
		c-0.2-0.1-0.4-0.3-0.5-0.6c-0.1-0.3-0.2-0.6-0.1-1.1c0.3-1.7-1.8-0.9-2.1-1.9c-0.1-0.3-0.3-0.6-0.5-0.9c-0.4-0.5-0.8-0.9-1.3-1.1
		c-1.2-0.6-2.6-0.3-3.7,0.8c-1.1,0-1.4,0.6-1.2,1.9c0.2,1.3-1.7-0.1-3,0.1c-1.3,0.2,0,1.7-0.7,1.8c-0.7,0.1-0.7,1.3-1.8,1.3
		c-0.6,0.3-0.9,0.9-0.9,1.4c0,0.4,0,0.9,0.1,1.3c0.1,1,0.2,1.7-1.2,1.5c-0.4-0.1-0.9-0.2-1.6-0.5c-1.1,0.4,0.2,1.5,0.6,3
		c0,0.2,0,0.4,0,0.6c-0.1,1-0.6,1.5-1.1,1.4c-0.5,0-1.1-0.5-1.4-1.4c-0.1-0.4-0.2-1-0.2-1.6c0-1.3-1.5-0.1-3,1.6
		c-1.5,1.7,0.5,4.3-0.2,4.7c-1.8,2.1-3.2,3.5-4.6,4.6s-2.7,1.8-4.3,2.3c-0.3,0.1-0.6,0.2-1,0.3c-0.7,0.2-1.4,0.4-2.2,0.6
		c-1.2,0.3-2.5,0.6-4,1c-1.2,1-3.4,2.9-3.7,1.7c-0.5-0.6-1.2-0.7-2-0.4c-0.4,0.1-0.8,0.4-1.2,0.6c-0.2,0.1-0.4,0.3-0.6,0.4
		c-2,1.4-4.1,3.5-5.5,3.3c-0.2,0.1-0.4,0.2-0.5,0.3c-0.1,0-0.1,0.1-0.2,0.1c-0.1,0.1-0.2,0.2-0.3,0.3c-0.2,0.3-0.4,0.6-0.6,0.9
		c-0.1,0.1-0.1,0.2-0.2,0.3c0,0.1-0.1,0.1-0.1,0.2c-0.2,0.3-0.3,0.5-0.4,0.6c0,0-0.1,0-0.1,0c-0.1-0.1-0.1-0.4-0.1-0.8
		c0-0.4,0-0.9,0-1.5c0-0.6-1.4,1.1-2,3c-0.6,1.9,1.4,2.6,0.2,4.6c-4.3,3.5,2.3,9,1.5,11.8c-0.6,1.3-1.9-2.5-2.3-2.1
		c-0.4,0.4,1.6,2.1,1.1,2.9c-1.2-1.2-1.9-1.7-2.3-1.8c-0.2,0-0.3,0.1-0.3,0.4c0,0.3,0.2,0.7,0.4,1.2c0.1,0.3,0.3,0.7,0.5,1.2
		c0.1,0.2,0.2,0.4,0.3,0.7c0.2,0.5,0.5,1,0.7,1.5c2.1,4.1,5.2,9.7,5,11.5c-0.2,3.2,2.9,8.4,3.4,9.2c-0.5,1-0.1,9.3-3.1,8.7
		c-1.5,0-0.6,2.5,1,2.7c0.3,0.3,0.5,0.5,0.8,0.8c0.5,0.5,1.1,0.9,1.7,1.3c0.8,0.5,1.7,0.9,2.6,1.2c0.6,0.2,1.2,0.3,1.8,0.3
		c0.3,0,0.6,0,0.9,0c0.3,0,0.6,0,0.9-0.1c0.3,0,0.6-0.1,0.9-0.2c1.2-0.3,2.5-0.9,3.7-1.7c0.3-0.2,0.6-0.4,0.9-0.6
		c4.1-2.3,4.9-3.7,12.7-2.6c2.7,0,4.5-0.6,6-1.6c0.3-0.2,0.6-0.4,0.9-0.7s0.6-0.5,0.9-0.7c2.3-2.1,4.1-4.5,8.6-4.5
		c0.5,0,1.1,0,1.7,0.1l0,0l0,0c1.6-0.2,3-0.5,4.4-0.9c2.4-0.7,4.7-1.6,7-2.2c0.7-0.2,1.3-0.3,2-0.4c1-0.2,2.1-0.2,3.3-0.2
		c2-0.5,2.4,2.7,4.7,2.4c2.3-0.3,3.3,0.7,4.5,0.6c0.2,0.1,0.4,0.2,0.6,0.2c0.6,0.3,1.4,0.6,1.9,1c0.3,0.3,0.6,0.6,0.6,1
		c0,0.3-0.1,0.6-0.3,1c-0.1,0.1-0.2,0.3-0.3,0.4c0.1,0.3,0.2,0.6,0.4,0.8c0.6,0.8,1.7,1.3,2.6,2.3c0.3,0.3,0.5,0.6,0.7,0.9
		c0.5,0.9,0.8,2.1,0.7,3.9c0.3,0.7,0.6,1,0.9,1.1c0.1,0,0.2,0,0.3-0.1c0.5-0.2,1.1-0.9,1.9-2.2c1.7-2.5,3.6-0.9,3.8-3
		c0.3-2.1,2.1-4.7,3.3-3.6c1.2,1.1-0.9,1.6-1.6,5.7c-0.8,4.1-2.1,2-2.4,3.1c0.3,0.3,0.5,0.5,0.7,0.7c0.2,0.2,0.4,0.3,0.6,0.4
		c0.4,0.2,0.7,0.2,0.9,0.1c0.1-0.1,0.2-0.1,0.3-0.3c0.4-0.4,0.6-1.1,0.8-1.7c0.3-0.9,0.6-1.8,1.2-1.7c0.3,0,0.6,0.3,1.1,1
		c0.4,0.5,0.5,1,0.5,1.4c0,1.3-1.3,2.4-1,3.1c1.8-0.2,3.1,0,3.9,0.4c1.4,0.7,1.7,1.9,1.8,3.4c0,0.6,0,1.2,0.1,1.8
		c0,0.3,0,0.6,0.1,0.9c0.2,1.6,0.6,3,2.3,4c0.7,0.6,1.5,1,2.3,1.3c1.3,0.5,2.6,0.8,3.8,1c0.3,0.1,0.5,0.1,0.8,0.2
		c0.5,0.1,1,0.2,1.5,0.4c0.7,0.2,1.4,0.5,2.1,0.9c0.4,0.3,0.9,0.6,1.3,0.9c0.2,0.2,0.5,0.3,0.8,0.3c0.4,0,0.9-0.1,1.4-0.4
		c1.1-0.6,2.3-1.7,2.6-2.9c0.6-2.1,2.8-0.1,1.6,0.6c-1.2,0.7,0.6,2.4,0.7,1.3c0.1-1.2,0.6-2.1,1-0.8c0.1,0.1,0.3,0.3,0.5,0.5
		c0.5,0.6,1.2,1.4,1.9,2c0.2,0.2,0.4,0.4,0.6,0.6c0.4,0.3,0.7,0.5,0.8,0.5c0.1,0,0.1-0.1,0.1-0.2c0.1-0.5,0.4-1,0.8-1.4
		c2.3-3,8.6-5.2,12-5.2c0.3,0,0.5,0,0.7,0.1c0.1-0.7,0.3-1.7,0.5-2.9c0.2-0.9,0.4-1.9,0.6-2.9c0.2-0.7,0.3-1.4,0.5-2.1
		c0.2-0.7,0.4-1.4,0.6-2.1c0.1-0.3,0.2-0.7,0.3-1c0.2-0.6,0.5-1.3,0.7-1.8s0.5-1.1,0.8-1.5c0.3-3.2,1.5-5.1,2.8-6.7
		c0.3-0.4,0.7-0.8,1-1.1c0.2-0.2,0.3-0.4,0.5-0.6c0.8-0.9,1.6-1.9,2.1-3.1c0.6-1.4,1.2-3,1.6-4.5c1.3-4.7,1.8-9.7,2-13.9
		c0.2-0.8,0.1-1.4-0.1-2C1249.9,582.9,1249.8,582.7,1249.6,582.4z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1346.4,629.3c-0.1,0-0.2,0-0.2,0c-0.2,0-0.3,0.1-0.5,0.1s-0.4,0.2-0.6,0.3c-0.3,0.2-0.7,0.5-1.1,0.9
		c-0.3,0.3-0.8,0.4-1.5,0.3c-1,0-2.2-0.4-3.1-0.9c-0.9-0.5-1.6-1.3-1.5-2.2c0.1-0.6,0-1.1-0.1-1.5c-0.1-0.3-0.2-0.6-0.4-0.8
		c-0.4-0.7-1-1-1.3-0.7c-0.2,0.1-0.2,0.4-0.2,0.9c0,0.3,0.1,0.7,0.2,1.2c-2-0.2-2.8-2.4-3.6-4.8c-0.1-0.3-0.2-0.7-0.3-1
		c-0.2-0.5-0.4-1-0.6-1.5c-0.3-0.6-0.6-1.2-0.9-1.8c-0.1-0.1-0.2-0.3-0.3-0.4c-0.8-1-1.9-1.6-3.5-1.5l0,0l0,0
		c-0.3,0-0.4,0.1-0.5,0.2c0,0.1,0,0.1-0.1,0.2c0,0.1,0,0.3,0,0.5c0.5,2.1,4.8,7.4,6.3,9c0.2,0.2,0.3,0.4,0.4,0.4
		c0.1,0,0.1,0.1,0.2,0.1c0.7,0.7-0.8,3.5-0.8,5.9c-2.9,5.6-6.3,3.3,2,8.5c0.1,0.1,0.1,0.2,0.2,0.3c0.1,0.2,0.2,0.4,0.2,0.6
		c0.3,1.6-0.9,3.5-1.3,4.8c-0.1,0.2-0.1,0.3-0.1,0.5c0,0.6,0.2,1,1.3,1c0.5,0,1.2,0,2.1-0.2c2.7-2,5-7.4,5.5-10.5
		c0.2-0.2,0.4-0.3,0.6-0.4c0.7-0.4,1.1-0.6,1.4-0.6c0.2,0,0.3,0,0.4,0c0,0,0.1,0,0.1,0c0.1,0,0.2-0.1,0.4-0.4c0.3-0.3,0.6-0.9,1.3-2
		c0.3-0.1,0.5-0.3,0.6-0.6c0.2-0.3,0.4-0.8,0.5-1.2C1347.8,631,1347.5,629.4,1346.4,629.3z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1142.9,427.7c-0.1,0-0.1-0.1-0.2-0.1c-0.4,0-0.8,0.5-1.1,1.7c0.4,0.7,0.2,0.9-0.2,1c-0.6,0.2-1.9,0-2,1.2
		c-0.2,1.8-2.4,0.4-2.3,1.8c0.1,1.4-2.3,0-3.4-0.8c-0.8-0.2-3.4,1.5-5,3.3c-1.3,1.4-2,2.9-0.9,3.6l0,0l0,0c3.1-2,4.9-2.8,5.8-2.9
		c0.4,0,0.6,0.1,0.8,0.2c0.3,0.3,0.5,0.8,0.6,1.4c0.1,0.5,0.2,1.1,0.4,1.8c0,0.2,0.1,0.3,0.1,0.5c0.2,0.5,0.4,1,0.7,1.5
		c0.1,0.2,0.2,0.3,0.3,0.5c0.6,0.8,1.5,1.6,2.8,2.3c0.5,0.3,1.1,0.5,1.8,0.7c1.2,0-0.2-3.8-0.1-5.1c0.1-1.3,2.7-0.1,4.1-1.8
		c1.1-0.6-0.3-7.9-1.8-10.2C1143.4,428,1143.1,427.8,1142.9,427.7z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1133.4,422.9c-0.1-0.2-0.1-0.3-0.2-0.5c-0.4-0.7-1-1.3-1.6-1.8c-0.2-0.2-0.5-0.4-0.8-0.5
		c-0.8-0.4-1.6-0.6-2.3-0.6c-0.8,0-1.5,0.3-1.8,1c-0.1,0.2-0.1,0.4-0.1,0.6c0,0.2,0,0.5,0.1,0.8c0.1,0.3,0.2,0.6,0.3,1
		c0.2,0.4,0.4,0.7,0.6,1.1c0.1,0.2,0.3,0.4,0.4,0.6c0.6-0.1,1.9,0,2.1-1.2c2.3-0.3-2.3,7.1,0.9,7.6c1.6,0.4,4.6-5.4,4.1-6.4
		c-0.4-1.1-1.8,0.7-1.5-0.7C1133.7,423.5,1133.6,423.2,1133.4,422.9z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1064.5,490.9c0.4-0.3,0.7-0.9,0.8-2.1c0-0.3,0-0.6,0.1-0.9c0-0.7,0-1.5,0-2.4c0.4-2.2-0.3-5.5,2.2-5.1
		c1.1-0.3,0.8-1.6,0-2.9s-2.1-2.6-3.1-2.8l0,0l0,0c-0.8,0.2-1.2,0.4-1.4,0.6c-0.1,0.2-0.1,0.3,0,0.5c0.1,0.1,0.1,0.1,0.2,0.2
		c0,0,0.1,0.1,0.1,0.1c0.2,0.2,0.6,0.3,0.9,0.5c0.1,0.1,0.2,0.2,0.4,0.2c0.4,0.2,0.7,0.5,0.8,0.8c0.1,0.1,0.1,0.3,0.1,0.5
		c0,0.1-0.1,0.2-0.1,0.3c-0.1,0.2-0.1,0.2-0.2,0.2c-0.1,0-0.2-0.1-0.4-0.2c-0.4-0.3-0.9-0.8-1.8-0.7c-1.6,0.1-0.6-2-2.4-2.2
		c-1.8-0.1-1.5-3.6-3.4-3.6c-1.9,0-2.1-1.7-0.8-2.9c1.2-1.2-1.9-2.3-1.9-3.7c0-1.4-2.1-0.6-2.1-1.8c-0.9-1.7-2.3-2.5-3.9-3.2
		c-1.8-0.7-3.8-1.2-5.2-2.5c-0.6-0.6-1.2-1.3-1.5-2.3c-1.5-0.9-2.8-2.4-4.1-3.9c-0.9-1-1.8-2-2.8-2.9c-1.5-1.3-3.1-2.1-5.3-2.1
		c-0.8,0-1.4-0.2-2-0.4c-0.3-0.1-0.5-0.3-0.8-0.4c-0.2-0.2-0.4-0.3-0.6-0.5c-0.8-0.6-1.3-1-1.6-0.2c0.1,2.5,2.4,5.5,5,8.2
		c2,2.1,4.2,4,5.5,5.3c0.9,0.1,1.4,1.2,1.9,2.6c0.8,2.3,1.3,5.3,2.3,5.5c0.6,0.3,1.1,0.8,1.6,1.3c1.2,1.3,2.3,3.3,3.4,5.3
		c0.8,1.7,1.7,3.4,2.6,5c0.7,1.2,1.4,2.3,2.2,3.2c0.3,0.3,0.6,0.6,0.8,0.8c0.3,0.3,0.6,0.5,0.9,0.7c0.6,1.1,2.2,2.5,3.7,3.7
		c1.5,1.2,2.9,2.2,3.3,2.7c0.7,1,1.4,3.1,2,2c0.1,0,0.3-0.1,0.4-0.1c0.3,0,0.5-0.1,0.8-0.1c0.1,0,0.2,0,0.4,0c0.2,0,0.5,0,0.7,0
		c0.2,0,0.4,0,0.6,0c0.2,0,0.4,0,0.5,0c0.1,0,0.2,0,0.2,0C1063.8,491.1,1064.2,491,1064.5,490.9z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1180.3,316.2c0.1-1.1-2.1-0.5-4.7,0.6c-2.6,1.1-4.2-1.2-5.7,0.4c-1.5,1.6-4,4.3-5.9,4.7
		c-6,8,19.3-8.2,15.5,2.4c0.1,1.1,0.9,1.4,1.9,1.2c1-0.2,2.2-0.9,3.2-1.7c0.2-0.2,0.3-0.3,0.5-0.5s0.3-0.3,0.4-0.5
		c0.1-0.1,0.1-0.2,0.1-0.2c0.2-0.3,0.3-0.6,0.2-0.8c0-0.2-0.1-0.3-0.3-0.5c-1.3-1.1,0.2-2,1.1-0.6c0.7,0.7,1.3,1,1.8,1.1
		c0.2,0,0.3,0,0.5,0s0.3,0,0.5-0.1c0.2-0.1,0.4-0.2,0.6-0.3c0.3-0.2,0.5-0.4,0.8-0.6c0.6-0.5,1.2-0.9,1.9-1c0.5,0,1,0.2,1.7,0.6l0,0
		l0,0c0.5,0,1-0.6,1.3-1.2c0.6-1,0.9-2.1,1-1.2c3.7,4.7,6.5-10.3,5.6-12.5c-0.9-1.8,2.1-3,3.5-4.4c1.5-1.4-1.7-8.6-1.8-10.7
		c-0.1-2.1-3.8,0.8-4.9,1.4c-0.4,0.5-0.7,1-0.9,1.4s-0.3,0.8-0.4,1.1c-0.2,1.7,1.1,2.6-0.2,5.9c-0.3,0.7-0.6,1.4-1.1,2.3
		c0,0.1,0,0.2,0,0.2c0,0.2-0.1,0.4-0.2,0.6c0,0.1-0.1,0.2-0.1,0.3c-0.1,0.2-0.2,0.5-0.4,0.7c-0.2,0.3-0.3,0.5-0.5,0.8
		c-0.2,0.3-0.4,0.5-0.6,0.8c-1.2,1.5-2.9,2.9-4.2,3.5c-0.4,0.2-0.7,0.3-1,0.3c-0.8,0.1-1.4-0.4-1.4-1.8c0.5-1.1-3,0.6-2.8,3.2
		c0.1,2.6-3.2,2.9-2.4,4.1C1183.5,316.6,1180.2,317.3,1180.3,316.2z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1204.5,277c-0.1,0.5-0.2,1-0.4,1.4c-0.3,0.9-0.7,1.6-1.8,1.9c-0.3,0.1-0.6,0.1-1,0.2c-0.2,0-0.4,0-0.7,0
		c-1.4,0.2,0.1,1.7-1.5,2.4c-1.6,0.7-1.5,1.9-0.7,2.4c0.6,0.4,0.3,1.6,0.2,2.5c-0.2,1.1-0.2,1.8,1.7-0.2c0.9-1.1,1.9,0.5,2.1-0.5
		c-0.2-0.4-0.4-0.8-0.7-1.1c-0.3-0.4-0.7-0.7-1-1c-0.1-0.1-0.2-0.2-0.3-0.3s-0.2-0.2-0.2-0.2c0,0-0.1-0.1-0.1-0.1
		c0-0.1-0.1-0.1-0.1-0.2c0,0,0-0.1,0-0.1c0,0,0-0.1,0.1-0.1c0.2-0.2,1.1-0.3,3.2-0.3c1.4-0.7,4.1-0.1,5.8,1.4
		c0.5,0.4,0.9,0.6,1.2,0.6c0.1,0,0.3,0,0.4-0.1c0.4-0.2,0.7-0.7,1.1-1.3c0.2-0.3,0.3-0.5,0.5-0.8s0.4-0.5,0.6-0.8
		c0.4-0.4,0.8-0.8,1.3-1.1c0.1-0.1,0.2-0.1,0.3-0.1c0.4-0.1,0.7-0.2,1.2-0.2s0.9,0,1.5,0.1l0,0l0,0c1.3,0,2-0.3,2.3-0.7
		s0.1-0.9-0.3-1.2c-0.8-0.6,4.3-4.1,4.2-5c-0.1-1-2.7,2.1-3.5,1.1c-0.7,0.2-1.3,0.3-1.9,0.4s-1.1,0.1-1.7,0.1c-0.5,0-1-0.1-1.5-0.2
		c-2.5-0.5-4.6-2.4-7.2-5.8c-1.4-2.1-3.7-0.4-2.5,1.2c1.1,1.6-0.2,2-0.2,4.2C1204.7,276,1204.6,276.5,1204.5,277z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1175.1,326.9c0.4,0,0.7,0,1-0.1c1.1-0.4,1.5-1.8,1.4-3c0-0.4-0.1-0.8-0.3-1.1c-0.1-0.2-0.2-0.3-0.3-0.4
		c-0.6-0.7-1.6-0.8-3,0.6c-2.2,1.8-7.6,0.7-4.2,6.4C1172.1,330.5,1172.1,325.1,1175.1,326.9z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1121.9,375.5c0.2,0.1,0.4,0.2,0.5,0.2c0.1,0,0.2,0,0.3-0.1c0.9-0.4,2.1-2.2,3.1-4.4c1.3-2.9,2.3-6.3,2-7.9
		c-0.2-0.3-0.5-0.4-0.9-0.4c-0.1,0-0.3,0-0.4,0c-1.8,0.3-4.6,2.4-5.5,6c-0.1,2,0,3.5,0.1,4.6c0.1,0.8,0.3,1.3,0.5,1.7
		C1121.8,375.4,1121.9,375.4,1121.9,375.5z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1137.9,427.3c-0.9-1.2-3.2,1-2.8,1.9C1135.5,430.5,1138.8,428.5,1137.9,427.3z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1121.8,413c0.3,1.7,1.1,5.1,3.7,4.3C1127.9,416.5,1121.5,411.8,1121.8,413z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1119.6,400.8c-0.3,0.9-0.3,1.6-0.3,2.2c0,0.4,0.1,0.8,0.3,1.1c0.1,0.3,0.3,0.6,0.5,0.9
		c0.2,0.3,0.4,0.5,0.6,0.8c0.1,0.1,0.2,0.3,0.3,0.4c0.3,0.4,0.5,0.8,0.7,1.4c0.1,0.5,0.2,1.1,0.1,1.8c-0.6,1.9,1.7,2.1,3.9,1.3
		c2.2-0.8,1.4,1.6,3.5,1.2c1.2-0.1,2.1,0.1,2.7,0.5c1,0.6,1.4,1.7,1.6,2.9c0.2,1.1,0.2,2.3,0.5,3c0.2,0.4,0.5,0.7,1,0.8l0,0l0,0
		c0.3-0.2,0.6-0.3,0.8-0.3c0.1,0,0.1,0,0.2,0c0.1,0,0.3,0.1,0.4,0.2c0.5,0.3,0.8,1.1,0.2,2c-1,1.6,1.6,3.1,1.9,5
		c1.6,3,0.4-3.2,3-3.5c1.5-1.1-0.1-2.9-1-4.9c-0.9-2-4.3-1.3-4.3-3.2c-1.7-3.2-3.6-6.3-8-4.5c-2,1.2-0.6-2.1-2-3.7
		c-1.4-1.6,1-3.8,2.6-5.7c1.1-1.5,0-8.7-0.9-8c-0.8-0.3-1.5-0.4-2.1-0.6c-0.3-0.1-0.6-0.1-0.9-0.1c-0.3,0-0.5,0-0.7,0
		c-1.4,0-2.1,0.5-2.5,1.3c-0.2,0.4-0.3,0.9-0.3,1.4c0,0.5,0,1,0.1,1.6c0.1,0.7,0.2,1.5,0.3,2.2c0.1,0.7,0.2,1.3,0.1,1.8
		C1121.7,400.9,1121.2,401.4,1119.6,400.8z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1248.5,487.3c2.2-0.6,0.6-2.9-1.3-4.1c-1.8-1.2-3.4-2-1.1,0.2C1248.4,485.7,1247.1,487.7,1248.5,487.3z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1243.3,487.8c-0.3,4-12.3,2.2-12.9,3.2c0.3,1.8,2.4,2.6,4.8,2.6c0.2,0,0.4,0,0.6,0c0.6,0,1.3-0.1,1.9-0.1
		c0.8-0.1,1.7-0.3,2.5-0.5c0.4-0.1,0.8-0.2,1.1-0.4c0.5-0.2,1.1-0.4,1.5-0.7c1-0.6,1.8-1.3,1.9-2.1c0.1-0.2,0.3-0.3,0.4-0.5
		C1246.7,486.8,1245.3,483.8,1243.3,487.8z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1281.7,510.6c0.2,0.4,2.4,2.6,3.5,1.8C1286.4,511.6,1281.4,509.8,1281.7,510.6z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1275.4,502.8c1-0.6-5.3-4.2-5.4-3C1269.9,500.8,1274.4,503.4,1275.4,502.8z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1262.5,495.6c-0.1,0.4,2.7,3.2,3.3,2.8C1266.4,498,1262.7,494.8,1262.5,495.6z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1278.7,502.6c-0.9,2,2.6,5.4,3.1,5.5C1282.3,508.2,1279.4,501.1,1278.7,502.6z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1274.7,506.7c0.3,1.8,2.9,2.9,3.9,2.2C1279.7,508.3,1274.3,504.8,1274.7,506.7z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1255.2,490.6c-0.2,1.1,3.4,6.8,4.8,5.9C1262.5,494.8,1255.4,489.5,1255.2,490.6z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1159.3,516.3c0.9,0.7,5.1-0.2,5-1.3C1164.1,514,1158,515.2,1159.3,516.3z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1222.3,646c-2.3,0.3-5.8-2.2-6.6-2c-0.9,0.3,3,6.4,1.8,8.1c-0.2,0.6,0,1.3,0.4,2.1c0.2,0.3,0.4,0.7,0.6,1
		c0.2,0.2,0.3,0.4,0.5,0.6c1.1,1.3,2.5,2.3,3.8,2.3c0.8,0,1.6-0.3,2-1.4c0.1,0,0.3,0,0.3,0c0.1,0,0.2-0.1,0.2-0.1
		c0.1-0.1,0.2-0.3,0.2-0.5c0.1-0.4,0-1,0.1-1.4c0.2-0.9,1.3,1,2.2,0.4c0.9-0.5-0.2-4.4,1.1-4.4s0.3-4.6-0.2-6.2
		C1228.3,643,1224.6,645.7,1222.3,646z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1183.5,621.1c0.9,0.9,4.1,0.6,4.6-0.6C1188.7,619.2,1182.7,620.3,1183.5,621.1z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1326.9,646.1c-4.4-2.3-2.7-3.4-5.7,1.9c-1.8,1.6-1,2.1-3.2,5.6c-2,2.2-4.6,3.8-7.2,5.3
		c-0.5,0.3-0.9,0.6-1.4,0.8c-0.5,0.3-0.9,0.6-1.4,0.8s-0.9,0.6-1.3,0.8c-0.4,0.3-0.8,0.6-1.2,0.9c-2.5,2-4.3,4.3-4.1,7.8
		c1.3,0.8,2.4,1.4,3.5,1.9c4.9,2.2,8.1,1.5,10.6-1.8c1.4-1.8,2.6-4.5,3.8-7.8c0.2-1.8,1.7-2.3,3.2-2.5c1-0.2,2-0.2,2.5-0.6
		c0.1-0.1,0.2-0.2,0.2-0.3c0.1-0.2,0.1-0.5,0-0.9c-0.1-0.2-0.2-0.4-0.3-0.7c-0.2-1.5,2.6-1.8,2.6-3.8c1.5-3.4,4.4-4.6,2.5-8.6
		C1328.8,644.3,1328.2,645.5,1326.9,646.1z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1305.2,676.1c0.6,0.2,2.6-0.4,1.5-1.8C1305.5,672.8,1303.8,675.6,1305.2,676.1z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1349,538.2c1,0.4,4-0.7,4-2.2C1353,534.4,1347.6,537.6,1349,538.2z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1343.7,542.9c1.5,1.4,4.4,1.3,4.4-1.3C1348.2,539.1,1342.4,541.6,1343.7,542.9z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1211.1,227c-2.3-1.8-1.4-5.5-2.5-4.8c-1.1,0.7,0,3.9-1.8,4.2c-0.8,0.7-1.1,2.7-1,4.8c0.1,2.8,0.7,6,1.5,7.6
		c0.5,1.2,0,5.4-0.3,9.1c-0.3,2.9-0.5,5.6-0.1,6.4c0.9,1.9-1.5,10.4-0.7,11.3c0.7,0.5,1-0.4,1.4-1.3c0.1-0.1,0.1-0.3,0.2-0.4
		c0-0.1,0.1-0.1,0.1-0.2c0.1-0.2,0.2-0.4,0.3-0.5c0,0,0.1-0.1,0.1-0.1c0.1-0.1,0.3-0.2,0.5-0.2c0.1,0,0.3,0,0.4,0.1
		c0.1,0,0.2,0.1,0.2,0.1c0.3,0.2,0.6,0.4,0.9,0.8c0.4,0.5,1,1.2,1.6,2.1c0.2-0.2,0.3-0.3,0.4-0.4c0.2-0.3,0.3-0.6,0.3-0.9
		c0-0.3-0.1-0.6-0.3-0.9c-0.5-0.9-1.6-1.8-2.4-2.9c-0.7-0.9-1.3-2-1.3-3.3c0.1-2,0.3-3.6,0.6-4.8c0.2-1,0.5-1.7,0.8-2.3
		c0.1-0.1,0.1-0.2,0.2-0.3c0.4-0.5,0.8-0.8,1.2-0.9c0.1,0,0.1,0,0.2,0s0.1,0,0.2,0c0.3,0,0.6,0.1,0.9,0.2s0.6,0.3,0.8,0.4
		c0.5,0.3,1,0.7,1.3,0.8c0.1,0.1,0.2,0.1,0.3,0.1c0.1,0,0.1,0,0.2-0.1c0.5-0.5-0.3-3.8-3.8-15.2
		C1210.3,232.2,1213.4,228.8,1211.1,227z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1303.1,560.7c1.4-1-11.4-9.6-11.2-8.1C1292.1,554.1,1301.7,561.7,1303.1,560.7z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1036.9,472.2c-0.2,0.6,1,3,1.8,2.3C1039.5,473.8,1037.6,470.3,1036.9,472.2z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1074.2,479.5c0-1.1-2.9-0.6-2.1,1C1073.1,482,1074.2,480.6,1074.2,479.5z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1115.7,507.1c1.6,0.7,6.3,4.7,7.1,2C1122.8,507.1,1115,505.9,1115.7,507.1z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1110,501.2c-0.4,1.1-2.4,1.3-3.5,0.5c-1.7-0.1-2.2,0.7-2,1.5c0.2,0.7,0.9,1.5,1.9,1.9s2.4,0.4,3.8-0.3
		C1119.2,504,1115,500.1,1110,501.2z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1151.1,481.1c0.5,1.4,2.8,0.8,5.2,0.8c0.6,0.3,1.1,0.5,1.7,0.7c0.3,0.1,0.5,0.2,0.8,0.3
		c0.3,0.1,0.5,0.1,0.7,0.2c0.2,0.1,0.5,0.1,0.7,0.1c0.8,0.1,1.4,0.1,1.7-0.1c0.1-0.1,0.1-0.1,0.1-0.2s0-0.2-0.1-0.3
		c-0.1-0.2-0.2-0.4-0.4-0.6c-0.1-0.1-0.2-0.2-0.2-0.3c-0.2-0.2-0.4-0.4-0.6-0.6c-0.5-0.4-1.1-0.9-1.9-1.4
		C1155.7,478.7,1150.6,479.5,1151.1,481.1z"
          />
          <MapPath
            initial={{ pathLength: 0 }}
            animate={mapPathAnimation}
            d="M1143.2,481.3c0.5,1.9,3.8,3,4.7,1.3C1148.8,480.9,1142.7,479.1,1143.2,481.3z"
          />
        </g>
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M574.9,120.2c-4.6,2.8-10.5-2.7-15.4-0.7c-2.6,1.6-1-2.3,1.1-2.6c2.2-0.4,1.3-1.4,0.7-3.8
	c2-4.7,8.9,8.6,13.9,4.9c3.1-1,0.1-3.6,1.4-5.4c1.3-1.8-9.5-7.4-10.1-9.3c-0.6-1.9,2.7-0.6,5.7,0.7c7,0,3.7-9.2-5.6-4.3
	c-2.9,1.7-5.6-2.2-1.7-2.6c3.9-0.5,0.3-2.2,1.5-3.3c3.4,0.5,10.7,4.8,15.1,1c2.2-1.8-3.9-3.5-5.1-5c-1.3-1.6,6.3-1,8.6-0.8
	c7.9-3.3-8-2.5-5.9-7.3c1.4-2.1,3.9,0.4,6.7-1.2c2.9-1.6,0.2-7.4-1.8-7.4c-4.4,0.1-8.8-2-9.7-4.9c1.1-1.1,2.6,1.3,5.3-0.7
	c2.6-2,8.1,0.5,10.5,0c2.4-0.5-1-5.1-2.4-4.3c-1.4,0.8-6.1,1.1-6.4-1.4c-0.3-2.5,4.5,0,5.3-1.2c0.8-1.2-6.6-4.3-7.8-1.8
	c-1.1,2.5-5.3,1.7-3.1,0.2c2.2-1.4,2.6-5.5,2.4-7.5c-0.2-2,7.8-2.1,6.9-5.5c-0.9-3.3,3.8-4.1,6.4-4.1c2.6,0-0.1-3.6-2.6-3.2
	c-7.1,4.4-9.5,0.5-1.1-1.2c2.3,0,7.8-0.2,9.7-1.4c3.8-2.8-18.8-1.1-5.3-3.1c12-2.3,36.6-9.3,8.9-12.2c-12.4-0.2-2.5,6.9-15.8,4.3
	c-2.3,1.1-1.1-2.5-3.4-1.3c-6.9,4.9-22.1,16.5-7.7,1.3c2.7-2.9,1.3-6.2-2.4-5.9c-4.1,1.8-13.5,8.5-17.3,7.3c-0.5-1.6,8.1-4.2,8-5.3
	c-0.1-1.1-10.9-1.2-15.7-0.5c-18.9,5.1-14.5-0.1,1.5-1.9c9.1,0.1,22.2-1.2,28.9-4.4c1-3.6-12.5-1.8-12.5-4.7
	c-2.7-3.8-20.3-5.6-27.3-5.7c-8.7,1.2-21.1-1.5-25.4,3.3c2.1,2.4-3.1,3.3-2.7,1.6c0.3-1.8-4.5-2.6-5.9-1.3c-1.7,1.5-9.4-1.6-10.5,0
	c-2,3.5-21.6-2.3-8.9,5.3c-14.1,1.8-2.5,3.5,3,8.6c2.6,2.7-2.1,1.1-5-1c-5.7-2.4-28.7-12.5-14.3-0.1c-4.7,4.5-16.4-11.6-12.8,1.3
	c0,3-4.5,4.8-3.2,2.7c1.3-2,0.3-7.3-2.1-8.2c-1.7-0.1-21,2.1-16.5,4.5c2.7,1.7-0.1,2.6-2.5,0.8c-2.4-1.8-10.3,0-8.7,1.2
	c4.4,6.8,0.3,6.9-6.3,4.9c-7.3,2.6-30,11.7-8,11.5c3.5,10.1-12.9,7.2-15.3,9.7c-4.7,3.9-27.5,3.4-6.1,10.9
	c2.8,5.1,15.9-3.5,16.6,3.2c-0.1,2.2-8-0.8-10.4,0.7c-2.4,1.6-9.8,0.2-9.5,1.9c0.3,1.7,4,1.3,7,1.9c3,0.6,0.2,1.9,0.1,3.2
	c-1,1.1,13.3,6.4,10.9,3.3c-1.6-1.8,3.1-1.6,3.9-0.5c0.8,1.1,3-1.6,5.1-1c3.6-1.8,21.9,2,23.3,6.7c-0.4,4.3,10.9,6.3,8.9,11.9
	c-0.1,2,3.3,3.5,2.7,4.4c-0.6,1-0.1,2.3,1.9,4.2c2.1,1.9-3.2,3.3-1.9,5c1.3,1.7-1.8,4.9,1.8,5.5c3.7,0.6,3.1-2.6,5.8-2.6
	c2.7,0-0.1,2.5,1.4,4.1c1.5,1.5,4.6,1.2,7.4,3.7c2.9,2.5,1,3.7-1.6,1.7c-14.7-5-10.3,2.6,0.1,4.2c5.9,0.1,2.1,6.4,2.6,9.8
	c-1,1.9-8.3-1.8-8,3.1c-1.3,3.4-6.2,7.9,0.6,9.4c1.5,1.1-2.7,1.8-3,3.6c2.1,5.4,7.6,4.8,6.1,12.8c0.6,2,2.5-0.5,2.3,2.1
	c-0.2,2.6,1.8,2.5,1.9,4.1c0.1,1.6,3.7,1.3,3,3.5c-0.8,4.6,4.4,6.2,6.2,9.7c1,3.7,5.7,2.8,8.1,4.2c4.3,0,6.6,4.9,11.3,4.8
	c3.2-0.8,3.6-7.3,4.8-9.4c3.4-2.7-4.2-8.7,1.6-8.4c2.3,0.7,3.8-5.1,4.6-5.9c2.2-1-2.2-4.5,1.6-4.8c3.6-0.7-5.5-6-0.2-4.5
	c5.2,2.5-1.1-3.8,5.8-3.5c2.4-0.2,3.7-1.6,3.2-3.5c-0.5-1.9,2.7-1.8,1.9-0.1c-1.7,4.9,3.1,0.5,8,0.6c3.9-1.2,7.7-4.5,8.6-7.8
	c0.9-3.2,5.4-2.9,4.8-5c-0.6-2.1,0.8-3,3.7-1.6c2.9,1.4,0.6-1,3.8-1c10.4-0.9,21.7-5.9,30.5-11.2
	C575.8,120.7,576.4,119.3,574.9,120.2z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M224.6,55.1c4-1.7,10.2,2.8,11.1-1.4C234.7,46.8,209.9,55.8,224.6,55.1z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M283.9,58.7c2.6,0,4.6-0.4,6.9-1.3c2.2-0.9-1.2-0.9,0.3-3c1.5-2.1-3.8-2.3-4.1-1.6c-0.3,0.7-7-4.3-8.1-2.7
	C277.9,51.7,281.3,58.7,283.9,58.7z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M253.2,49.6c2.1-1.4,5.8,0.9,5.1,2.5c-0.7,1.6-6.6-0.3-6.2,1.1c0.2,0.5,0.9,2.7,5,1.4c7-3.9,12.3,7.6,17.5,2.9
	c1.9-2.3-2.4-2.7-1.5-4.5c0.9-1.8-2.7-2.9-4.6-2.9s-2.6-3.6-4.1-2.9c-1.5,0.7-2.6,2-2.6-0.4c0-2.3-8.9-2.5-11.1-1.6
	C248.4,46.1,251.2,51,253.2,49.6z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M254.4,64.6c1.4-1.8-5.5-7.5-5.8-4.8C248.3,62.4,253,66.5,254.4,64.6z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M271.6,39.2c1.2,1.1,3.9,4.1,5.1,2C277.9,39,269.5,37.3,271.6,39.2z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M233,58.5c4.7-3.1-13.8-1.3-12.2,1.6c-1.5,5.8,13.9,2.8,10.6,0C229.6,60.1,230.8,58.9,233,58.5z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M276.7,69.3c-0.6,2.9-14.8-7-6,3c4.1,4.8-11.5-7.1-5.8,1.6c1,6.6-6.3-7.4-8.7-5.9c-3.3-0.2-1.8,3.3-0.5,5
	c9.2,11.5,18.3-2.6,14.6,9.3c2.5,2,10.2,1,11.3-2.9c1.7-1.3-1.7-1.1-0.3-4.1C282.5,72.3,276.9,67.5,276.7,69.3z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M376.6,111c-0.9,2-4.5-2.2-4.5-3.8c-9.6-3,0-5.3-13-6.3c-4.6-0.9-4.1,8.1-6.7,2.5c-0.3-2-5,2.1-6.3,2.1
	c-1-3.9,1.8-6-2.6-11.6c-1-2.7-6.2-1.6-7.5,0c-3.2,1.5-14,3.5-7,9.3c1,0.9-3.6,2.3-2.1,3.8c1.5,1.4,3.9,0.7,4.1,3
	c0.2,2.3-5.7-0.7-6.5-2.7c-0.9-2,0.3-3.8-0.9-5.2c-2.9-2.5,5.6-8.1,6-9.3c-6.8-4.9-24,8.1-18.9,17.9c-1.5,0.8,16.3,2.1,7.4,3.4
	c-2.2-0.4-6-1.1-5.8,0.5c2.1,5.9,9.3,2,11.5,4.8c2.7,2,13.3,1.2,16.3,2.9c6-2.3,5.5-2,12.7-0.5c1.9-0.2,0.7-2-0.5-2.5
	c-2.8-5.5,7.2,1.5,7.5,3c0.2,1.3,5.7,2.7,5.8,4.1c0.2,1.4-5.3,2.1-3.8,3.4c1.5,1.3,3.9-1.6,6-1.8c2.1-0.2,2.1,4.7,3.6,3.9
	c4.5-2.1,8.1,8.5,5.8,10.9c2.8,2.2,9.3-3.5,12.3,2.5c1.7,1.6-6.3,4.3-5.7,2.9c0.7-1.4-4.8-5.7-9.8-3.8c-5,2-1.5,5-1,6.8
	c-0.8,5.4-11.3-0.4-14.2,3c-7.5,4,0.6,7.3,6.3,5.4c6,1.4,2.2-3.7,9.6-1.1c2.2,0.5,1.7,3.8,4.5,4.1c2.7,0.4,0.5,3.2,2.2,5.2
	c5.9,0.6,16.2,8.9,21.3,7.9c1.4-1.8-7.5-10-10.1-10.7c-1.1-4.2,13.6,8.1,16.3,1.8c2.4-3.2-2.1-2.3-2.1-4.7
	c1.4-6.3-12.6-6.6-10.3-11.6c2.2-1.1-0.7-2.5,1-4.5c1.7-2,3.4,1.8,5.5,2c1.5-0.6,8.7,12.4,9.8,4.7c0.5-2,4.1-0.4,3.9-2.3
	c-0.2-2,2.9-4.3,5-5.2c2.1-0.9-1.7-3-4.5-2.9c-4.3,0.1-3.2-7.3-10.1-6.8c-3.1-1.3-11.8-2.4-12-7c0.3-2.5,4.5,0.7,5.3-1.3
	c0.9-2-4.3-0.9-4.5-2.3c1.1-2.2,4.9-2,0.2-5.2c-2.1,0.3-5,3.7-2.1-1.8c0.7-3.7-9.5,0.1-8.7-3.9c0.4-2.4-9.3,3.9-6.3-0.2
	C386.1,112.9,380.1,106.6,376.6,111z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M306.7,23.2c2.4-1.6,4.3-0.5,2.2,0.5c-2.1,1.1-1.4,1.8,1.5,1.6c2.9-0.2-1.2,0.9-1.2,2.7c0,1.8,3.4,0.7,3.6,2.5
	c4.8,5.7,13.2-4.2,11,2.3c-0.2,1.4,10.5,2.7,11,0.9c0.5-1.8,2.1-1.1,4.1-0.5c2.1,0.5,12.2-2,12.3-4.1c0.2-2.1,3.3,1.1,0,3.4
	c-5.5,3.9-21.2,0.3-7.9,8.2c2.9,2.3-3.8,1.1-6.5-2c-4-5.1-20.5-3.5-12,3.6c2.4,0,4.8,0.9,7.2,4.8c2.4,3.9,7.5,3,7.5,4.8
	c-2.5,1.4-18.7-6.4-20.4,2.5c-0.2,2.9,5.5,2.5,9.3,0.2c3.8-2.3,1,0.7-0.9,2.5c-1.9,1.8,4.5,3.2,4.5,5.4c-4.8,5.3-4.7-5.9-11.8-4.1
	c-4.5,0.4-3.3,3.6-0.5,3.9c2.7,0.4,2.9,2.5-0.2,2.5c-19.5,4.5-0.4,5.2,4.8,5.6c1.5,1.4,5.8,2,6.9,0.4c1.7-1.9,11.8-1.5,13.7,1.1
	c3.5,3,6.7-2.5,10.5-2c2.4,0,2.9-1.6,1.7-3.6c-1.2-2-4.3,1.4-4.6,0c-0.4-1.8-3.3-2.3-8.1-1.6c-4.8,0.7-2.7-3.2,0.2-2.3
	c2.9,0.9,6.9,0.5,10.1-0.4c3.3-0.9,0.7-2.5,0.7-3.9c0-1.4,4.1-0.5,7.2-0.5c9.4-3.2,10.9-13.1-2.6-10.6c-3.4,0,2.2-2.7,8.7-2.3
	c6.5,0.4,3.6-2.7,4.8-3.8c1.2-1.1,4.3,1.3,7.5,0.5c3.3-0.7,1-3.6,3.3-3.6c0.6-0.6,24.8-12,25-12.4c0.3-2.3-9.8,0-11.5-0.7
	c4.8-4.4,11.3,0.6,20.9-5.7c7-3.6,3.9-4.5,1-3.9c-2.9,0.5-6.2-0.5-6-2.5c0.2-2-4.3-0.2-4.5-1.3c-0.2-1.1-4.3-0.5-8.7,1.3
	c-4.5,1.8-0.3-1.8,1.5-2.5c1.9-0.7-11.3,0-13.5-1.6c-2.2-1.6-4.8,2.7-6.9,0.5c-2.1-2.1-7.4-0.4-6.7,1.3c0.7,1.6-0.9,1.6-2.9-0.2
	c-2.1-1.8-6,0.2-9.6-0.4c-3.6-0.5-2.1,3-6,1.3c-10-3.5-4.5,1.7-9.1,0.7c-1.7-0.5-3.3,0.2-2.4,2c0.9,1.8-6.3-0.5-6.3,1.4
	c0,2-2.6,3-4.6,1.3c-2.1-1.8-8.6-2.3-6.7-0.7c1.9,1.6-5.8,0.7-3.8,2.3c2.1,1.6-2.6,2.9-2.6,2c0-0.9-4.3-2.5-6-0.9
	c-1.7,1.6-2.2,3.6-3.1,2.5C311.3,18.5,295.1,25.7,306.7,23.2z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M301.9,82.1c1.2,3.6,2.6,1.1,4.5,2.5c1.9,1.4,4.8,2.3,6.3,0.9c1.5-1.4,2.2-2.5,2.7-1.3c0.8,2,4.6,2.3,12.9,2.5
	c8.2,0.2,6.2-3.6,8.6-2.1c20,5.6,18.7-13.3-8.1-7.2c-3.3,2-8.4-0.9-11,0.5c-2.6,1.4-1.7-1.8-6.3-2c-4.6-0.2,0.3-2.7,1.2-3.8
	c0.9-1.1-7-3.9-10.3-3.2c-4,0.7-17.4-7.5-18-1.1c1.2,1.8,11.1,5.5,12.2,3.8c1-1.8,3.6,2.1,5.1,4.1C303.3,77.8,300.7,78.6,301.9,82.1
	z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M310.6,61c-3.1-1.6-7,1.8-3.8,3C309.6,65.1,313.7,62.6,310.6,61z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M297.8,59.6c-0.3-1.1-13.3,0.6-11.3,2.2C289.9,64.4,298.1,60.7,297.8,59.6z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M345.6,93.1c-1,2.3,2.2,2.3,2.4,5c0.2,2.7,3.4,3.6,5.8,2c2.4-1.6,9.3,0.5,9.3-1.6
	C361.6,94.3,348.8,91.3,345.6,93.1z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M286,41c4.1,8.2,10.5,2.2,15.1,4.7c0.7,2-8.1,2.2-6.9,3.4c5,2.7,8.2,8.7,14.6,7c0.9-1.4,3.3-1.1,5.5-0.2
	c2.2,0.9,2.4-5.6,3.9-4.5c1.5,1.1,1.4-2.3,3.9-3.4c2.6-1.1,6.3-1.1,6.5-2.3c1.1-3.2-10.6-0.3-7.5-4.5c1.4-1.8-3.4-2.3-1.7-3.8
	c1.7-1.4-3.3-2.9-2.7-0.7c0.5,2.1-3.1,0.2-3.6-1.8c-0.5-2-7.5-2.7-12.2-7c-4.6-4.3-9.4-1.4-6.7-0.9c7.6,2.8-8.4,0.8-2.2,3.4
	c3.6,1.4-0.3,2-3.3,2c-2.9,0-3.1,3.9,0.7,4.5C297,40.5,278.9,36.7,286,41z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M274,110.8c3.8,2.7,2.6-2.1,6.9-2c5.4,1.4,6.3-17.1,2.7-10.4c1,2-0.2,3.9-0.5,1.6c-0.3-2.3-3.6-0.2-4.8-2
	c-1.2-1.8,3.8-2,5-4.3c1.2-2.3-3.9-2-2.7-3.8c1.2-1.8-7.5,0.2-5,1.3c2.6,1.1,0.2,2.5-2.2,0.7c-2.4-1.8-9,1.2-6.7,3.2
	c1.9,1.6,7.5-1.1,4.3,3.6c-3.3,4.7-4.6-1.3-7.7-0.7C256.6,100.8,272.3,106.7,274,110.8z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M262.5,122.3c14.3,1.9-2.6-6.1-7.5-8.8c-3.3-2.5,0.9-3.8-1.5-5.7c-2.4-2-3.3-7.3-1.5-9.1c7.5-6.6-6.4-7.3-7-3.9
	c0.5,0.7,4.8,3.2,4.8,4.3c0,1.1-3.9-3-5.3-2.2c-11.9-3.1,3.9,12-3.9,12c-1.4,0.4-1.5-5-2.9-7c-1.4-2-6.9-4.3-7.2-2.9
	c-0.3,1.4,3.4,1.4,2.9,3.4c-0.5,2-2.6-1.3-4.5,0.4c-3.1,2.7,1.5-5.1-7.4-4.1c-3.4,0.4-1.5,2.9-2.6,3.8c-1,0.9-6.2-0.5-2.9-1.3
	c3.3-0.7,1.9-2.7,0.7-4.7c-1.2-2-4.5,0.4-9.8,2.3c-9.9,4.1-4.4,2.5-8.2,7.2c-2.1,1.8,0,3.4,1.9,3.4c1.9,0,0.2,1.6,1.5,2
	c1.2,0.4,7.4-2.5,9.1-1.3c1.7,1.3-10.1,2-10.1,4.1c1.1,5.3,23,0.2,24.5,4.5c-1.2,2.1-20.9-0.6-21.3,3.4c0.7,5.3,14.9,3.4,14.2,5.7
	c-0.2,3,2.4,3.8,8.4,3.9c7.9-1.8,13.1-1.4,17.3-5.2c1.4-2.2,2.6-0.9,2.9,0.2c4.4,2.2,11.9,4.8,17.1,2c2.7-1.4-1.2-5.4-2.6-3.6
	C259.1,127.5,256.7,124.8,262.5,122.3z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M233.2,75c-1.5,0.7,3.6,2,2.9,3c-6.8,3.4-11-6.8-18-5.9c-4.4,0.3-0.7-5.6-8.2,0.2c-7,2.5,8.7,3.3,0.2,3.4
	c-3.9-0.2-5.7,3.4-2.9,2.7c2.7-0.7,6-0.7,3.9,0c-16.1,1.9,2,6,3.4,3.2c1.7-2,3.9-3.9,3.1-1.3c-0.9,2.7,2.7,0.7,7.4,0.5
	c4.6-0.2-0.9,2.9-5.7,3.2c-11.5,4.3,10.4,4.9,13.5-0.7c1.9-2,3.6,1.1,5.5-0.4c1.9-1.4,5.5,0.2,8.7-0.5c5.5-2.4,2.3-12.4-2.9-6.6
	c-3.9,0.6-4.7-7.7-7.2-8.1C225.6,72.6,238.6,72,233.2,75z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M606,151.9c-1.4,0-2.4-1.1-2.1-2c-0.9-5.3-1.2-0.4-7-4.2c-1.6,3,1.4,2.4-2.7,2.6c0,0.8-0.9,1-1.5,0.1
	c-0.7-0.9-2.3-0.4-2.2,0.4c-0.4,0.8-4.4-2.8-4.6,1.1c0.1,2.6-3.7-5.1-3.3,0.4c0.6,1.1,0.3,2.4-0.5,1.8c-0.9-0.5-2,0.4-2.5,1.2
	c-3.6-1,2.4-5.3-6.9-7.3c-1.7,0-0.6,1.3,0.6,2.3c0.6,2.5-2.3-4-5.9,3.6c0.5,3,7-1.1,8.4,1c0.9,0.9-1.7,1.3-0.7,2
	c0.9,0.7,0.2,1.3-2,1.1c-2.2-0.2-4.8,0.5-4.4,1.3c0.4,0.8,5.5-0.7,5.6,0.1c0,3.3,2-0.2,2.5,2.9c0.7,0.3,0.5,1.7-1.3,1.9
	c-5.3,0.8,0.4,2.7,3,1.1c14.7,6.3,9.7,1.1,23.7-2.5C604.8,159.3,611.5,154,606,151.9z"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M205.3,330.4c0.6,0.9,1.3,2.2,2.2,3.6c1.5,2.7,3.1,5.7,3.1,7.5c0,0,0,0.1,0,0.1c0,0.1,0.1,0.1,0.1,0.2
	c0,0,0,0,0,0c0,0,0.1,0.1,0.1,0.1c0,0,0,0,0,0.1c0.6,0.8,1.8,1.8,3.2,2.8c2.3,1.8,4.6,4,3.3,6.2c0,0.1-0.1,0.2-0.2,0.2
	c-0.3,0.1-0.9,0-1.5-0.1c-0.7-0.1-1.3-0.2-1.4,0.1l0,0c0,0,0,0,0,0c0,0,0,0.1,0,0.1c0.3,0.9,1.2,1.7,2.5,2.5
	c0.1,0.1,0.3,0.2,0.5,0.3c0,0,0,0,0,0c0.4,0.3,0.9,0.5,1.4,0.8c2.2,1.2,4.7,2.4,5.8,4c0.7,1.1,0.8,2.3-0.1,3.8c0,0,0,0,0,0
	c-0.6,1,5.5,4.7,7.8,7.3c0.6,0.7,0.9,1.3,0.8,1.8c0,0.1,0,0.1,0,0.2c0,0,0,0,0,0v0c0.4,0.1,0.6,0.2,0.9,0.1c1-0.2,1.1-1.8,0.6-3.4
	c-0.5-1.7-1.9-3.3-3.8-2.7c0,0,0,0,0,0c-0.2-0.7-0.4-1.6-0.8-2.5c-0.6-1.8-1.3-3.7-2.2-5.5c-0.2-0.4-0.3-0.9-0.4-1.3
	c-1.3-2.2-4.4-6.6-6.3-9.2c-1.2-2.5-0.3-3-0.8-3.6c-0.3-0.4-1.1-0.6-2.6-1.1c-0.1,0-0.1,0-0.2-0.1c0,0,0,0,0,0
	c-0.3-0.1-0.5-0.3-0.8-0.5c-0.6-0.6-1.1-1.9-1.5-3.2c-0.3-1.7-0.3-3.6-0.9-5c0-0.7,0.2-1.2,0.5-1.2c0.2,0.1,0.5,0.4,0.8,0.6
	c0.2,0.3,0.5,0.4,0.7,0.5c0.2,0.1,0.4,0.1,0.5,0c0,0,0,0,0,0c0.2,0,0.4-0.1,0.6-0.1c0.4,0,0.8,0.3,1.3,0.6c0.1,0.1,0.1,0.2,0.2,0.4
	c0.2,0.5,0.7,0.5,1.2,0.5c0.4,0.2,0.9,0.2,1.4,0.1c-0.1,0.2-0.2,0.4-0.2,0.6c-0.3,1.3,0.1,2.6,0.7,3.8c0.5,1.1,1,2.2,1.2,3.4
	c0.1,0.6,0,1.2-0.2,1.9c0,0.9,0.1,1.3,0.3,1.6c0.4,0.5,1.2-0.2,1.5-0.5c0,0,0,0,0,0c0.5-0.1,2.8,2,4.4,3.5c0.7,0.6,1.2,1,1.5,1.2
	c0,0,0.1,0,0.1,0c-0.2,1.2,0.9,2.1,2.1,3c1,0.7,2,1.3,2.4,1.9c1.3,0.7,0.7,1.9,0.6,3c0,0.4,0,0.7,0.1,1c0.2,0.6,0.8,1.2,2.3,1.6
	c1.3,1.3,3.1,3,5,4.8c3,3,6.1,6.5,7.5,9.9c0.9,2.1,1.1,4.2,0.1,6c0,0.1-0.1,0.2-0.1,0.2c0,0,0,0,0,0c-0.1,0.2-0.3,0.4-0.4,0.6
	c4.3,11,21.7,12.4,31.2,18.9c2.6,0.8,5.3,0.3,7.9,0c2.7-0.4,5.3-0.6,7.8,0.9c1.1,0.7,2.2,1.7,3.3,3.2c0,0,0,0,0,0c0,0,0,0,0,0
	c1.3,1.7,2.6,2.7,3.8,3.3c0.3,0.1,0.6,0.3,0.8,0.4c0.2,0.1,0.4,0.1,0.5,0.2c0,0,0,0,0.1,0c0.2,0,0.3,0.1,0.5,0.1c0,0,0.1,0,0.1,0
	c0.3,0.1,0.6,0.1,0.9,0.2c0.4,0.1,0.9,0.2,1.4,0.3c0.2,0,0.4,0.1,0.7,0.1c0.3,0.1,0.6,0.1,1,0.2c0.9,0.5,2.4,1.3,4,1.9
	c0.8,0.3,1.6,0.5,2.4,0.7c1,0.2,1.9,0.1,2.4-0.4c0,0,0,0,0,0c0.8-0.9,1.9,0.6,1.5,1.5l1.4-0.3c0,0,0.5-0.4,0.8-0.7
	c-0.1,0.1-0.1,0.3-0.2,0.4c-0.3,0.3-0.9,0.6-2,0.7c0.1,0.7,0.5,1.4,1,2.1c0.1,0.1,0.2,0.2,0.3,0.3c0.2,0.2,0.4,0.5,0.6,0.7
	c0.1,0.1,0.2,0.2,0.3,0.2c0.5,0.5,1,0.9,1.6,1.4c0.2,0.1,0.3,0.3,0.5,0.4c0.3,0.3,0.6,0.6,0.9,0.8c0.5,0.5,0.9,0.9,1.2,1.4
	c0,0,0,0,0,0.1c0.1,0.2,0.2,0.3,0.3,0.5c0,0,0,0,0,0c0.4,1.9-2.5,3.3,0.7,5.1c1.9,0.8,1.3-0.4,2.2-0.4c7.3,3.9,2.2,6,7.6,6.2
	c1,0.1,2,0.5,3,1c1.8,1,3.5,2.4,4.8,3.3c0.8,0.7,1.5,0.7,2.1,0.3c0,0,0.1-0.1,0.1-0.1c0,0,0.1-0.1,0.1-0.1c0,0,0.1-0.1,0.1-0.1
	c0.6-0.8,0.6-2.1-0.6-3c0.4-0.4,0.9-1,1.4-1.5c0,0,0,0,0,0c0.2-0.2,0.4-0.3,0.6-0.5c0.7-0.6,1.4-1.1,2.2-1.4c0.7-0.2,1.4-0.3,2.1,0
	c0.2,0.1,0.4,0.2,0.5,0.3c0,0,0,0,0,0c0,0,0.1,0.1,0.1,0.1c1.7,1.4-0.4,3.5,1.5,5.6c0.3,0.3,0.7,0.7,1.3,1c0.2-0.2,0.4-0.4,0.5-0.6
	c-0.2,0.2-0.4,0.4-0.5,0.6c1.1,0.9,1.8,2.2,2.1,3.5c0.1,0.5,0.2,1.1,0.2,1.6c0,0.6-0.1,1.1-0.3,1.6c-0.2,0.4-0.3,0.7-0.4,1.1
	c-0.2,1.5,0.6,3.1,0.9,4.8c0,0.1,0,0.1,0,0.2c0,0.1,0,0.1,0,0.2c0,0.2,0.1,0.3,0.1,0.5c0.1,1.1-0.1,2.1-0.9,3.1
	c0,0.1-0.1,0.1-0.1,0.2c-0.6,1.1-1.3,1.7-2.1,2.2c-1,0.6-2,1-2.7,2c-0.5,0.6-0.8,1.4-1,2.6c0.4,0.3,0.7,0.6,1.1,0.9
	c0.8,0.6,1.5,1,2.2,1.3c1.3,0.6,2.5,1.1,3.9,2.5c1.1,0.4,1.8,0.4,2.3,0.3c-0.5,0.1-1.2,0.2-2.3-0.3c-2.4-2.5-4.1-1.9-7.2-4.7
	c0.2,1.3-0.8,1.8-2.7,2c-1.5,0.1-1.3,1.8-1.3,3c0,1.2-1.2,1.2-1.7,2.4c-0.5,1.3,0.2,1.5-1,2.2c-2.6,1.1,0.3,3.9-1.1,5.5
	c-0.2,0.6,1,0.9,2.2,2.1c1.2,1.2,1.4-0.4,1.9,0.4c0.6,1.1-0.6,2.6-2,2.6c0,0,0,0,0,0c-3.9,1.3-3.6,6.4-1.7,9.1
	c-2.8,1.8,0.6,2.7,3,4.6c5.5,5.8,7.8,17.1,12.6,23.6c4.2,4.6,0.4,5.3,3.8,8.9c5.6,4.9,18.3,10.7,22.1,16.1c0,0,0,0,0,0
	c0.2,5.4,1.4,14.6-1.2,20.1c0.5,6.8-0.6,18.5-4.4,24.6c1.1,6.5,2.3,1.3,0,9.2c0.7,5.5,0,7.8-0.9,11.6c-1.2,4.4-3.3,13.7-6.2,16.8
	c-1,0-1.3,1.2-0.5,3c-0.2,3.2,1.7,6.2,0.7,9.2c-1.1,2.8-2.1,5.1-1.7,8.1c0.7,1.8,0.5,2.4-0.3,2.4c-0.9-0.7-3.6,8.9,0.2,7.1
	c2.1-0.2,0.4-7.3,1.7-7.3c1.3,0,0.2-1.2,1.5-1.5c1.3-0.4,0.2,1.3,1,1.5c0.9,0.3,1.5,1,0.3,1.8c-1.2,0.8,0.3,2.4-0.3,3.4
	c-0.6,1-1.5,2.1-0.9,3.2c0.5,1.2-0.6,1.1-0.7,2.6c-0.1,1.5,0.8,1.6,0.2,2c-0.6,0.4,0.8,1.5-0.1,1.7c-0.9,0.2-0.3,1.3,0.2,2.4
	c-0.4,2.2-3.1-1.1-1.6,3.9c-2.2,0.8-3.5-1.9-0.8-3.8c0.8-3.7-4.6-4.4-0.8,0.3c0,1.2-2.1-0.3-2.1,1c0.1,1.3-1.5,0.8-2.2,1.7
	c-0.8,0.9,1,1.7,0.2,2c-0.9,0.3-2.3,1.5-2.4,2.4c0.5,2.5,1.4-2.6,2.7,0.2c-0.1,1,4.9-0.6,2.6,2.3c-2.7,1.8-1,3.8,2.4,2.9
	c2.4,3.7-1.8-0.7-2.5,1c-0.6,1.1-1.5,0.5-2.3,0.2c-4.5-0.3-1.8,6.6,0.8,2.8c0.3-0.7,1.5-1.4,1.6,0.2c0,1.2-7.4,5.4-3.2,5.8
	c0.9-0.5-0.3,1.1,0.9,1.3c2.4,0.5,0.1-4.9,2.8-3.5c1,1-0.5,2-0.5,2.9c-0.1,1.7-0.8,3.3-1.6,4.6c0.1,1.1-2.4,0.4-1.3,1.1
	c1.1,0.7-0.7,1.7-0.1,2.4c0.6,0.7,1.1-1,2.1-0.6c0.7,0.8,4.9-0.8,2.7,1.4c-4.2,2.8-0.4,0.9,0.5,2.1c1.1,2.5,1.5-3.7,3.8,0.5
	c0.3,1.1-2.1,1.7-3.4,1.4c-3.3,0.2-0.3,5.1,1.7,1.4c1.9-1.8,9.7,0.6,3.5,1.2c-1.5,2.1-3.7-1.4-4.5,1.9c-0.1,1.2-1.5,1.2,0.1,1.6
	c1.5,0.4,1.1-1.7,3.5-1.5c2.4,0.2,3.7-2.3,4.4-0.9c0.7,1.4-2,2.6-2.9,1.7c-3.5-0.8,0.2,4.5-6.1,1.4c-1.4-0.2-0.8,1.3,0.4,2.7
	c1.2,1.3,1.9-0.2,3.3,0.8c1.5,1,1.5,2.1,2.5,1.1c1-1,0-0.9-0.7-1.3c-0.7-0.4-0.1-2,1.5-1.2c4.9,1.3,1.2-6.5,6.4-6.7
	c4,0.1-4.1,2.6-1.1,4.3c1.1,1.3,2.6-0.8,3.5,0.2c0.9,1-1.7,0.8-2.1,1.8c-0.4,1,1.6,2.1,0.2,2.9c-1.5,0.7-0.8-3-1.6-3.3
	c-3.1-0.9,1.7,6.5-2.7,1.8c-0.9-1-2.1,0.4-1,1.5c1,1.1-2-0.1-2,1.6c1.4,2.6,2.9-2.4,4.8,4.2c1.1,0.9,0.2-2.9,1.4-3.1
	c1.2-0.3,0.1,2.9,2.7,3.8c2.7,0.9,0.2-0.5,1.4-1.4c1.3-0.6,6.9,7.1,3.5,0.4c-0.7-1.8,3.5,1.7,5.4-0.6c0,0,0,0,0,0
	c0.7,0.1,1.4,0.1,1.8,0c1.5-0.8,9.3-1.9,2.2-2.6c-3.6-0.4-7.4-6.1-9.5-7.2c-2.6-0.2,3-2.7-1-3.9c-0.1,2.4-0.6,13.2,0,13.3
	c0.5-0.2,1.5-0.2,2.5-0.1c-1.2,0.1-2.3,0.2-2.5,0.1c-0.6-0.1-0.1-10.8,0-13.3c-1.4,0.1-4.9-0.2-3-2.2c0,0,0,0,0,0c0,0,0,0,0,0
	c5.4,0.9,3.6,0.4,1.6-3.2c-0.6-2.7-1-7.6,2.4-8.4c3.2,0,1.9-6,5.1-7.3c4.7-3,4.5-3.7,5.2-7.9c-2.3-1.5-8.6-1.1-6.5-7.3
	c3.9-5.7,7.6-1.5,8.3-8.7c-1.3-4.3,4.3-4.2,2.6-5.5c-2.3-1.7,0.7-2.2,2.3-0.3c3.7-0.6,1.4-5.6-1.1-2.4c-3.1-0.4-3.5-5.1-2.9-7.9
	c1.1-0.4,10.2,4,10.6-0.6c0-1-1.2-2.8,0.3-3.8c2.1-0.7-2.3-5.5,1.2-4.5c6.4,2.1,18.6-2.6,20.8-9.7c1.6-2.9-5.4-2.7-2.3-6.2
	c2.1-3.1-7.4-2-4.9-5.2c0.7-0.7,1-0.6,1.4-0.2c0.4-0.9,0.4-1.3,0.3-1.5c0.1,0.2,0.1,0.6-0.3,1.5c5.5,2.1,14.9,5.1,18.4-2.5
	c0,0,0,0,0,0c0.5-0.7,1.1-1.3,1.8-1.9c3.7-2.9,2.2-6.6,5-9.9c1.9-1.9-0.3-3.1,2.1-4.3c2.4-1.2,2.1,1.1,0.1,3.7
	c-2,2.6-2.3,3.3,0.3,1.3c3.7-2,4.9-11.5,8.8-12.2c3-2.3-0.9-12.5,2.4-14.8c2.5-0.3,6.4-6.9,9.9-6.2c4.4-2.2,5.6-5.2,11.6-3.6
	c4.7,1,2.3-3.1,5.6-3.6c2.8,0.3,1-5,3.3-5.8c2-3.2,3.5-7,3.6-10.8c2.6-3,1.8-2.1,2.4-7.2c1.3-4.1-1.3-10.7,0.9-13.8
	c1.7-1.2-2.3-2.2,0.4-2.8c3.1,3,4.1-2.1,5.5-5.5c6.6-4.6,12.7-13.4,8.3-22.7c-2.6-5.7-6.4,0.9-12.6-7.5c-4.3-4.8-8.5-4.5-11.3-3.7
	c-5.3,0.4-8.6-4.1-12.7,0.7c1.2-5.6,0.3-6-8.3-9.6c-3.9-1.6-5.7-0.8-5.7,1.8c0,2.6-1.5-0.8-2.7,2c-1.2,2.8-3.4,1-2,0.1
	c6-3.8,2.2-8.5-4.5-6.8c-2.1,0.9-0.8,4.1-2.5,3.1c-2.9-3.6,4.4-3.3,4.9-8.3c0.9-2.1-1.6-2.5-3.2-4.3c-1.5-1.8-1.2-6.9-2.8-7.8
	c0,0,0,0,0,0c0,0,0,0,0,0c-2.4-2.1-6-6-9.9-6.5c-1.8-1.4-4.3-0.7-6.3-0.4l-2.1-0.1c-0.2-0.1-0.3-0.2-0.4-0.3c-0.9-1.5-2.1-0.5-3.3,0
	c-0.2,0.4-0.3,0.7-0.4,1.1c0,0,0,0,0,0c0-0.2,0.1-0.4,0.1-0.6c0.1-0.2,0.1-0.3,0.2-0.5c-1.1-0.1-2.6-3.3-3.6-3.3c-0.1,0-0.3,0-0.4,0
	c-0.9-0.2-1.8-1-1.6-2.4c0-1.4-3.7-3-5.3-4.3c0,0,0,0,0,0c0,0,0,0,0,0c-0.2-0.1-0.5-0.2-0.7-0.3c-1.8-0.6-3.5-0.2-4.4-0.3
	c0,0-0.1,0-0.1,0c-0.7-0.2-0.7-0.9,0.7-3.3c0.4-1.4-4.2-1.8-6.1-2.5c-1-0.4-1.2-1,0.8-1.9c0-0.1,0.1-0.1,0.1-0.2
	c0.2-0.6-0.6-0.8-1.7-0.8c-0.5,0-1.1,0.1-1.7,0.2c0,0,0,0,0,0c0,0,0,0-0.1,0c-2.7,0.5-6.1,1.6-7.2,2.5c0,0-0.1,0.1-0.1,0.1
	c-1.9,1.1-5.1-3.1-7.3-2.2c-0.3,0.2-0.6,0.3-0.9,0.4c-0.8,0.3-1.4,0.4-1.8,0.4c-0.1,0-0.2,0-0.3,0c0,0,0,0,0,0
	c-0.7-0.1-1.1-0.5-1.4-1.1c-0.8-1.4-1-3.7-3.6-3.7c-1.5,0.1-1.7-1-2.2-1.7c-0.2-0.3-0.5-0.6-1-0.6c-0.3,0-0.5,0.1-0.6,0.2
	c-0.5,0.5,0.4,2-0.5,2.4c-0.4,0.1-0.8,0.2-1.2,0.3c-3.2,1-3.8,2.1-3.6,3.8c0.1,1.2,0.6,2.7,0.8,4.6c0,0.1,0,0.2,0,0.3
	c-0.3,0.6-0.8,0.8-1.4,0.7c-0.1,0-0.1,0-0.2,0c0,0-0.1,0-0.1,0c-0.9-0.3-1.9-1.1-2.4-2.1c0.1-1.5,0.6-2.5,0.9-3.4
	c0.5-1.3,0.9-2.4,0.3-4.7c0.1-0.2,0.2-0.3,0.4-0.4c1.2-0.9,3.6-1.3,1.6-2.8c-0.1,0-0.1-0.1-0.2-0.1c-0.1,0-0.3,0-0.4,0c0,0,0,0,0,0
	c0,0-0.1,0-0.1,0c-0.7,0.1-1.5,0.5-2.3,0.9c-0.2,0.1-0.5,0.3-0.7,0.4c-1.5,1-3,2.2-4.1,2.8c-0.9,0.1-1.5,0.2-2,0.4
	c-0.8,0.3-1.2,0.6-1.5,0.8c-0.3,0.3-0.5,0.4-1.2,0c-0.2-0.1-0.4-0.2-0.6-0.3c0,0,0,0,0,0c0,0,0,0,0,0c0,0-0.1,0-0.1,0
	c-4.2,0.4-2.2,5.2-4.8,7.3c-1.9,1-2.6,3.5-3.8,4.5c-0.3,0.2-0.6,0.4-0.9,0.4c-0.1,0.5-0.3,0.8-0.5,1.1c-0.2,0.4-0.5,0.6-0.9,0.9
	c0,0,0.1-0.1,0.1-0.1c0.1-0.1,0.3-0.3,0.4-0.4c0.1-0.1,0.2-0.2,0.3-0.4c0,0,0,0,0,0c0.2-0.3,0.4-0.7,0.5-1.1
	c-1.2-1.9-2.3-3.3-3.4-4.2c-4.3-4-8.7,0.7-12.9,1.2c-1.6,0.1-3.3-0.6-5.2-2.7c0,0,0,0,0,0c-1.4-1.6-3-3.7-3.8-5
	c-1.5-1.6-0.3-3.8,0.4-6.1c-0.1-2.6,0.2-4.4,0.5-6.3c0.2-1.3,0.5-2.6,0.7-4.2c0-0.3,0.1-0.6,0.1-1c-1.9-0.1-3.9,1.8-4.5,1.9
	c-0.7,0.1-3.4-0.6-3.7,0.9c0,0,0,0,0,0c0,0,0,0,0,0c0.3-1.5,3-0.8,3.7-0.9c0.6-0.1,2.6-2,4.5-1.9c-1.7-3.1-8.7-5.7-12.5-3.4
	c-2.8-0.3-5.9,0-8.1,1.5c0.3-0.4,0.6-0.7,0.8-1c-1.3,0.8-1.8,0-1.6-1c2.9-2.6,0.1-7.9,3.1-9.3c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0
	c0,0,0,0,0,0c2.4-2.1,0.9-7.9,2.4-10c1-1.2,2.2-1.4,2.2-2.7c0-0.2,0-0.4-0.1-0.6c-0.2-1-0.8-0.9-1.5-0.8c-0.7-0.2-1.5-0.3-2.4-0.4
	c-0.6-0.2-1.3-0.2-2.2,0c-1.7,0.1-3.4,0.4-4.8,0.9c-0.7,0.2-1.2,0.5-1.7,0.8c-1,0.7-1.7,1.6-1.8,2.8c-0.3,0.9-0.5,1.9-0.8,2.9
	c-0.2,0.2-0.3,0.6-0.3,1.2c-0.2,0.7-0.5,1.4-0.9,2.1c-0.8,0.6-1.7,1-1.6,1.8c0.2,1.3-2.2,0-2.9-0.5c-0.3-0.2-1.5,0.1-2.9,0.4
	c-2.4,0.6-5.5,1.5-6.2,1.7c-1.6-1-3.9-1.5-5.3-2.8c-0.8-0.8-1.3-1.8-1.3-3.4c-0.5-0.5-0.9-1.1-1.3-1.6c-2.6-3.5-4-7.1-4.3-11.2
	c-0.3-4,0.3-8.4,1.7-13.3c-0.3-0.1-0.6-0.3-0.9-0.4c0.3,0.1,0.6,0.2,0.9,0.4c0.1-1-0.2-1.7-0.6-2.3c-0.2-0.4-0.4-0.7-0.6-0.9
	c-0.1-0.1-0.1-0.2-0.2-0.3c-0.3-0.4-0.4-0.8-0.2-1.2c0.1-0.2,0.2-0.3,0.5-0.5c0.3-0.2,0.5-0.4,0.6-0.6c0.1-0.2,0.2-0.4,0.3-0.6
	c0-0.1,0-0.2,0.1-0.3c0-0.1,0-0.2,0-0.3c0,0,0,0,0,0c-0.1-0.9-0.7-1.6,0-2c2.5-0.7,2.2-2.9,3.7-3.4c0.4-0.1,0.8-0.1,1.4,0
	c0.5,0,0.9-0.1,1.2-0.2c1.9-0.7,1.8-3.2,3.1-3.9c0.3-0.2,0.8-0.2,1.4-0.1c1.1,0.3,1.4-0.8,1.7-1.4c0.2-0.2,0.4-0.4,0.7-0.2
	c0.1,0.1,0.1,0.1,0.2,0.1c0.6,0.4,1,0.4,1.3,0.3c0.3-0.1,0.5-0.4,0.7-0.6c0.1-0.1,0.2-0.1,0.3-0.1c0.2-0.1,0.5,0.1,1,0.5
	c0.1,0.1,0.2,0.2,0.3,0.3c0.9,1,1.6,1,2.1,0.8c0,0,0.1,0,0.1-0.1c0,0,0,0,0,0c0.4-0.2,0.7-0.5,1-0.7c0.1-0.1,0.2-0.2,0.4-0.2
	c0.5-0.2,1.2,0,2.1,1.5c0.5,0.7,1.9,1.2,3.1,1.1c0.5,0,1-0.1,1.3-0.4c0.4-0.3,0.6-0.6,0.5-1.2c0,0,0,0,0,0c0-0.1,0-0.1,0-0.2
	c0.3-0.5,1.8,2.3,3.2,2.6c0.1,0,0.1,0,0.2,0c0,0,0,0,0,0c0.3,0,0.4,0,0.5-0.1c0.4-0.4-1.1-1.8-1.9-3.1c-0.5-0.9-0.7-1.7,0-2.3
	c0.5-0.3,1.3-0.5,2.7-0.6c0.1,0,0.2,0,0.3,0c0.9,0.4,1.5,0.2,2.1-0.1c1.2-0.7,1.7-2,2.2,0.5c0.3-0.1,0.6-0.1,0.9-0.2
	c2.8-0.5,4.6-0.4,5.7,0c1.8,0.6,2,2,2.4,2.7c0.4,0.6,0.9,0.7,2.4-0.1c1.9-2.4,4-1.5,5.5,0.4c1.5,1.9,2.5,4.8,2.2,6.7
	c0,0.3-0.1,0.5-0.2,0.7c0.5,0.7,0.3,1.6,0.6,2.5c0,0.1,0.1,0.2,0.1,0.3c0.2,0.4,0.4,0.9,1,1.3c0.4,0.3,0.7,0.7,0.8,1.1
	c0.1,0.4,0.1,0.9,0.1,1.3c0,0,0,0,0,0c0,0.9,0,1.6,0.6,1.8c1.7,0.4,2.5,3.5,3.6,4.5c0,0,0,0,0,0c0,0,0,0,0,0
	c0.4,0.3,0.9,0.4,1.4-0.1c0.1-0.1,0.3-0.3,0.5-0.5c0.2-0.4,0.4-0.9,0.6-1.3c0.1-0.3,0.3-0.7,0.4-1c0.5-1.5,0.6-3.1,0.5-4.7
	c-0.2-2-0.8-3.9-1.9-5.8c-1.7-3.9-2.7-6.9-2.9-9.5c-0.4-3.8,0.8-6.6,3.3-9.4c1.8-2,4.3-4,7.5-6.5c0.3,0,0.5,0,0.7,0
	c2.5-0.2,1.8-3.7,5.7-3.9c2.2-0.4,0.9-1.6,0.3-2.7c-0.2-1.1-0.1-1.5,0.2-1.5c0,0,0,0,0.1,0c0.6,0,1.7,0.9,2.7,0.6
	c0.2-0.1,0.4-0.2,0.6-0.4c0.4-0.3,1-0.8,1.3-1.2c0.1-0.1,0.1-0.1,0.2-0.2c0.4-0.5,0.4-1-0.6-1c-0.1,0-0.1,0-0.2,0
	c-0.1,0-0.1,0-0.2,0c0,0-0.1,0-0.1,0c-0.1,0-0.1,0-0.2,0c-2.9-0.5,2.4-2.8-1.9-4c-1-0.3-0.9-0.8-0.6-1.3c0.2-0.4,0.5-1,0.5-1.6
	c0,0,0-0.1,0-0.1c0,0,0,0,0-0.1c-0.1-0.6-0.6-1.3-1.9-2c-0.2-0.1-0.4-0.2-0.6-0.3c-0.5-0.4-0.4-0.6-0.1-0.9c0.1,0,0.1-0.1,0.2-0.1
	c0.1-0.1,0.3-0.1,0.4-0.2c0.4-0.1,0.7-0.2,1-0.3c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0.1,0,0.1,0c0.4-0.4,0.4-3.2,0.6-4.8
	c0.1-0.5,0.2-0.9,0.4-1.1c0.2-0.2,0.4,0,0.8,0.6c0,0,0,0,0,0c-0.3,0.4-0.5,0.7-0.7,1.1c-0.9,2.5,2.4,4.6,1.4,7.7c0,0,0,0,0,0
	c-0.1,0.4-0.1,0.7-0.1,0.9c0,0.1,0,0.2,0,0.2c0,0,0,0.1,0,0.1c0.2,0.3,0.9-0.5,1.8-2.4c0.1-0.1,0.1-0.2,0.2-0.3
	c0.1-0.1,0.2-0.2,0.2-0.4c0.8-1.5,0.6-3,0.2-4.1c-0.3-1-0.7-1.8-0.6-2.1c0.1-0.1,0.3-0.2,0.7-0.1c0,0,0,0,0,0
	c0.1,0.2,0.2,0.3,0.3,0.4c0.5,0.6,0.9,0.8,1.5,0.3c0.3-0.2,0.5-0.5,0.9-0.9c0.4-0.4,0.7-0.9,1.1-1.4c1-1.5,1.7-3.2,1-3.6
	c-0.2-0.2-0.1-0.4,0-0.6c0.8-1,6.3-0.9,7.6-1.8c0,0,0.1-0.1,0.1-0.1c0,0,0,0,0,0c0,0,0.1-0.1,0.1-0.1c0,0,0,0,0,0c0,0,0,0,0,0
	c0,0,0-0.1,0-0.1c0.1-0.2,0.1-0.4,0-0.5c-0.5-0.4-2.8,0.2-4.4,0.4c-0.9,0.1-1.5,0.2-1.5-0.1c0-0.2,0.2-0.4,0.4-0.5
	c0.2-0.1,0.4-0.2,0.6-0.3c0.5-0.2,1.2-0.4,1.8-0.6c0.3-0.1,0.7-0.2,1-0.2c0.2,0,0.3-0.1,0.5-0.1c0.3-0.1,0.7-0.1,1-0.1
	s0.6-0.1,0.8-0.1c0.4,0,0.6-0.1,0.8-0.3c0.1-0.1,0.2-0.2,0.3-0.4c0-0.1,0.1-0.1,0.1-0.2c0.2-0.5,0.2-1.1,0.3-1.1c0,0,0,0,0.1,0
	c0,0,0,0,0,0c0.1,0,0.1,0.1,0.2,0.3c0,0,0,0,0,0c0.9,1.2,2.1,1,2.9,0.4c0.1-0.1,0.2-0.2,0.3-0.3c0.7-0.9,0.8-2.3-1-3c0,0,0,0,0,0
	c-1.3-0.4,0.9-1.3,0.5-2.1c-0.4-1.1,0.3-2.4,1.4-3.5c1-1.1,2.4-2,3.2-2.3c0,0,0.1,0,0.1,0c1-1.1,1.8-1.8,2.4-2.3
	c1.3-0.9,1.9-0.7,2.5-0.5c0.7,0.3,1.3,0.7,2.9-0.2c0.7-0.4,1.6-1.1,2.7-2.1c1.4-1,2.8-1.4,4.1-1.8c1.8-0.6,3.4-1.1,4.5-3.3
	c0,0,0,0,0,0c0.3-0.5,0.5-0.5,0.7-0.2c0,0,0,0.1,0.1,0.1c0.1,0.1,0.1,0.3,0.2,0.5c0,0.1,0,0.3,0,0.4c0,0.1-0.1,0.3-0.1,0.4
	c-0.1,0.2-0.3,0.4-0.5,0.4c-0.5,0.2-0.5,0.4-0.2,0.6c0.1,0.1,0.3,0.2,0.5,0.3c0.6,0.3,1.7,0.5,2.7,0.5c0.3,0,0.6,0.1,0.8,0.1
	c0.5,0.3-1.2,0.3-3.2,0.7c0,0,0,0,0,0c-2.3-0.3-4.1,1.1-5.2,2.9c-1.8,1.8-1.6,3.3-0.6,4.2c0.4,0.6,1,0.9,2.1,0.7
	c1.1,0,2.4-0.5,3.3-1.6c0,0,0,0,0,0c0.9-0.8,1.4-1.6,1.8-2.3c0.5-0.7,0.9-1.1,1.9-1.1c0,0,0,0,0,0c0.8-0.2,2.9-0.5,4.9-1
	c2.9-0.7,5.7-1.7,4.7-3.6c0,0,0,0,0,0c0-0.3,1-0.6,2.2-1c1.1-0.2,2.3-0.5,2.9-0.9c0.1-0.1,0.2-0.2,0.2-0.3c0.3-0.2,0.4-0.4,0.2-0.6
	c-0.2-0.7-2.1-0.3-2.5-1.4c0.1-0.6,0.4-1.5,0.5-2c0,0,0,0,0,0c0,0,0,0,0,0c-0.1-0.1-0.1-0.1-0.2-0.2c-0.5-0.4-1.2,0.2-1.8,1
	c-1.1,1.1-2.2,2.8-2.6,3.2c0,2.1-0.3,1-1.6,1.7c-4,1.1-7.4-1.1-10-3.7c-0.4-0.4-0.7-0.8-1.1-1.2c-0.1-1.3-1.2-1.9-1.1-2.9
	c0-0.3,0.2-0.7,0.6-1.2c1.5-1.9-0.3-2.8-1.7-1.3c-1.5,1.4-2.3-0.8-0.3-1.6c2-0.8,5.7-1.6,3.8-5.2c-0.4-0.8-1.4-1.2-2.6-1.3
	c-2.7-0.6-6,0.3-9.2,2c-2.1,0.8-3.9,1.6-4.8,2.2c-1.3,1.3-2.5,2.3-3.4,3.2c-2,1.6-3.7,3.1-4.8,4.1c-0.5,0.2,0-0.5,1-1.8
	c0.9-0.8,2-1.7,2.3-2.2c0.1-0.2,0.2-0.5,0.3-0.7c0.7-0.7,1.4-1.5,2.1-2.4c1.6-1.5,3.3-3.7,5.5-4.8c1.2-0.1,2.1,0.2,2.3-0.6
	c0,0,0,0,0,0c0,0,0,0,0-0.1c0.4-2.4,3.3-3.7,7-4.4c7-1.2,17.1-0.1,20-0.4c0,0,0,0,0,0c4.8-0.1,6.4-6.5,11.2-6.2c0.2,0,0.3,0,0.5,0.1
	c0.2,0,0.4,0,0.5,0.1c0,0,0,0,0,0c1.3,0.2,3.3-1,4.6-2.3c1.2-1,1.9-2.2,0.9-2.7c-0.1-0.1-0.3-0.1-0.5-0.2c0,0,0,0,0,0
	c-0.3-0.1-0.5-0.2-0.8-0.3c-0.5-0.2-0.9-0.5-0.8-0.7c0-0.1,0.2-0.2,0.5-0.2c2.6-0.8,3.5-3.1,0.3-5.6c-1.2-1.7-2-0.4-3,0.2
	c-0.3,0.2-0.7,0.4-1.1,0.3c-0.3,0-0.5-0.1-0.5-0.3c0-0.1,0-0.2,0-0.3c0.1-0.4,0.4-0.9,0.5-1.4c0.2-0.5,0.2-0.9-0.3-1.2c0,0,0,0,0,0
	c-0.4-0.6-1.2-0.6-2.2-0.3c-1.8,0.3-3.9,1.8-5.7,2.8c0,0,0,0-0.1,0c-0.6,0.3-1.2,0.6-1.7,0.7c0,0-0.1,0.1-0.1,0.1
	c-1.2,0.5-1.8,0.2-0.8-1.7c0,0,0,0,0,0c0.3-0.1,0.5-0.2,0.8-0.4c1.8-0.7,5.4-1.9,7.6-3c0.6-0.2,1.1-0.5,1.6-0.6
	c0.1-0.2,0.2-0.5,0.1-0.7c0.1-0.2-0.1-0.4-0.4-0.6c-0.5-0.5-1.5-0.8-2.5-0.5c0,0,0,0,0,0c-1.3,0.3-2.3-1.1-3.2-1.7
	c-0.4-0.3-0.8-0.4-1.2-0.1c-0.2,0.2-0.4,0.2-0.5,0.2c-0.6,0-0.7-1.4-1.8-0.7c0,0,0,0,0,0c-0.9,0.2-1.6-0.3-2.4-1.1
	c-0.1-0.3-0.2-0.5-0.3-0.9c0.2-0.7-1-1.6-2.4-2.7c-1.1-1.3-2.3-2.3-3.9-2.5c-1.3-0.9-1.7-1.4,0.2-1.1c0,0,0,0,0,0
	c1,0.6,2.2-0.2,2.7-1.2c0.5-0.9,0.5-1.9-0.6-2.3c-0.2,0-0.3-0.1-0.5-0.1c-0.5-0.1-0.6-0.7-0.6-1.5c0-0.2,0-0.5,0.1-0.7
	c0.1-0.5-0.6-1-1.2-1.5c-1-1.3-2-2.5-2.9-4c-0.3-0.6-0.6-1.3-1-2c-0.1-0.3-0.2-0.5-0.3-0.8c-0.2-0.3-0.3-0.6-0.5-0.9
	c-1.3-2.7-2.8-5.6-3.9-6.8c-0.1-0.1-0.2-0.3-0.2-0.4c-0.4-1.1-0.9-1-1.3,0c-0.2,0.3-0.3,0.7-0.4,1.3c-0.1,0.5-0.3,0.9-0.6,1.2
	c-0.7,0.9-1.6,1-1.2,1.5c0.3,0.4,0.4,0.6,0.5,0.9c0.2,0.7-0.4,0.9-1,1.5c-0.4,0.5-0.2,1.1-0.2,1.6c0,0.1,0,0.2,0,0.3
	s-0.1,0.2-0.1,0.2c-0.1,0.1-0.1,0.1-0.3,0.1c-0.1,0-0.1,0-0.2,0c-0.2,0-0.3,0-0.4,0.1c-0.1,0-0.1,0.1-0.1,0.1
	c-0.6,0.7-0.3,2.3-0.8,1.3c0,0,0,0,0,0c-0.3-0.3-0.5-0.5-0.7-0.6c-1.6-0.6-2.5,3.6-5,3.4c-0.1,0-0.3,0-0.4-0.1
	c-0.9-0.1-1.1-1.7-1.6-1.9c-0.1-0.1-0.3,0-0.4,0.1c-0.9,0.7-0.8-2.1-2.2-2c-1.5,0.1-2.5-0.1-2.1-1.4c0.1-0.3,0-0.5-0.1-0.7
	c-0.1-0.1-0.2-0.3-0.3-0.4c-0.1-0.1-0.2-0.2-0.3-0.3c-0.7-0.5-1.4-1-0.9-1.4c0,0,0,0,0,0c0.8-0.9-1.1-6.2,0.5-7.7
	c0,0,0.1-0.1,0.1-0.1c0.1-0.1,0.2-0.1,0.2-0.2c0-0.8-0.8-0.9-1.8-0.4c-0.4,0.1-0.8,0.1-1.3,0c-0.3-0.3-0.5-0.7-1.1-0.7c0,0,0,0,0,0
	c0,0,0,0,0,0c-0.5,0-1.6,0-2.5-0.2c-0.8-0.3-1.5-0.9-2.1-1.5c-0.1-1-0.6-1.1-1.1-1.4c-0.2-0.3-0.5-0.6-0.7-0.9
	c-0.2-0.6-0.5-0.8-0.8-0.8c-0.5-0.1-1.1,0.1-1.1-0.6c0,0,0,0,0,0c0-0.9-0.4-1.3-1.1-1.5c-0.6-0.5-1.5-0.9-2.4-1.1
	c-0.9-0.3-1.7-0.3-2.4-0.1c-0.5,0.1-1,0.2-1.5,0.4c-0.8,0.2-1.7,0.4-2.8,0.3c-2.1-0.5-4.8-1.6-7-1.8c-1.4-0.3-2.6-0.3-2.7,0.4
	c-0.5,0.3-0.8,0.6-1.1,1.2c-0.4,0.5-0.5,1-0.5,1.5c0,0.1,0,0.2-0.1,0.3c0.1,0.1,0.1,0.2,0.2,0.3c0.3,0.7,0.9,1.3,1.7,2
	c0.3,0.3,0.4,0.5,0.5,0.6c-0.6,1.1-1,1.9-1.3,2.5c-0.2,0.4-0.5,0.8-0.8,1.3c0,0.1,0.1,0.3,0.2,0.4c0,0.3,0.2,0.5,0.5,1
	c0.4,0.6,0.8,1.4,1.2,2.1c0.3,0.6,0.6,1.4,0.9,2.4c0,0.2,0,0.4,0,0.5c0,0.6-0.3,1.1-0.9,1.3c-0.2,0-0.4,0.1-0.6,0.1
	c-0.4,0.1-0.4,0.4-0.4,0.7c-0.6,1.1-1.1,1.9-1.4,2.7c-0.5,0.5-0.6,1.1-0.6,1.6c-0.2,1.2,0.3,1.7,1.5,2.6c1.5,1.7,3.8,3.6,5.1,5.9
	c0.7,2.3,0.8,4.2,0.4,5.8c-0.7,1.7-2.1,3.6-4.6,5.7c-2,1.2-4.1,2.1-5.4,3c-1.5,0.2-2.3,0.3-1.4,2.1c0.1,0.5,0.4,1.2,1.2,1.9
	c0.1,0.2,0.2,0.4,0.3,0.5c1,2,0.7,4.1,1.1,6.1c0.1,0.7,0.3,1.4,0.7,2.1c0,0,0,0,0,0.1c0.1,0.1,0.1,0.2,0.1,0.2
	c0.2,0.3,0.3,0.6,0.4,0.8c0.2,0.9-0.4,1.5-1.4,2.4c-0.1,0.1-0.1,0.1-0.2,0.2c-0.2,0.2-0.2,0.3-0.3,0.4c0,0.1,0,0.1,0.1,0.2
	c0.2,0.2,0.9,0.3,1,1.3c0,0.2,0,0.3,0,0.5c-0.1,0.8-0.7,0.1-1.7-0.6c0,0,0,0-0.1,0c-1.2,0-1.5,0.5-1.7,1.1c-0.4,1-0.3,2.2-2.8,0.7
	c-0.3-0.2-0.5-0.3-0.8-0.5c-0.6,0.1,0.2-1.5-1.4-2.8c-0.1,0-0.1-0.1-0.2-0.1c-2.8-2-3.4-3.8-5.2-6.2c0.2-3.9,0.2-6.5,0-8.3
	c0.1-2.1-0.4-3.1-1.6-3.5c-2-1-6.1,0.7-13.6-2.8c-3.4-1.9-6.7-4.4-9.6-6.3c0,0,0,0,0,0c-0.3-0.1-0.6-0.2-0.8-0.4
	c-0.7-0.3-1.5-0.5-2.3-0.7c-1.9-0.5-3.9-1-5.8-1.9c-0.2-0.1-0.3-0.1-0.5-0.2c-0.9,0.1-2.4,0.6-3.7,1.1c-1,0.4-1.8,0.7-2,0.7
	c0.2-1.3,0.4-2.2,0.2-3.3c-0.2-0.9-0.6-2.1-1.4-3.8c0,0,0-0.1,0-0.1c0-2-1.2-2.8-2.5-3.2c-1.6-0.5-3.6-0.4-4.6-1.3
	c-1.4-3.8,0.5-14.8,5-16.6c0.1,0,0.1-0.1,0.2-0.1c0.8-0.2,0.7-1.3,0.8-2.2c0.1-0.5,0.2-0.9,0.7-1c1.3-0.2,1.4-0.7,1.6-1.8
	c0.3-1.1,1.8-0.4,0.9-1.3c-0.9-0.9-0.2-1.2,1.8-1.2s4.9-0.7,4.5-2.8c-0.4-2.1-5.6-0.5-5.6-2.2c0-0.5-0.6-0.9-1.5-1.2
	c-0.3-0.1-0.7-0.2-1.1-0.3c-0.2,0-0.4-0.1-0.6-0.1c-1.6-0.4-3.1-0.6-2.9-1.1c0,0,0.1,0,0.1-0.1c0.3-0.2,0.8-0.3,1.3-0.3
	c0,0,0.1,0,0.1,0c0.1,0,0.2,0,0.3,0c2,0.2,4.9,1.5,7.4,2.2c1.3,0.4,2.5,0.6,3.4,0.5c1.3-0.2,2.1-1.1,1.6-3.5c2.6,0.7,4.3,1,5.6,0.6
	c0.1,0,0.1,0,0.2,0c0.1,0,0.2-0.1,0.2-0.1c0.1,0,0.2-0.1,0.3-0.1c0,0,0,0,0.1,0c1.5-0.7,2.7-2.4,4.8-5.2c0.5-0.4,0.7-0.7,0.7-1
	c-0.1-0.3-0.4-0.6-0.9-0.7c-0.6-0.2-1.6-0.3-2.8-0.2c0,0,0,0-0.1,0c-1.5,0.4-4-0.5-5.8-1.6c-0.9-0.6-1.6-1.3-1.8-1.8c0,0,0,0,0,0
	c0-0.1,0-0.1,0-0.2c0-0.4,0.3-0.8,1.3-0.9c1.9,1,3.5,2,4.9,2.6c0.1,0.1,0.2,0.1,0.3,0.1c0.1,0,0.2,0.1,0.3,0.1
	c0.2,0.1,0.5,0.2,0.7,0.2c0,0,0,0,0,0c2.4,0.7,4.5,0.1,7.2-3.6c0.3-0.2,0.4-0.4,0.4-0.5c0,0,0-0.1,0-0.1c0,0,0-0.1,0-0.1
	c-0.2-0.6-1.9-1-2.6-1.6c0,0,0,0,0,0c-0.1-0.3,0-0.6,0.2-0.8c0.4-0.2,1.1-0.4,1.8-0.5c0.5,0,1.1,0.1,1.7,0.2
	c0.7,0.4,1.2,0.8,1.7,1.1c0.1,0.2,0.2,0.3,0.2,0.5c0.2,0.3,0.8,0.2,1.4,0.1c0.6,0,1.5-0.1,2.7-0.5c0,0,0,0,0,0
	c0.2,0,0.4-0.1,0.4-0.2c0,0,0-0.1,0-0.1c0,0,0,0,0,0c0-0.4-0.5-1.1-1.3-1.7c-0.4-0.3-0.8-0.6-1.3-0.8c-2-0.8,0.1-2.3,0.7-1.1
	c0.1,0.3,0.3,0.5,0.6,0.7c0.7,0.5,1.6,0.6,2.1,1.7c0.6,1.3,1.8,1.1,3.1,0.4c1.4-0.7,2.9-2.1,4.2-3.3c0.7-0.3,1.2-0.7,1.6-1.2
	c0.1,0,0.1-0.1,0.2-0.1c0.4-0.7,0.3-1.3,0.1-2c-0.1-0.6-0.5-1.1-1-1.7c-0.5-0.7-1.2-1.4-1.7-2.1c0-0.6,0.1-1.1-0.6-1.5c0,0,0,0,0,0
	c-1.9-1.5-0.9-1.6,0.6-1.7c2.4-0.1,3.2-0.6,3.3-1.2c0-0.1,0.1-0.2,0.1-0.2c0.4-0.6-0.2-1-0.8-1.3c-0.3-0.4-0.6-0.8-0.5-1
	c0.1,0,0.1-0.1,0.2-0.1c1.5-0.9,0.4-1.7-1.1-1.8c-0.8-0.2-1.7-0.3-2.3-0.7c-0.5-0.4-0.8-0.9-0.8-1.5c0,0,0,0,0,0c0,0,0,0,0,0
	c0,0,0,0,0,0c-0.4-0.5-1.5-0.8-2.8-1.1c-1.9-0.5-4.3-0.9-6.1-0.7c-0.7,0-1.2,0-1.6,0c-1.1,1.2-1,2.1-0.4,2.8c0.2,0.3,0.4,0.7,0.7,1
	c0.2,0,0.4,0.1,0.7,0.2c0.8,0.6,1.3,1.1,0.5,1.8c-1.1,0.4,0.2,1.9-0.7,1.8c-0.9-0.1-2.2-0.5-2.5,1.8c-0.3,2.3-0.4,3.2-1.9,4
	c-0.6,0.4-0.8-0.1-0.9-0.7c-0.1-0.8-0.1-1.8-0.9-1.8c-1.2,0.7-1.4,1.3-1.2,2c0.2,0.8,1.1,1.5,1.7,2.2c0.2,0.4,0.4,0.8,0.4,1.2
	c0,0.3-0.3,0.7-0.7,1.1c-0.6,0.1-1.5,0.2-2,1.1c0,0,0,0,0,0c-0.3,0.7-1.4-0.4-2.5-1.8c-1-1.4-2-3.1-2.2-3.9c0-0.1,0-0.2,0-0.3
	c0.2-1.1,0.8-1.4,1.2-1.7c0.4-0.2,0.8-0.4,0.7-1.2c0-0.1,0-0.2-0.1-0.4c0,0,0,0,0-0.1c0-0.1-0.1-0.3-0.1-0.4c0,0,0-0.1,0-0.1
	c0,0,0-0.1,0-0.1c-0.1-0.2-0.1-0.3-0.2-0.5c-0.5-0.6-0.9-1.2-1.3-1.7c-0.6-0.7-1.2-1.3-1.7-1.7c-1.1-0.9-1.9-0.9-2.6-0.4
	c-0.6,0.4-1.3,1.3-1.9,2.7c-0.5,0.9-0.9,1.9-1.5,3c-0.2,0.3-0.4,0.5-0.5,0.5c-0.1,0-0.2-0.1-0.3-0.3c-0.1-0.5-0.1-1.7-0.4-3
	c-0.1-0.8-0.2-1.4-0.5-1.6c0,0,0,0,0,0c0,0,0,0,0,0c-0.4-0.2-0.6-0.5-0.5-0.8c0.1-0.3,0.6-0.6,1-0.8c0,0,0,0,0,0
	c0.6-0.1,1.2-0.5,1.4-0.9c0.1-0.1,0.2-0.3,0.1-0.4c0-0.2-0.2-0.3-0.5-0.3c0,0,0,0,0,0c-0.1,0-0.1,0-0.2-0.1c-2.3-0.7-2.9-1.3-4-1.3
	c-0.6,0-1.3,0.1-2.5,0.5c0,0-0.1,0-0.1,0c-0.2-0.2-0.3-0.4-0.3-0.7c-0.1-0.6,0.2-1.3,0.3-1.9c0.1-0.3,0.2-0.6,0.2-0.9
	c0.1-1,1.4,0.5,2.2-0.8c0.2-0.2,0.2-0.4,0.1-0.6c-0.3-0.7-2.5-0.9-2.5-2c0,0,0,0,0,0c0-0.7-0.7-1.1-1.5-1.4
	c-0.8-0.4-1.6-0.5-1.9-1.1c0-0.8,0-1.6-0.1-2.3c0.1-0.3,0.1-0.6,0.2-0.9c0-0.5-0.5-1.1-1.2-1.5c-0.7-0.9-1.9-1.6-3.8-2.1
	c0,0,0,0,0,0c-0.6-0.4-1.1-0.7-1.2-1.1c0.1-0.2,0.3-0.4,0.6-0.7c0.5-0.1,1.7-1.1,2.6-2.2c0.8-0.7,1.3-1.3,1.1-1.9
	c0.1-0.4-0.1-0.6-0.6-0.7c0,0,0,0,0,0c-0.4-0.4-0.7-0.6-0.7-0.8c-0.1-0.4,0.7-0.5,1.8-0.4c2,0.3,5,1,5.2,0.4c0,0,0,0,0,0
	c0.6-0.5,2.2-2.8,3.5-4.8c0.1-0.2,0.3-0.4,0.4-0.6c0.3-0.4,0.5-0.8,0.8-1.2c0.4-0.5,0.7-1,0.9-1.4c0.4-0.6,0.5-1,0.3-1.3
	c-0.4-0.6-2.2-0.7-4.3-0.5c-2.7-0.5-6.2-1.3-9.3-1.3c-2-0.1-3.7,0.1-4.4,0.8c-0.2,0.1-0.5,0.3-0.7,0.4c-0.4,0.4,0,0.8,0.4,1.3
	c0.1,0.3,0.3,0.7,0.6,1.1c0,0,0,0,0,0c-0.6,0-1,0-1.4-0.1c-0.4-0.2-0.8-0.3-1.1-0.2c0,0-0.1,0.2-0.1,0.4c0,0.1,0,0.3,0,0.5
	c-0.1,1,0,2.5,0.1,4.1c0,0.6,0.1,1.3,0.3,1.9c0.2,0.8,0.4,1.4,0.7,1.8c0.5,1.1,1,2.2,1.3,3.2c0,1.2-0.2,2.4-0.8,3.2
	c-0.4,0.3-1,0.7-1.7,0.9c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0c0.6,1,1,1.7,1.2,2.2c0.1,0.6-0.5,0.5-1.3,0.4
	c-0.5-0.1-1.1-0.3-1.9-0.5c-0.2,0.1-0.3,0.2-0.4,0.4c-0.4,0.1-0.7,0.5-0.8,1.1c-0.3,0.7-0.1,1.3,0.2,1.9c0.1,0.3,0.2,0.6,0.3,1
	c0.2,0.1,0.4,0.3,0.6,0.4c0.3,0.5,0.5,1.1,0.1,1.6c-0.8,1-1.1,1.8-1,2.5c0,0.6,0.3,1.3,1.3,2.1c0.9,0.8,2.3,1.2,3.8,1.5
	c1.5,0.5,2.9,0.8,3.8,1.3c0.3,0.3,0.5,0.6,0.4,1.1c-0.3,0.1-0.7,0.4-0.4,1c0.8,1.5-1.3,2.1-1.2,3.2c0,0.4,0.2,0.6,0.4,0.6
	c0.3-0.1,0.9-0.8,1.5-2c0,0,0,0,0,0c0.4-0.7,0.8-0.7,1.2-0.4c0.3,0.4,0.4,1.1,0.5,1.8c0,0.5-0.2,1-0.5,1.5c-0.3,0.3-0.6,0.7-1,1.1
	c-0.3,0.2-0.7,0.5-1.1,0.7c-0.2,0.4-0.5,0.7-0.8,0.9c-0.3,0.2-0.7,0.4-1,0.5c-0.4,0.1-0.7,0.2-1,0.2c-0.3,0-0.6,0-0.9-0.1
	c-0.8,0-1,0.4-0.9,1c-0.1,0.5,0.1,1.3,0.8,2.7c0.2,0.5,0.3,1,0.2,1.4c-0.2,0.5-1,0.6-1.9,0.4c-0.1,0-0.2-0.1-0.3-0.1c0,0,0,0,0,0
	c-0.8-0.3-1.7-0.8-2.1-1.4c-0.2-0.8,0.2-1.8,0.6-2.6c0.7-1.1,0.7-1.7,0.4-2c-0.1-0.2-0.2-0.3-0.4-0.4c-0.1,0-0.1,0.1-0.2,0.1
	c-0.6-0.1-1.4-0.1-2.2-0.3c-0.9-0.3-1.8-0.9-2.1-1.3c0-0.1-0.1-0.1-0.1-0.2c-0.1-0.3,0.2-0.5,0.9-0.5c0.5,0.1,1.2,0.2,2,0.3
	c0.4,0.1,0.7,0,0.9,0c0.8-0.3-0.2-1.6,1.2-1.6c0,0,0,0,0,0c1,0.4,1.9,0.5,2.6,0.3c0.6,0,1.2-0.1,1.8-0.7c0.1-0.3,0.2-0.5,0.2-0.7
	c0.1-0.3,0.2-0.6,0.2-0.9c0-1-0.5-1.3-1.3-1.5c-0.3-0.2-0.7-0.3-1.2-0.4c-0.5,0.2-0.8,0.3-1.2,0.4c-0.7,0-1.3,0-1.6-0.2
	c-0.4-0.3,0-0.9,0.9-1.1c0.6,0,1.4,0.2,1.9,0c0,0,0,0,0,0c0,0,0,0,0,0c0.3-0.5,0.2-0.8,0-1c-0.2-0.2-0.7-0.3-1.3-0.3
	c-1.6-0.1-4.1,0.3-5-0.7c-0.1-0.1-0.2-0.2-0.2-0.4c0,0,0,0,0,0c0,0,0,0,0,0c0,0-0.1-0.1-0.1-0.1c-5-4.3-2.9,4.1-8.3,4
	c-0.5,0.5-0.3,0.8,0.2,1.1c0.8,0.5,2.4,0.9,3.9,1.3c1.1,0.3,2.1,0.6,2.5,1.1c0.2,0.2,0.2,0.4,0,0.7c0,0,0,0,0,0c0,0,0,0.1-0.1,0.1
	c0,0-0.1,0.1-0.2,0.2c-0.4,0-0.6,0.3-0.8,0.6c-0.3,0.2-0.6,0.3-1,0.5c0,0,0,0,0,0c-1.1-0.6-2.3-0.1-1.6,0.9c0.1,0.5,0.2,1.1,0.2,1.6
	c-0.2,0.3-0.4,0.7-0.3,1.1c0,0-0.1,0-0.1,0c-1.3,0.5-4.3,0-7.2,0.1c-1.2,0-2.4,0.2-3.4,0.6c-0.1,0.1-0.3,0.1-0.4,0.2
	c-2.2,0.6-3.6-0.1-4.9-0.8c-0.5-0.3-1-0.6-1.4-0.9c-1.1-0.6-2.2-1.1-3.8-0.8c0,0,0,0,0,0c-0.6,0-1-0.2-1.3-0.5
	c-0.1-0.1-0.2-0.2-0.3-0.3c-0.1-0.1-0.2-0.2-0.3-0.3c-0.3-0.3-0.8-0.6-1.6-0.6c-0.7,0-1.1-0.2-1.4-0.6c-0.1-0.2-0.2-0.4-0.3-0.6
	c-0.2-0.7-0.1-1.5-0.3-2.1c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0c-0.3-0.8-1.9-0.9-3.9-0.5c-3.1,0.5-6.9,2-8.1,3.4
	c0,0.1,0,0.1-0.1,0.2c-0.1,0.2-0.2,0.4-0.1,0.6c-0.1,0.8,0.6,1,1.6,0.9c0.1,0,0.2,0,0.3,0.1c0.4,0,0.6-0.1,0.8-0.3
	c0.8-0.2,1.8-0.6,2.8-1c1.4,0.2,2.6-0.8,3.6-1.4c0.8-0.3,1.4-0.4,1.5-0.2c0,0,0,0,0,0c0,0,0,0,0,0c0,0.4-0.2,0.8-0.4,1.1
	c-0.5,0.4-1.2,0.8-2,1.1c-1.2,0.4-2.6,0.7-3.6,1.2c-0.1,0-0.2,0-0.3,0.1c-1.1,0.2-1.5,0.7-1.4,1.6c-0.2,0.9,0.3,2.1,1.9,3.9
	c2.1,3.3-0.9,1.4-0.9,3c0,0.4-0.3,0.6-0.6,0.6c-0.2,0-0.4,0-0.6-0.1c-0.1,0-0.2-0.1-0.3-0.1c-0.3-0.1-0.6-0.3-0.9-0.5
	s-0.4-0.4-0.4-0.6c0-0.1,0-0.2,0.1-0.3c0.1,0,0.1-0.1,0.2-0.1c0.4-0.1,0.6-0.3,0.7-0.5c0.5-0.5,0.3-1.1-0.2-1.7
	c-0.2-0.4-0.6-0.8-1-1.1c-0.5-0.4-0.9-0.5-1.2-0.5c-0.4-0.3-0.7-0.5-0.7-0.6c0,0,0,0,0,0c-0.3-0.3-0.6-0.5-1-0.7c0,0,0,0,0,0
	c-0.1,0-0.2-0.1-0.2-0.1c-5-2.3-15.5,1.9-21.4,0.3c-0.1,0-0.2-0.1-0.3-0.1c-0.1,0-0.2-0.1-0.3-0.1c-0.3-0.1-0.6-0.3-0.9-0.4
	c-0.1-0.1-0.2-0.1-0.3-0.2c0,0,0,0,0,0c-0.1-0.4-0.1-0.7,0-1c0.3-0.5,0.9-0.9,1.5-1.3c0.9-0.3,2.1-0.5,2.8-0.8c0,0,0.1,0,0.1,0
	c0.6-0.2,0.7-0.5,0.5-0.9c-0.1-0.5-0.7-1.1-2-2.1c-1.9-1.2-3.1-1.7-4-1.8c-1.3-0.4-2.3-0.5-2.3,0.2c-0.6,0.1-1.3,0.3-2.2,0.2
	c-1-0.2-2.1-0.5-3.4-0.7c0,0,0,0,0,0c-2.6-1.2-4.4-1.5-6.2-1.8c-2.5-1.1-5-2.3-7.5-3.1c-2.6-1.6-7.6-1-8.2,0.7c0,0,0,0,0,0
	c-0.5,1.3-1.7,1.6-3.1,1.7c-0.5,0-0.9,0-1.4,0c-0.4,0-0.5-0.1-0.6-0.2c-0.4-0.6,1.3-2.1,0.8-2.4c-0.2-0.5-0.3-0.9-0.4-1.3
	c0-0.8,0-1.7-0.8-1.9c-0.1,0.1-0.1,0.2-0.2,0.3c-0.8-0.3-1.4,1.6-2.3,3.2c-3.6,4.5-4.3,0.9-8.6-5.8c-1.5-1.1-2.9-1.5-3-0.8
	c-0.1,0.3,0.1,0.8,0.7,1.6c1.8,2.4-1.5,0.7-1.4,2.3c-0.2,0.6-0.4,1.1-0.6,1.5c-0.4,0.5-1,0.9-1.4,1.2c-0.3-0.1-0.6-0.3-0.9-0.5
	c-0.2-0.6-0.6-0.7-1.2-0.4c-0.7,0-1.4,0.3-2.5,1.5c-0.1,0.1-0.1,0.1-0.2,0.2c-0.7,0.6-1.5,1.1-2.1,1.4c-0.5,0.1-0.9,0.1-1.4,0
	c-0.1-0.1-0.1-0.2-0.1-0.4c-0.1-0.3-0.8-0.1-1.8,0.3c-0.8,0.1-1.8,0.5-3,1.7c-0.8,0.6-1.3,1.1-1.2,1.5c0,0,0,0,0,0c0,0,0,0,0,0
	c0.1,0.3,0,0.5-0.1,0.7c-0.2,0.3-0.5,0.6-0.9,0.8c-0.4,0.2-1,0.3-1.6,0.2c0,0,0,0,0,0c-0.5-0.7-0.2-1.4,0.5-2.2
	c0.7-0.6,1.6-1.2,2.6-1.8c2.2-1.2,5-2.3,7.3-3.4c2.1-0.7,3.2-1.4,3.5-1.9c0.2-0.2,0.4-0.4,0.6-0.6c-1.1-0.8-2.8-0.7-4.6-0.2
	c-1.5,0.3-3.2,1-4.8,2.2c0,0,0,0,0,0c-1.6,0.7-3.3,0.8-4.1,1.2c-0.3,0.2-0.4,0.5-0.2,0.9c0.7,1.4-2.1-0.2-2.7,0.8
	c-0.6,1-1.7-0.1-0.9-0.6c0.9-0.5-2.1-1.5-2.1-0.9c0,0.2,0,0.3-0.1,0.5c-0.1,0.2-0.4,0.5-0.7,0.7c-0.4,0.2-0.9,0.3-1.7,0.3
	c-5.2,0.7-2.3,2.4-1.4,4c0,0,0,0,0,0c0,0,0,0,0,0c0.1,0.2,0.2,0.5,0.2,0.7c0,0.1,0,0.1,0,0.2c-0.9-0.3-3.7-0.9-6.6-1.8
	c-3.8-1.1-7.8-2.6-8.2-4.2c0,0,0,0,0,0c0-0.5-1.6-0.4-3.6-0.5c-0.4,0-0.8,0-1.2,0c-0.4,0-0.8,0-1.2,0c-0.4,0-0.8,0-1.1-0.1v0
	c-8.4-3.4-14.7-2.2-23.3-3.3c-2.8,0.5-4.6-1.6-8.2-2.5c-5.9-1.4-9,1.9-12.3-0.4c-1.5,0.2-0.3-0.7-1.2-2.1c-1.3-2.2-8.6,2.3-8.6-1.5
	c-0.8-1.9-3.4,3.2-5,1.1c-0.7-0.7,1.5-1,1.5-1.8c-1.9-2.1-4.4-2.1-7.1,1.1c-2.6,3-8.3-0.3-9.7,2.5c0.1,1,2.1,0.9,0.9,2.1
	c-1.2,1.3-1.6-1.7-2.8-0.4c-1.2,1.3-4.8,1.3-5.7,1.1c-7.4,3.9-3.1,12-15.9,10.3c-4.9,5.2,8.6,5,9.6,12.4c0.4,1.2,4.8-0.1,6.5,0.2
	c1.7,0.3-0.2,2.8,1.3,3.5c1.5,0.7,3.7-0.4,4.1,0.9c0.4,1.3-2.9,0-4.6,1.3c-1.7,1.3-2.6,2-3.3,1c-1.6-1.8-7.6,2-5-3
	c0.6-2.3-8,0.6-6.9,2.6c-0.3,1.3-1.5-0.8-3.3,0.4c-5.9,2.5-6.9,4.4,0.3,5.5c1.7,0.4-2.1,1.5-0.7,2c1.7,3.7,8.1,3.9,11.1,3
	c1.3,0,2.5,1.6,3.5,0.4c1-1.2,4.9-4.4,6.9-2.5c2,1.9-2.1,1.6-1.1,2.8c1,1.2,2.3,3.9,0.4,5.4c-1.9,1.4-3.7,0.4-4.7,0.4
	c-2.4,3.8-1.5,4.6-6.9,1.9c-2.1,0-1.6,2.8-1.5,4.1c0.1,1.3-3.1-0.3-3.9,3c-0.8,3.2-4,0.5-1.9,3.6c2.1,3.1,1.8,1.2,3.5,2.4
	c-0.2,7.1-1.9,0.2,4.6,8.6c1.2,1.2,2.7-0.6,4.5-0.7c1.8-0.1,0.9-2.7,2-1.7c3.9,2.6-2.4,13,5.4,8.1c1.7-4.3,7.8,4.3,7.7,1.3
	c1.5-5.8,1.1,1.5,5.8-1.6c2.7-1.3,1.5,0.1,0.1,1.9c-1.9,2.5,0,6.1-1.4,6.4c-2.1,3-9.4,6.6-11.2,9.7c-6-0.6-6.1-0.1-8.8,5
	c2.4,1,5-4.6,6-2.7c1.7,1.1,3.9-2.6,6-1.4c0.9-0.7,6-1.6,5.7-3.8c1.9-2.5,7.5-3.7,8.6-6.4c1.9-0.2,4.9-4.1,7.2-3.9
	c1.2-4.3,2.5-2.1,3.3-5c-3.2-2-4.9-1.8,0.1-4.5c2.5-0.1,4.7-6.7,7.1-7.8c1.1-0.1,2.1-1.7,3.3-2.4c1.2-0.7,0,0.9,2.3,1
	c2.3,0.1,1.8,2.3,0.1,1.5c-3.2-3.1-8.3,7.1-5.3,7.4c2.9,0.9-6,2.7,0.2,3.1c2.8-0.5,9.8-6.9,13.2-4.7c0.8-0.3-0.9-1.6,0.1-2.1
	c0.9-0.4-1.1-0.7-1.7-2.4c-0.6-1.7,0.9-0.6,1.3-2c0.3-1.3,1.3-0.8,2.1-0.2c0.9,0.6,2.1-0.6,2.8,1.1c0.8,1.7,1.6-0.4,3,0.7
	c1.4,1.2-0.3,1-1.7,1.3c-1.5,0.4-0.7,2.4,0.4,1.4c2.1-2.9,3.7,1.4,5.4-1c1.5-0.8-0.2,2.3,5.2,2.2c6,0.5,7.4-1.5,11.8,2.2
	c2.7,0.5,3.3-2.8,4.7-1.6c1.4,1.2,0,1.6-0.8,2.5c-0.8,0.9,0.9,1,1.9,1.4c4,0.9,4.9,3.7,9.1,6.2c3.7,1-2-5.1,5.3-0.3
	c3.3,3.5-2.4-9.3,1.9-3c1.7,1.9-0.1,9.1-2.6,4.7c0.3-0.9-2.6-0.5-2.7,0.7c-0.1,1.3,1.8,3.7,3.2,3.9c1.4,0.3,0.3,2.5,1.2,2.8
	c1.2,0.4,1.1,2.7,1.8,2.3c1.8-0.9-2.5-7.1,0.3-7.1c1,0.4,0,2.2,0.5,2.9c0.5,0.6,1.1-0.6,2.3-1.3c1.2-0.7-0.5-2.8-0.1-3.9
	c0.4-1.1,1.8,0.9,2.3,2.8c0.3,2.1-2,3.5-3.4,3.9c-0.5,0.5-0.3,4.1,0.4,4.2c0.7,0.1,1.1-3,1.5-1.4c0.4,1.5,2.1-2,2.5-0.5
	c0.4,1.4,2.5,3,3.3,2.7c1.6-1.4-4.9-2.7-1.5-4.2c4.9,2,2.7,9.1-2.3,4.4c-1.1,0.1,0,1.9,0.5,3.1c0.5,1.3-1.4,3,0.1,3.9
	c1.5,1,1-0.6,1.3-1.3c1.3-1,3.1,3.1,3-1.4c0-0.8,1.3-0.4,2,0.7c0.7,1.1,0.8-1.2,1.8-1c1,0.2,0.3,1.9,0.3,2.9c0,1,1.8-0.8,1.6,0.3
	c0,0.2,0,0.3,0,0.4c0,0,0,0,0,0c0.1,0.8,0.8,1.3,1.6,2.1c0.2,0.2,0.2,0.3,0.2,0.4c0,0.1-0.1,0.1-0.2,0.2c-0.1,0.1-0.3,0.2-0.5,0.2
	c-0.1,0-0.2,0.1-0.3,0.1c-0.4,0.2-0.6,0.4-0.3,0.7c0,0.1,0,0.1,0,0.2c0.3,2.1,1.1,5,4,2.7c0,0,0,0,0,0c0.1-0.1,0.1-0.1,0.2-0.1
	c0.5-0.3,0.9,0.2,1,0.9c0.1,0.5,0,1-0.3,1.5c-0.2,0.3-0.2,0.7-0.2,0.9c0.1,0.7,0.9,1,1.5,0.7c0.1-0.1,0.2-0.2,0.3-0.3c0,0,0,0,0,0
	c0.3-0.4,1.4,0.2,2.4,1.1c0,0,0.1,0.1,0.1,0.1c0.1,0.1,0.2,0.2,0.3,0.3c0,0,0.1,0.1,0.1,0.1c1,1,1.6,2.2,0.5,2.3
	c-2.6,1.2,0.7,2.4,0.8,4.1c0,0.2,0,0.3-0.1,0.5c0,0.2,0.1,0.3,0.3,0.5c0,0,0.1,0,0.1,0.1c0.1,0.1,0.2,0.1,0.2,0.2
	c0.2,0.2,0.5,0.3,0.9,0.5c0.9,0.4,2,0.6,2.6,0.4c1.1-0.3,2,0.8,2.8,1.7c0.2,0.3,0.5,0.5,0.7,0.7c0,0,0,0,0,0
	c0.1,0.1,0.2,0.2,0.4,0.3c0.6,0.4,1.2,0.5,2,0.1c0,0,0.1,0,0.1,0c0.6-0.5,1,0.4,1.3,1.4c0.1,0.3,0.1,0.7,0.2,1
	c0,0.3,0.2,0.5,0.4,0.6s0.5,0.1,0.8,0.1c0.2,0,0.4,0,0.6-0.1c0.4-0.1,0.9-0.2,1.2-0.2c1.2,0.5,2,0.8,2.7,1.2
	c0.2,0.1,0.3,0.2,0.5,0.3c0.8,0.6,1.4,1.6,2.1,3.4"
        />
        <MapPath
          initial={{ pathLength: 0 }}
          animate={mapPathAnimation}
          d="M183.6,250.3c1.4,1.8,2.2,8-0.1,8.8c-0.2,0-0.3,0-0.4-0.1c-0.6-0.2-0.8-0.9-0.6-1.4c0,0,0-0.1,0.1-0.1
	c0-0.1,0.1-0.2,0.1-0.2c0.1-0.1,0.2-0.2,0.3-0.2c0.5-0.5,0.6-0.9,0.6-1.2c-0.1-0.4-0.5-0.8-0.9-1.1c-0.3-0.2-0.6-0.5-0.8-0.8
	c-0.2-0.2-0.2-0.5-0.2-0.9c0,0,0,0,0,0c0.6-2.6,0.7-1.8-2-3.3c-4.8-0.9-5.4-5.6-9-7.4c-2.8,1.3-6.7-3.6-9.1-1.8
	c-1.2,0.8,1.3,1,1.2,1.9c-0.1,0.9,0.8,1.4,1.8,1.3c2.2,2,5.7,3.5,7.3,6.2c2,0.7,2.3,1.3,3.7,3.2c-0.1,0.3-0.1,0.8,0.1,1.5
	c0.4,1.4,1.5,3.5,2.5,5.3c0.9,1.6,1.6,3.1,1.6,3.7c0,0.3-0.1,0.4-0.5,0.3c-1.6,1.1-0.8,11-1.3,13.4c0,0.1,0,0.1,0,0.2
	c-0.3,1.7-2.1,4.5-1,6.3c2.6,4.7,0.4,12.2,2.8,16.8c0,0.1,0.1,0.2,0.1,0.3c0,0,0,0,0,0c0.5,0.4,1.2,1.2,1.9,2
	c1.1,1.3,2.3,2.8,2.9,3.7c0.1,0.7,0.1,1.2,0.3,1.5c0.2,0.4,0.5,0.7,1.3,1c0,0,0,0,0,0c0.4,0.8,0.6,1.4,0.7,2
	c-0.1,0.4-0.2,0.8-0.2,1.1c0,0.4,0.4,0.9,1,1.6c0.5,0.8,1.3,1.7,3.1,3c0.7,0.6,1,1.3,1.1,1.8c0.1,0.2,0.1,0.4,0.1,0.6
	c0,0.2,0,0.4,0,0.5c0,0.2,0,0.4,0.1,0.6c0.1,0.2,0.3,0.3,0.7,0.3c0.1,0,0.1,0,0.2,0.1c2.8,0.9,5.8,1.8,8.1,3.3
	c1.5,1,2.8,2.3,3.6,4.2c0.3,0.7,0.5,1.4,0.6,2.3"
        />
      </g>
      {/* CIRCLES */}
      <UkCircle
        initial={{ opacity: 0 }}
        animate={circleAnimation}
        $isInView={isInView}
        cx="650.7"
        cy="226.634"
        r="19.3333"
        fill="url(#paint0_linear_1956_47681)"
      />
      <UsCircle
        initial={{ opacity: 0 }}
        animate={circleAnimation}
        $isInView={isInView}
        cx="269.833"
        cy="295.267"
        r="71.5333"
        fill="url(#paint1_linear_1956_47681)"
      />
      <BeCircle
        initial={{ opacity: 0 }}
        animate={circleAnimation}
        $isInView={isInView}
        cx="672.933"
        cy="238.233"
        r="11.6"
        fill="url(#paint2_linear_1956_47681)"
      />
      <DeCircle
        initial={{ opacity: 0 }}
        animate={circleAnimation}
        $isInView={isInView}
        cx="699.999"
        cy="236.3"
        r="26.1"
        fill="url(#paint3_linear_1956_47681)"
      />
      <JpCircle
        initial={{ opacity: 0 }}
        animate={circleAnimation}
        $isInView={isInView}
        cx="1194.1"
        cy="296.9"
        r="26.1"
        fill="url(#paint4_linear_1956_47681)"
      />

      {/* LABELS */}

      {/* us label */}
      <motion.rect
        initial={{ opacity: 0 }}
        animate={labelAnimation}
        x="252.933"
        y="378.9"
        width="35"
        height="24"
        rx="12"
        fill="black"
      />
      <motion.rect
        initial={{ pathLength: 0 }}
        animate={labelBorderAnimation}
        x="252.933"
        y="378.9"
        width="35"
        height="24"
        rx="12"
        stroke="url(#paint5_linear_1956_47681)"
        fill="white"
        fillOpacity="0"
      />
      <motion.path
        initial={{ opacity: 0 }}
        animate={labelTextAnimation}
        d="M266.753 395.568C266.129 395.568 265.585 395.456 265.121 395.232C264.657 395.008 264.297 394.676 264.041 394.236C263.785 393.788 263.657 393.24 263.657 392.592V387H264.653V392.604C264.653 393.292 264.837 393.808 265.205 394.152C265.573 394.496 266.089 394.668 266.753 394.668C267.417 394.668 267.933 394.496 268.301 394.152C268.669 393.808 268.853 393.292 268.853 392.604V387H269.861V392.592C269.861 393.24 269.733 393.788 269.477 394.236C269.221 394.676 268.857 395.008 268.385 395.232C267.921 395.456 267.377 395.568 266.753 395.568ZM274.506 395.568C273.898 395.568 273.354 395.46 272.874 395.244C272.394 395.028 272.014 394.704 271.734 394.272C271.462 393.832 271.326 393.284 271.326 392.628V392.376H272.322V392.628C272.322 393.324 272.526 393.844 272.934 394.188C273.342 394.524 273.866 394.692 274.506 394.692C275.162 394.692 275.662 394.544 276.006 394.248C276.35 393.952 276.522 393.58 276.522 393.132C276.522 392.82 276.442 392.572 276.282 392.388C276.13 392.204 275.914 392.056 275.634 391.944C275.354 391.824 275.026 391.72 274.65 391.632L274.026 391.476C273.538 391.348 273.106 391.192 272.73 391.008C272.354 390.824 272.058 390.584 271.842 390.288C271.634 389.984 271.53 389.596 271.53 389.124C271.53 388.652 271.646 388.244 271.878 387.9C272.118 387.556 272.45 387.292 272.874 387.108C273.306 386.924 273.802 386.832 274.362 386.832C274.93 386.832 275.438 386.932 275.886 387.132C276.342 387.324 276.698 387.612 276.954 387.996C277.218 388.372 277.35 388.848 277.35 389.424V389.928H276.354V389.424C276.354 389.016 276.266 388.688 276.09 388.44C275.922 388.192 275.686 388.008 275.382 387.888C275.086 387.768 274.746 387.708 274.362 387.708C273.81 387.708 273.366 387.832 273.03 388.08C272.694 388.32 272.526 388.664 272.526 389.112C272.526 389.408 272.598 389.648 272.742 389.832C272.886 390.016 273.09 390.168 273.354 390.288C273.618 390.4 273.934 390.5 274.302 390.588L274.926 390.744C275.414 390.848 275.85 390.992 276.234 391.176C276.626 391.352 276.938 391.596 277.17 391.908C277.402 392.212 277.518 392.612 277.518 393.108C277.518 393.604 277.394 394.036 277.146 394.404C276.898 394.772 276.546 395.06 276.09 395.268C275.642 395.468 275.114 395.568 274.506 395.568Z"
        fill="white"
      />

      {/* uk label */}
      <motion.rect
        initial={{ opacity: 0 }}
        animate={labelAnimation}
        x="633.8"
        y="163.333"
        width="35"
        height="24"
        rx="12"
      />
      <motion.rect
        initial={{ pathLength: 0 }}
        animate={labelBorderAnimation}
        x="633.8"
        y="163.333"
        width="35"
        height="24"
        rx="12"
        stroke="url(#paint6_linear_1956_47681)"
        fill="white"
        fillOpacity="0"
      />
      <motion.path
        initial={{ opacity: 0 }}
        animate={labelTextAnimation}
        d="M647.638 180.001C647.014 180.001 646.47 179.889 646.006 179.665C645.542 179.441 645.182 179.109 644.926 178.669C644.67 178.221 644.542 177.673 644.542 177.025V171.433H645.538V177.037C645.538 177.725 645.722 178.241 646.09 178.585C646.458 178.929 646.974 179.101 647.638 179.101C648.302 179.101 648.818 178.929 649.186 178.585C649.554 178.241 649.738 177.725 649.738 177.037V171.433H650.746V177.025C650.746 177.673 650.618 178.221 650.362 178.669C650.106 179.109 649.742 179.441 649.27 179.665C648.806 179.889 648.262 180.001 647.638 180.001ZM652.594 179.833V171.433H653.602V175.093H653.77L657.322 171.433H658.666L654.574 175.585L658.81 179.833H657.43L653.77 176.101H653.602V179.833H652.594Z"
        fill="white"
      />

      {/* be label */}
      <motion.rect
        initial={{ opacity: 0 }}
        animate={labelAnimation}
        x="656.033"
        y="267.733"
        width="34"
        height="24"
        rx="12"
        fill="black"
      />
      <motion.rect
        initial={{ pathLength: 0 }}
        animate={labelBorderAnimation}
        x="656.033"
        y="267.733"
        width="34"
        height="24"
        rx="12"
        stroke="url(#paint7_linear_1956_47681)"
        fill="white"
        fillOpacity="0"
      />
      <motion.path
        initial={{ opacity: 0 }}
        animate={labelTextAnimation}
        d="M666.25 284.233V283.333H667.426V276.733H666.25V275.833H670.462C670.95 275.833 671.382 275.921 671.758 276.097C672.142 276.273 672.438 276.521 672.646 276.841C672.862 277.153 672.97 277.521 672.97 277.945V278.041C672.97 278.433 672.894 278.757 672.742 279.013C672.59 279.261 672.41 279.457 672.202 279.601C671.994 279.737 671.79 279.837 671.59 279.901V280.069C671.79 280.117 671.998 280.213 672.214 280.357C672.43 280.493 672.61 280.689 672.754 280.945C672.906 281.201 672.982 281.525 672.982 281.917V282.037C672.982 282.493 672.87 282.889 672.646 283.225C672.43 283.553 672.13 283.805 671.746 283.981C671.37 284.149 670.938 284.233 670.45 284.233H666.25ZM668.434 283.321H670.342C670.87 283.321 671.274 283.197 671.554 282.949C671.834 282.701 671.974 282.365 671.974 281.941V281.833C671.974 281.409 671.834 281.073 671.554 280.825C671.274 280.577 670.87 280.453 670.342 280.453H668.434V283.321ZM668.434 279.541H670.378C670.858 279.541 671.242 279.421 671.53 279.181C671.818 278.933 671.962 278.609 671.962 278.209V278.089C671.962 277.681 671.818 277.357 671.53 277.117C671.25 276.869 670.866 276.745 670.378 276.745H668.434V279.541ZM674.579 284.233V275.833H679.751V276.745H675.587V279.553H679.415V280.465H675.587V283.321H679.811V284.233H674.579Z"
        fill="white"
      />

      {/* de label */}
      <motion.rect
        initial={{ opacity: 0 }}
        animate={labelAnimation}
        x="683.1"
        y="175.9"
        width="34"
        height="24"
        rx="12"
        fill="black"
      />
      <motion.rect
        initial={{ pathLength: 0 }}
        animate={labelBorderAnimation}
        x="683.1"
        y="175.9"
        width="34"
        height="24"
        rx="12"
        stroke="url(#paint8_linear_1956_47681)"
        fill="white"
        fillOpacity="0"
      />
      <motion.path
        initial={{ opacity: 0 }}
        animate={labelTextAnimation}
        d="M693.304 192.4V191.5H694.48V184.9H693.304V184H696.58C697.684 184 698.536 184.28 699.136 184.84C699.744 185.4 700.048 186.26 700.048 187.42V188.98C700.048 190.148 699.744 191.012 699.136 191.572C698.536 192.124 697.684 192.4 696.58 192.4H693.304ZM695.488 191.488H696.58C697.396 191.488 698.012 191.288 698.428 190.888C698.844 190.488 699.052 189.868 699.052 189.028V187.384C699.052 186.528 698.844 185.904 698.428 185.512C698.012 185.12 697.396 184.924 696.58 184.924H695.488V191.488ZM701.657 192.4V184H706.829V184.912H702.665V187.72H706.493V188.632H702.665V191.488H706.889V192.4H701.657Z"
        fill="white"
      />

      {/* jp label */}
      <motion.rect
        initial={{ opacity: 0 }}
        animate={labelAnimation}
        x="1177.2"
        y="336.5"
        width="34"
        height="24"
        rx="12"
        fill="black"
      />
      <motion.rect
        initial={{ pathLength: 0 }}
        animate={labelBorderAnimation}
        x="1177.2"
        y="336.5"
        width="34"
        height="24"
        rx="12"
        stroke="url(#paint9_linear_1956_47681)"
        fill="white"
        fillOpacity="0"
      />
      <motion.path
        initial={{ opacity: 0 }}
        animate={labelTextAnimation}
        d="M1190.19 353.168C1189.37 353.168 1188.71 352.936 1188.23 352.472C1187.76 352.008 1187.53 351.344 1187.53 350.48V349.832H1188.52V350.48C1188.52 351 1188.66 351.424 1188.93 351.752C1189.21 352.08 1189.63 352.244 1190.18 352.244C1190.74 352.244 1191.13 352.084 1191.37 351.764C1191.6 351.436 1191.71 351.008 1191.71 350.48V345.512H1190.24V344.6H1193.63V345.512H1192.72V350.48C1192.72 351.352 1192.51 352.02 1192.07 352.484C1191.64 352.94 1191.01 353.168 1190.19 353.168ZM1195.12 353V344.6H1198.34C1198.84 344.6 1199.29 344.7 1199.68 344.9C1200.08 345.092 1200.4 345.368 1200.63 345.728C1200.86 346.088 1200.98 346.524 1200.98 347.036V347.192C1200.98 347.696 1200.86 348.132 1200.62 348.5C1200.38 348.86 1200.07 349.136 1199.67 349.328C1199.27 349.52 1198.82 349.616 1198.34 349.616H1196.13V353H1195.12ZM1196.13 348.704H1198.23C1198.76 348.704 1199.19 348.572 1199.5 348.308C1199.81 348.036 1199.97 347.656 1199.97 347.168V347.048C1199.97 346.56 1199.81 346.184 1199.5 345.92C1199.2 345.648 1198.77 345.512 1198.23 345.512H1196.13V348.704Z"
        fill="white"
      />

      {/* LABELS END */}

      <defs>
        <filter
          id="filter0_d_1956_47681"
          x="0.444281"
          y="0.39133"
          width="1399.11"
          height="724.378"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1.58035" />
          <feGaussianBlur stdDeviation="1.58035" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.01 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1956_47681" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_1956_47681"
            result="shape"
          />
        </filter>
        <linearGradient
          id="paint0_linear_1956_47681"
          x1="666.237"
          y1="199.412"
          x2="618.375"
          y2="218.943"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#365DFF" />
          <stop offset="0.294929" stopColor="#8A68FD" />
          <stop offset="0.515364" stopColor="#E77DCD" />
          <stop offset="0.677058" stopColor="#FC869C" />
          <stop offset="0.947736" stopColor="#FF8D5B" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_1956_47681"
          x1="327.323"
          y1="194.548"
          x2="150.231"
          y2="266.812"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#365DFF" />
          <stop offset="0.294929" stopColor="#8A68FD" />
          <stop offset="0.515364" stopColor="#E77DCD" />
          <stop offset="0.677058" stopColor="#FC869C" />
          <stop offset="0.947736" stopColor="#FF8D5B" />
        </linearGradient>
        <linearGradient
          id="paint2_linear_1956_47681"
          x1="682.256"
          y1="221.9"
          x2="653.538"
          y2="233.619"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#365DFF" />
          <stop offset="0.294929" stopColor="#8A68FD" />
          <stop offset="0.515364" stopColor="#E77DCD" />
          <stop offset="0.677058" stopColor="#FC869C" />
          <stop offset="0.947736" stopColor="#FF8D5B" />
        </linearGradient>
        <linearGradient
          id="paint3_linear_1956_47681"
          x1="720.976"
          y1="199.551"
          x2="656.361"
          y2="225.918"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#365DFF" />
          <stop offset="0.294929" stopColor="#8A68FD" />
          <stop offset="0.515364" stopColor="#E77DCD" />
          <stop offset="0.677058" stopColor="#FC869C" />
          <stop offset="0.947736" stopColor="#FF8D5B" />
        </linearGradient>
        <linearGradient
          id="paint4_linear_1956_47681"
          x1="1215.08"
          y1="260.151"
          x2="1150.46"
          y2="286.518"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#365DFF" />
          <stop offset="0.294929" stopColor="#8A68FD" />
          <stop offset="0.515364" stopColor="#E77DCD" />
          <stop offset="0.677058" stopColor="#FC869C" />
          <stop offset="0.947736" stopColor="#FF8D5B" />
        </linearGradient>
        <linearGradient
          id="paint5_linear_1956_47681"
          x1="288.433"
          y1="414.187"
          x2="245.046"
          y2="387.336"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0559916" stopColor="#61ACB2" />
          <stop offset="0.384306" stopColor="#93DAA2" />
          <stop offset="0.534491" stopColor="#A6D390" />
          <stop offset="0.757304" stopColor="#D5C375" />
          <stop offset="1" stopColor="#F58673" />
        </linearGradient>
        <linearGradient
          id="paint6_linear_1956_47681"
          x1="669.3"
          y1="198.621"
          x2="625.913"
          y2="171.77"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0559916" stopColor="#61ACB2" />
          <stop offset="0.384306" stopColor="#93DAA2" />
          <stop offset="0.534491" stopColor="#A6D390" />
          <stop offset="0.757304" stopColor="#D5C375" />
          <stop offset="1" stopColor="#F58673" />
        </linearGradient>
        <linearGradient
          id="paint7_linear_1956_47681"
          x1="690.533"
          y1="303.021"
          x2="647.702"
          y2="277.25"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0559916" stopColor="#61ACB2" />
          <stop offset="0.384306" stopColor="#93DAA2" />
          <stop offset="0.534491" stopColor="#A6D390" />
          <stop offset="0.757304" stopColor="#D5C375" />
          <stop offset="1" stopColor="#F58673" />
        </linearGradient>
        <linearGradient
          id="paint8_linear_1956_47681"
          x1="717.6"
          y1="211.188"
          x2="674.768"
          y2="185.417"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0559916" stopColor="#61ACB2" />
          <stop offset="0.384306" stopColor="#93DAA2" />
          <stop offset="0.534491" stopColor="#A6D390" />
          <stop offset="0.757304" stopColor="#D5C375" />
          <stop offset="1" stopColor="#F58673" />
        </linearGradient>
        <linearGradient
          id="paint9_linear_1956_47681"
          x1="1211.7"
          y1="371.787"
          x2="1168.87"
          y2="346.016"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0559916" stopColor="#61ACB2" />
          <stop offset="0.384306" stopColor="#93DAA2" />
          <stop offset="0.534491" stopColor="#A6D390" />
          <stop offset="0.757304" stopColor="#D5C375" />
          <stop offset="1" stopColor="#F58673" />
        </linearGradient>
      </defs>
    </svg>
  );
};

export default MapSvg;
